import { useState, useEffect, useRef, useCallback } from "react";
import { getStorage, ref, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import { getFirestore, collection, addDoc, getDocs, deleteDoc, doc, getDoc, query, orderBy, where, updateDoc, setDoc, writeBatch } from "firebase/firestore";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import "./firebase"; // Firebase 초기화

// ⚠️ Owner 이메일 — 이 이메일로 가입하면 자동으로 owner 권한 + 즉시 승인됨.
// 여러 명도 가능. 본인 이메일을 여기에 정확히 입력하세요 (대소문자 무관).
const OWNER_EMAILS = [
  "concone05@gmail.com", // ⚠️ 경민님 이메일 (기존 시드 데이터에서 가져옴 — 필요시 변경)
];
const isOwnerEmail = (email) => {
  if (!email) return false;
  return OWNER_EMAILS.map(e => e.toLowerCase()).includes(email.toLowerCase().trim());
};

// ─── Data ────────────────────────────────────────────────────────────────────
const SUBJECTS = [
  { id: "english", name: "영어", nameEn: "English", icon: "🅰", category: "main" },
  { id: "coding", name: "코딩", nameEn: "Coding", icon: "⌨", category: "main" },
  { id: "music", name: "음악", nameEn: "Music", icon: "♪", category: "arts", parent: "예체능" },
  { id: "senses", name: "오감", nameEn: "Senses", icon: "✋", category: "arts", parent: "예체능" },
  { id: "piano", name: "피아노", nameEn: "Piano", icon: "🎹", category: "arts", parent: "예체능" },
  { id: "science", name: "과학", nameEn: "Science", icon: "⚗", category: "etc", parent: "그 외" },
  { id: "art", name: "미술", nameEn: "Art", icon: "🖌", category: "etc", parent: "그 외" },
  { id: "reading", name: "독서", nameEn: "Reading", icon: "📖", category: "etc", parent: "그 외" },
  { id: "math", name: "수학", nameEn: "Math", icon: "∑", category: "etc", parent: "그 외" },
  { id: "hangul", name: "한글", nameEn: "Korean", icon: "ㄱ", category: "etc", parent: "그 외" },
];

// Bilingual label helper: shows Korean with small English underneath
const T = {
  홈: "Home", 과목: "Subjects", 즐겨찾기: "Favorites", 최근: "Recent", 행사: "Events",
  음원: "Audio", 영상: "Video", 계획안: "Lesson Plan", 그림자료: "Materials", 물품리스트: "Supplies", 공유: "Share",
  월간계획안: "Monthly Plan", 연간계획안: "Annual Plan",
  검색: "Search", 설정: "Settings", 로그아웃: "Logout", 회원가입: "Sign Up",
  "전체보기": "View All", "바로가기": "Quick Access", "최신 공지사항": "Notices",
  "최근 재생": "Recently Played", "음원 검색...": "Search audio...",
  "음원, 영상, 계획안 검색...": "Search audio, video, plans...",
  "공지사항": "Notices", "콘텐츠": "Contents", "사용자": "Users", "통계": "Stats",
  "대시보드": "Dashboard",
};
const Bi = (kr, style = {}) => (
  <span style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2, ...style }}>
    <span>{kr}</span>
    <span style={{ fontSize: 8, opacity: 0.5, fontWeight: 400 }}>{T[kr] || ""}</span>
  </span>
);

// Programs per subject (subjects with only one program skip the selection step)
const PROGRAMS = {
  english: [
    { id: "readyset", name: "레디셋", icon: "🚀", desc: "Ready, Set 영어 프로그램" },
    { id: "tikitogi", name: "티키토기", icon: "🐣", desc: "Tiki Togi 영어 프로그램" },
    { id: "wings", name: "윙스", icon: "🕊️", desc: "윙스 영어 프로그램" },
    { id: "jelling", name: "젤링", icon: "🫧", desc: "젤링 영어 프로그램" },
    { id: "ort", name: "ORT", icon: "📚", desc: "ORT 영어 프로그램" },
    { id: "hansol-toktok", name: "한솔톡톡", icon: "💬", desc: "한솔톡톡 영어 프로그램" },
  ],
  coding: [
    { id: "ai-enter", name: "AI엔터코딩", icon: "🤖", desc: "AI엔터코딩 프로그램" },
  ],
  music: [
    { id: "play-melody", name: "플레이 멜로디", icon: "🎵", desc: "플레이 멜로디 음악 프로그램" },
    { id: "world-music", name: "세상의 모든음악", icon: "🌍", desc: "세상의 모든음악 프로그램" },
    { id: "with-muse", name: "위드뮤즈", icon: "🎼", desc: "위드뮤즈 음악 프로그램" },
  ],
  senses: [
    // 스칸디오감은 연도별로 구분 (기존 자료는 id "scandi" 그대로 = 2026년)
    { id: "scandi-2025", name: "스칸디오감 2025", icon: "🧩", desc: "2025년도 음원·자료" },
    { id: "scandi", name: "스칸디오감 2026", icon: "🧩", desc: "2026년도 음원·자료" },
    { id: "scandi-2027", name: "스칸디오감 2027", icon: "🧩", desc: "2027년도 음원·자료" },
  ],
  piano: [
    { id: "fianse", name: "피앙세", icon: "🎹", desc: "피앙세 피아노 프로그램" },
    { id: "singfiang", name: "씽피앙", icon: "🎶", desc: "씽피앙 피아노 프로그램" },
  ],
  science: [
    { id: "dodaeche", name: "도대체과학연구소", icon: "🔬", desc: "도대체과학연구소 프로그램" },
    { id: "anda", name: "안다과학", icon: "🧪", desc: "안다과학 프로그램" },
    { id: "magic", name: "매직사이언스", icon: "✨", desc: "매직사이언스 프로그램" },
    { id: "tamgu", name: "탐구교실", icon: "🔎", desc: "탐구교실 프로그램" },
    { id: "norience", name: "놀이언스", icon: "🎈", desc: "놀이언스 프로그램" },
  ],
};


// Tracks structured: for subjects with programs: program -> issue -> step -> tracks
// For subjects without programs: issue -> step -> tracks
const SAMPLE_TRACKS = {
  english: {
    readyset: {
      1: {
        step2: [{ id: "rs-1-2a", title: "Ready Set Hello", duration: "2:15" }, { id: "rs-1-2b", title: "Ready Set Greeting", duration: "1:50" }],
        step3: [{ id: "rs-1-3a", title: "Ready Set Hello", duration: "2:15" }, { id: "rs-1-3b", title: "Ready Set ABC", duration: "3:02" }],
        step4: [{ id: "rs-1-4a", title: "Ready Set Hello", duration: "2:20" }, { id: "rs-1-4b", title: "Ready Set Phonics", duration: "3:10" }],
        step5: [{ id: "rs-1-5a", title: "Ready Set Hello", duration: "2:25" }, { id: "rs-1-5b", title: "Ready Set Story", duration: "4:00" }],
      },
      2: {
        step3: [{ id: "rs-2-3a", title: "Ready Set Weather", duration: "2:10" }],
        step4: [{ id: "rs-2-4a", title: "Ready Set Weather", duration: "2:15" }, { id: "rs-2-4b", title: "Ready Set Days", duration: "2:35" }],
      },
    },
    tikitogi: {
      1: {
        step2: [{ id: "tt-1-2a", title: "Tiki Hello Song", duration: "2:00" }, { id: "tt-1-2b", title: "Tiki Dance", duration: "1:45" }],
        step3: [{ id: "tt-1-3a", title: "Tiki Hello Song", duration: "2:10" }, { id: "tt-1-3b", title: "Togi Phonics", duration: "2:50" }],
        step4: [{ id: "tt-1-4a", title: "Tiki Colors", duration: "2:30" }, { id: "tt-1-4b", title: "Togi Shapes", duration: "2:45" }],
      },
      2: {
        step2: [{ id: "tt-2-2a", title: "Tiki Animals", duration: "2:20" }],
        step3: [{ id: "tt-2-3a", title: "Tiki Animals", duration: "2:25" }, { id: "tt-2-3b", title: "Togi Zoo", duration: "3:00" }],
      },
      3: {
        step3: [{ id: "tt-3-3a", title: "Tiki Numbers", duration: "2:15" }],
        step4: [{ id: "tt-3-4a", title: "Tiki Counting", duration: "2:40" }],
      },
    },
  },
  coding: {
    jemplay: {
      1: { step3: [{ id: "jp-1-3a", title: "잼플레이 시작음악", duration: "1:30" }], step4: [{ id: "jp-1-4a", title: "잼플레이 코딩송", duration: "2:10" }] },
    },
    "ai-enter": {
      1: { step4: [{ id: "ae-1-4a", title: "AI엔터 시작음악", duration: "1:30" }, { id: "ae-1-4b", title: "알고리즘 챈트", duration: "2:10" }], step5: [{ id: "ae-1-5a", title: "AI엔터 코딩송", duration: "2:45" }] },
    },
    metakinder: {
      1: { step3: [{ id: "mk-1-3a", title: "메타킨더 인트로", duration: "1:20" }], step4: [{ id: "mk-1-4a", title: "메타킨더 코딩송", duration: "2:00" }] },
    },
    "ai-thinking": {
      1: { step4: [{ id: "at-1-4a", title: "씽킹메이커 시작", duration: "1:45" }], step5: [{ id: "at-1-5a", title: "씽킹메이커 챈트", duration: "2:30" }] },
    },
  },
  music: {
    "play-melody": {
      1: { step2: [{ id: "pm-1-2a", title: "봄바람", duration: "3:15" }, { id: "pm-1-2b", title: "리듬 놀이", duration: "2:40" }], step3: [{ id: "pm-1-3a", title: "봄바람", duration: "3:15" }, { id: "pm-1-3b", title: "동물 소리", duration: "2:00" }], step4: [{ id: "pm-1-4a", title: "봄바람", duration: "3:20" }, { id: "pm-1-4b", title: "악기 탐험", duration: "3:30" }] },
    },
    "world-music": {
      1: { step3: [{ id: "wm-1-3a", title: "세계의 인사", duration: "2:45" }], step4: [{ id: "wm-1-4a", title: "아프리카 리듬", duration: "3:10" }], step5: [{ id: "wm-1-5a", title: "오케스트라 탐험", duration: "4:00" }] },
    },
    "with-muse": {
      1: { step2: [{ id: "wmu-1-2a", title: "뮤즈의 노래", duration: "2:30" }], step3: [{ id: "wmu-1-3a", title: "뮤즈와 함께", duration: "2:50" }] },
    },
  },
  senses: {
    scandi: {
      1: { step2: [{ id: "sc-1-2a", title: "오감 체조", duration: "2:20" }, { id: "sc-1-2b", title: "촉감 놀이 음악", duration: "1:55" }], step3: [{ id: "sc-1-3a", title: "스칸디 오감놀이", duration: "2:20" }] },
    },
  },
  piano: {
    fianse: {
      1: { step3: [{ id: "fi-1-3a", title: "피앙세 도레미", duration: "3:00" }], step4: [{ id: "fi-1-4a", title: "피앙세 작은 별", duration: "2:10" }], step5: [{ id: "fi-1-5a", title: "피앙세 워밍업", duration: "1:45" }] },
    },
    singfiang: {
      1: { step3: [{ id: "sf-1-3a", title: "씽피앙 도레미송", duration: "2:40" }], step4: [{ id: "sf-1-4a", title: "씽피앙 멜로디", duration: "3:20" }] },
    },
  },
  science: {
    dodaeche: {
      1: { step4: [{ id: "dd-1-4a", title: "도대체 실험음악", duration: "1:30" }], step5: [{ id: "dd-1-5a", title: "도대체 탐구송", duration: "2:00" }] },
    },
    anda: {
      1: { step4: [{ id: "an-1-4a", title: "안다과학 시작", duration: "1:40" }] },
    },
    magic: {
      1: { step3: [{ id: "mg-1-3a", title: "매직 실험음악", duration: "1:50" }], step4: [{ id: "mg-1-4a", title: "매직 사이언스송", duration: "2:10" }] },
    },
    tamgu: {
      1: { step4: [{ id: "tg-1-4a", title: "탐구 시작음악", duration: "1:30" }], step5: [{ id: "tg-1-5a", title: "탐구 실험송", duration: "2:20" }] },
    },
    norience: {
      1: { step3: [{ id: "nr-1-3a", title: "놀이언스 시작", duration: "1:45" }], step4: [{ id: "nr-1-4a", title: "놀이언스 챈트", duration: "2:00" }] },
    },
  },
  art: {
    1: {
      step2: [{ id: "a-1-2a", title: "그리기 시간 BGM", duration: "4:00" }],
      step3: [{ id: "a-1-3a", title: "그리기 시간 BGM", duration: "4:00" }],
      step4: [{ id: "a-1-4a", title: "그리기 시간 BGM", duration: "4:00" }],
    },
  },
  reading: {
    1: {
      step3: [{ id: "r-1-3a", title: "이야기 시간", duration: "2:30" }],
      step4: [{ id: "r-1-4a", title: "이야기 시간", duration: "2:30" }],
    },
  },
  math: {
    1: {
      step3: [{ id: "mt-1-3a", title: "숫자 노래", duration: "2:15" }],
      step4: [{ id: "mt-1-4a", title: "숫자 노래", duration: "2:15" }],
    },
  },
  hangul: {
    1: {
      step3: [{ id: "h-1-3a", title: "한글 놀이", duration: "2:40" }],
      step4: [{ id: "h-1-4a", title: "자음 노래", duration: "2:10" }],
      step5: [{ id: "h-1-5a", title: "받침 노래", duration: "2:50" }],
    },
  },
};

// Videos structured: same as tracks
const SAMPLE_VIDEOS = {
  english: {
    readyset: {
      1: {
        step3: [{ id: "rsv-1-3a", title: "Ready Set Phonics Guide", duration: "15:00", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" }],
        step4: [{ id: "rsv-1-4a", title: "Ready Set Lesson Demo", duration: "12:00", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" }],
      },
    },
    tikitogi: {
      1: {
        step3: [{ id: "ttv-1-3a", title: "Tiki Togi 수업 시연", duration: "10:00", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" }],
        step4: [{ id: "ttv-1-4a", title: "Tiki Togi Activity Guide", duration: "8:00" }],
      },
    },
  },
  coding: {
    jemplay: { 1: { step4: [{ id: "jpv-1-4a", title: "잼플레이 수업 시연", duration: "12:00", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" }] } },
    "ai-enter": { 1: { step5: [{ id: "aev-1-5a", title: "AI엔터 코딩 시연", duration: "14:00" }] } },
  },
  music: {
    "play-melody": { 1: { step3: [{ id: "pmv-1-3a", title: "플레이멜로디 수업 시연", duration: "8:00", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" }], step4: [{ id: "pmv-1-4a", title: "플레이멜로디 악기 가이드", duration: "10:00" }] } },
    "world-music": { 1: { step4: [{ id: "wmv-1-4a", title: "세상의모든음악 시연", duration: "9:00" }] } },
  },
};

const STEPS = [
  { id: "step2", label: "STEP 2", age: "만2세", ageEn: "Age 2" },
  { id: "step3", label: "STEP 3", age: "만3세", ageEn: "Age 3" },
  { id: "step4", label: "STEP 4", age: "만4세", ageEn: "Age 4" },
  { id: "step5", label: "STEP 5", age: "만5세", ageEn: "Age 5" },
];

// 행사 이모지 카탈로그 — 카테고리별로 분류
const EVENT_EMOJI_CATEGORIES = [
  {
    name: "행사 · 축제",
    emojis: ["🎉", "🎊", "🎈", "🎁", "🎂", "🍰", "🧁", "🍭", "🍬", "🎀", "🪅", "🎆", "🎇", "✨", "🎄", "🎃", "🎗", "🏆", "🥇", "🏅"],
  },
  {
    name: "음악 · 공연",
    emojis: ["🎵", "🎶", "🎼", "🎤", "🎧", "🎹", "🥁", "🎺", "🎷", "🎻", "🪕", "🪘", "🎸", "🎬", "🎭", "🎟", "💃", "🕺", "🎙", "📯"],
  },
  {
    name: "자연 · 계절",
    emojis: ["🌸", "🌺", "🌻", "🌷", "🌹", "🌼", "🌱", "🌿", "🍀", "🌳", "🌲", "🌴", "🍂", "🍁", "🌾", "❄️", "☀️", "🌈", "⭐️", "🌙"],
  },
  {
    name: "동물 · 친구",
    emojis: ["🐰", "🐻", "🐼", "🦁", "🐯", "🐶", "🐱", "🦊", "🐨", "🐮", "🐷", "🐸", "🐵", "🐧", "🦄", "🦋", "🐞", "🐝", "🐢", "🐬"],
  },
  {
    name: "음식 · 간식",
    emojis: ["🍎", "🍓", "🍇", "🍉", "🍌", "🥕", "🍞", "🥨", "🥐", "🍪", "🍩", "🍦", "🍧", "🍨", "🍫", "🍿", "🥤", "☕️", "🥛", "🍵"],
  },
  {
    name: "활동 · 체험",
    emojis: ["⚽️", "🏀", "🎾", "🏐", "🎳", "🎯", "🎮", "🧩", "🪁", "🎨", "✏️", "📚", "📖", "🔬", "🔭", "🧪", "🤸", "🏃", "🚴", "🏊"],
  },
  {
    name: "장소 · 여행",
    emojis: ["🏫", "🏠", "⛺️", "🏰", "🗽", "🗼", "🏖", "🏔", "🚌", "🚂", "✈️", "🚀", "⛵️", "🌍", "🗺", "🧭", "🎡", "🎢", "🎪", "🏟"],
  },
  {
    name: "기타",
    emojis: ["❤️", "💛", "💚", "💙", "💜", "🧡", "💖", "💝", "👑", "💎", "🔔", "🎌", "🚩", "📣", "🎓", "📷", "🎥", "💌", "📅", "🗓"],
  },
];

// Subject-specific step restrictions
const SUBJECT_STEPS = {
  piano: ["step4", "step5"],       // 만4,5세만
  coding: ["step3", "step4", "step5"], // 만3,4,5세만
  senses: [],                       // 연령 구분 없음 (skip)
};
// Returns filtered STEPS for a subject, or all STEPS if no restriction
const getStepsForSubject = (subjectId) => {
  const allowed = SUBJECT_STEPS[subjectId];
  if (!allowed) return STEPS; // no restriction
  if (allowed.length === 0) return []; // skip steps entirely (e.g. senses)
  return STEPS.filter(s => allowed.includes(s.id));
};
// Whether a subject skips the step selection entirely
const subjectSkipsSteps = (subjectId) => SUBJECT_STEPS[subjectId]?.length === 0;

// Play Melody custom stages
const PLAY_MELODY_STAGES = [
  { id: "touch", label: "터치", desc: "만 2,3세" },
  { id: "play", label: "플레이", desc: "만 4,5세" },
];

const ISSUES = Array.from({ length: 12 }, (_, i) => ({ id: i + 1, label: `${i + 1}호` }));

// ─── 업로드 순서 정렬 ───────────────────────────────────────────────────────
// 기본은 order(관리자가 정한 순서) 기준. 그런데 예전 업로드 방식은 여러 번 나눠 올릴 때마다
// order가 0,1,2...로 다시 시작해서 같은 번호가 겹쳤다(→ 제목순으로 섞여 보임).
// 목록 안에 order가 겹치면 그 목록은 "올린 시각(createdAt)" 순서로 보여준다.
const sortByUploadOrder = (items) => {
  const arr = Array.isArray(items) ? items.slice() : [];
  const ords = arr.map(x => x?.order ?? 999);
  const hasDup = new Set(ords).size !== ords.length;
  return arr.sort((a, b) => {
    if (!hasDup) { const d = (a?.order ?? 999) - (b?.order ?? 999); if (d) return d; }
    const ca = a?.createdAt || "", cb = b?.createdAt || "";
    if (ca !== cb) { if (!ca) return 1; if (!cb) return -1; return ca < cb ? -1 : 1; }
    const d2 = (a?.order ?? 999) - (b?.order ?? 999); if (d2) return d2;
    return (a?.title || "").localeCompare(b?.title || "", "ko");
  });
};
// 새로 올릴 파일의 시작 order = 그 폴더의 가장 큰 order + 1 (기존 곡 뒤에 차례대로 붙음)
const nextOrderOf = (items) => (Array.isArray(items) ? items : []).reduce((m, t) => (typeof t?.order === "number" && t.order < 999 && t.order > m ? t.order : m), -1) + 1;

// 오감 · 스칸디오감 연도별 구분: 2025 / 2026(기존 "scandi") / 2027
// Firestore programs 목록에 없더라도 항상 세 연도가 순서대로 보이도록 보정한다.
// 스칸디오감 로고 (배경 투명 PNG를 파일 안에 바로 넣어 둠 → 별도 이미지 파일 없이 배포 가능)
const SCANDI_LOGO = "data:image/png;base64,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";
const SCANDI_YEAR_PROGRAMS = [
  { id: "scandi-2025", subjectId: "senses", name: "스칸디오감 2025", icon: "🧩", logo: SCANDI_LOGO, desc: "2025년도 음원·자료" },
  { id: "scandi", subjectId: "senses", name: "스칸디오감 2026", icon: "🧩", logo: SCANDI_LOGO, desc: "2026년도 음원·자료" },
  { id: "scandi-2027", subjectId: "senses", name: "스칸디오감 2027", icon: "🧩", logo: SCANDI_LOGO, desc: "2027년도 음원·자료" },
];
const isScandiProgram = (id) => typeof id === "string" && id.startsWith("scandi");
const withScandiYears = (list) => {
  const others = list.filter(p => !isScandiProgram(p.id));
  const base = list.find(p => p.id === "scandi");
  const maxOrder = list.reduce((m, p) => (typeof p.order === "number" && p.order > m ? p.order : m), 0);
  const startOrder = typeof base?.order === "number" ? base.order : maxOrder + 1;
  const years = SCANDI_YEAR_PROGRAMS.map((y, i) => ({ ...(list.find(p => p.id === y.id) || {}), ...y, order: startOrder + i * 0.01 }));
  return [...others, ...years];
};

// 피앙세 음원 전용 추가 폴더 (1~12호 뒤에 붙는 특수 폴더).
// id는 문자열 키로 Firestore issue 필드에 그대로 저장/조회된다.
const FIANSE_AUDIO_EXTRA_ISSUES = [
  { id: "recital", label: "발표회" },
  { id: "etc", label: "그외" },
];

// 현재 (과목/프로그램/탭) 맥락에 맞는 호수 목록을 반환.
// 피앙세(fianse) 프로그램의 음원(tracks) 탭에서만 추가 폴더를 노출한다.
const getIssuesForContext = (programId, tab) => {
  if (programId === "fianse" && tab === "tracks") {
    return [...ISSUES, ...FIANSE_AUDIO_EXTRA_ISSUES];
  }
  return ISSUES;
};

// 오감 과목 등 호 → 월 표기를 사용하는 과목용 매핑.
// 학년도 흐름: 1호=3월, 2호=4월, ..., 10호=12월, 11호=1월, 12호=2월
const MONTH_LABELED_SUBJECTS = ["senses"]; // 호 대신 월로 표기할 과목 id 목록
const issueToMonth = (issue) => {
  const n = parseInt(issue, 10);
  if (!n || n < 1 || n > 12) return null;
  return ((n + 1) % 12) + 1; // 1→3, 2→4, ..., 10→12, 11→1, 12→2
};
const issueLabel = (issue, subjectId) => {
  // 피앙세 음원 특수 폴더(발표회/그외 등): 숫자가 아닌 문자열 id는 라벨로 그대로 표시
  const extra = FIANSE_AUDIO_EXTRA_ISSUES.find(e => e.id === issue);
  if (extra) return extra.label;
  if (MONTH_LABELED_SUBJECTS.includes(subjectId)) {
    const m = issueToMonth(issue);
    return m ? `${m}월` : `${issue}호`;
  }
  return `${issue}호`;
};

// issue 값 정규화: 숫자 호수는 Number로, 특수 폴더(recital/etc 등 문자열)는 문자열 그대로 유지.
// 문자열을 Number()로 강제하면 NaN이 되어 issue 비교/필터가 깨지므로 이 헬퍼를 사용한다.
const normalizeIssue = (issue) => {
  if (typeof issue === "number") return issue;
  const n = Number(issue);
  return Number.isNaN(n) ? issue : n;
};

// Plans are structured: type(monthly/yearly) -> issue -> step
const SAMPLE_PLANS = {
  english: {
    readyset: {
      monthly: {
        1: { step2: { id: "rsp-m-1-2", title: "레디셋 1호 월간계획안 (STEP2)" }, step3: { id: "rsp-m-1-3", title: "레디셋 1호 월간계획안 (STEP3)" }, step4: { id: "rsp-m-1-4", title: "레디셋 1호 월간계획안 (STEP4)" }, step5: { id: "rsp-m-1-5", title: "레디셋 1호 월간계획안 (STEP5)" } },
        2: { step3: { id: "rsp-m-2-3", title: "레디셋 2호 월간계획안 (STEP3)" }, step4: { id: "rsp-m-2-4", title: "레디셋 2호 월간계획안 (STEP4)" } },
      },
      yearly: {
        step2: { id: "rsp-y-2", title: "레디셋 연간계획안 (STEP2)" },
        step3: { id: "rsp-y-3", title: "레디셋 연간계획안 (STEP3)" },
        step4: { id: "rsp-y-4", title: "레디셋 연간계획안 (STEP4)" },
        step5: { id: "rsp-y-5", title: "레디셋 연간계획안 (STEP5)" },
      },
    },
    tikitogi: {
      monthly: {
        1: { step2: { id: "ttp-m-1-2", title: "티키토기 1호 월간계획안 (STEP2)" }, step3: { id: "ttp-m-1-3", title: "티키토기 1호 월간계획안 (STEP3)" }, step4: { id: "ttp-m-1-4", title: "티키토기 1호 월간계획안 (STEP4)" } },
        2: { step2: { id: "ttp-m-2-2", title: "티키토기 2호 월간계획안 (STEP2)" }, step3: { id: "ttp-m-2-3", title: "티키토기 2호 월간계획안 (STEP3)" } },
      },
      yearly: {
        step2: { id: "ttp-y-2", title: "티키토기 연간계획안 (STEP2)" },
        step3: { id: "ttp-y-3", title: "티키토기 연간계획안 (STEP3)" },
        step4: { id: "ttp-y-4", title: "티키토기 연간계획안 (STEP4)" },
      },
    },
  },
  coding: {
    jemplay: { monthly: { 1: { step3: { id: "jpp-m-1-3", title: "잼플레이 1호 계획안 (STEP3)" }, step4: { id: "jpp-m-1-4", title: "잼플레이 1호 계획안 (STEP4)" } } }, yearly: { step3: { id: "jpp-y-3", title: "잼플레이 연간계획안 (STEP3)" }, step4: { id: "jpp-y-4", title: "잼플레이 연간계획안 (STEP4)" } } },
    "ai-enter": { monthly: { 1: { step4: { id: "aep-m-1-4", title: "AI엔터 1호 계획안 (STEP4)" }, step5: { id: "aep-m-1-5", title: "AI엔터 1호 계획안 (STEP5)" } } }, yearly: { step4: { id: "aep-y-4", title: "AI엔터 연간계획안 (STEP4)" }, step5: { id: "aep-y-5", title: "AI엔터 연간계획안 (STEP5)" } } },
    metakinder: { monthly: { 1: { step3: { id: "mkp-m-1-3", title: "메타킨더 1호 계획안 (STEP3)" } } }, yearly: { step3: { id: "mkp-y-3", title: "메타킨더 연간계획안 (STEP3)" } } },
    "ai-thinking": { monthly: { 1: { step4: { id: "atp-m-1-4", title: "씽킹메이커 1호 계획안 (STEP4)" }, step5: { id: "atp-m-1-5", title: "씽킹메이커 1호 계획안 (STEP5)" } } }, yearly: { step4: { id: "atp-y-4", title: "씽킹메이커 연간계획안 (STEP4)" }, step5: { id: "atp-y-5", title: "씽킹메이커 연간계획안 (STEP5)" } } },
  },
  music: {
    "play-melody": { monthly: { 1: { step2: { id: "pmp-m-1-2", title: "플레이멜로디 1호 계획안 (STEP2)" }, step3: { id: "pmp-m-1-3", title: "플레이멜로디 1호 계획안 (STEP3)" }, step4: { id: "pmp-m-1-4", title: "플레이멜로디 1호 계획안 (STEP4)" } } }, yearly: { step2: { id: "pmp-y-2", title: "플레이멜로디 연간계획안 (STEP2)" }, step3: { id: "pmp-y-3", title: "플레이멜로디 연간계획안 (STEP3)" }, step4: { id: "pmp-y-4", title: "플레이멜로디 연간계획안 (STEP4)" } } },
    "world-music": { monthly: { 1: { step3: { id: "wmp-m-1-3", title: "세상의모든음악 1호 계획안 (STEP3)" }, step4: { id: "wmp-m-1-4", title: "세상의모든음악 1호 계획안 (STEP4)" } } }, yearly: { step3: { id: "wmp-y-3", title: "세상의모든음악 연간계획안 (STEP3)" }, step4: { id: "wmp-y-4", title: "세상의모든음악 연간계획안 (STEP4)" } } },
    "with-muse": { monthly: { 1: { step2: { id: "wmup-m-1-2", title: "위드뮤즈 1호 계획안 (STEP2)" }, step3: { id: "wmup-m-1-3", title: "위드뮤즈 1호 계획안 (STEP3)" } } }, yearly: { step2: { id: "wmup-y-2", title: "위드뮤즈 연간계획안 (STEP2)" }, step3: { id: "wmup-y-3", title: "위드뮤즈 연간계획안 (STEP3)" } } },
  },
  senses: {
    scandi: { monthly: { 1: { step2: { id: "scp-m-1-2", title: "스칸디오감 1호 계획안 (STEP2)" }, step3: { id: "scp-m-1-3", title: "스칸디오감 1호 계획안 (STEP3)" } } }, yearly: { step2: { id: "scp-y-2", title: "스칸디오감 연간계획안 (STEP2)" }, step3: { id: "scp-y-3", title: "스칸디오감 연간계획안 (STEP3)" } } },
  },
  piano: {
    fianse: { monthly: { 1: { step3: { id: "fip-m-1-3", title: "피앙세 1호 계획안 (STEP3)" }, step4: { id: "fip-m-1-4", title: "피앙세 1호 계획안 (STEP4)" }, step5: { id: "fip-m-1-5", title: "피앙세 1호 계획안 (STEP5)" } } }, yearly: { step3: { id: "fip-y-3", title: "피앙세 연간계획안 (STEP3)" }, step4: { id: "fip-y-4", title: "피앙세 연간계획안 (STEP4)" }, step5: { id: "fip-y-5", title: "피앙세 연간계획안 (STEP5)" } } },
    singfiang: { monthly: { 1: { step3: { id: "sfp-m-1-3", title: "씽피앙 1호 계획안 (STEP3)" }, step4: { id: "sfp-m-1-4", title: "씽피앙 1호 계획안 (STEP4)" } } }, yearly: { step3: { id: "sfp-y-3", title: "씽피앙 연간계획안 (STEP3)" }, step4: { id: "sfp-y-4", title: "씽피앙 연간계획안 (STEP4)" } } },
  },
  science: {
    dodaeche: { monthly: { 1: { step4: { id: "ddp-m-1-4", title: "도대체 1호 계획안 (STEP4)" }, step5: { id: "ddp-m-1-5", title: "도대체 1호 계획안 (STEP5)" } } }, yearly: { step4: { id: "ddp-y-4", title: "도대체 연간계획안 (STEP4)" }, step5: { id: "ddp-y-5", title: "도대체 연간계획안 (STEP5)" } } },
    anda: { monthly: { 1: { step4: { id: "anp-m-1-4", title: "안다과학 1호 계획안 (STEP4)" } } }, yearly: { step4: { id: "anp-y-4", title: "안다과학 연간계획안 (STEP4)" } } },
    magic: { monthly: { 1: { step3: { id: "mgp-m-1-3", title: "매직사이언스 1호 계획안 (STEP3)" }, step4: { id: "mgp-m-1-4", title: "매직사이언스 1호 계획안 (STEP4)" } } }, yearly: { step3: { id: "mgp-y-3", title: "매직사이언스 연간계획안 (STEP3)" }, step4: { id: "mgp-y-4", title: "매직사이언스 연간계획안 (STEP4)" } } },
    tamgu: { monthly: { 1: { step4: { id: "tgp-m-1-4", title: "탐구교실 1호 계획안 (STEP4)" }, step5: { id: "tgp-m-1-5", title: "탐구교실 1호 계획안 (STEP5)" } } }, yearly: { step4: { id: "tgp-y-4", title: "탐구교실 연간계획안 (STEP4)" }, step5: { id: "tgp-y-5", title: "탐구교실 연간계획안 (STEP5)" } } },
    norience: { monthly: { 1: { step3: { id: "nrp-m-1-3", title: "놀이언스 1호 계획안 (STEP3)" }, step4: { id: "nrp-m-1-4", title: "놀이언스 1호 계획안 (STEP4)" } } }, yearly: { step3: { id: "nrp-y-3", title: "놀이언스 연간계획안 (STEP3)" }, step4: { id: "nrp-y-4", title: "놀이언스 연간계획안 (STEP4)" } } },
  },
  art: {
    monthly: {
      1: { step2: { id: "pa-m-1-2", title: "1호 월간계획안 (STEP2)" }, step3: { id: "pa-m-1-3", title: "1호 월간계획안 (STEP3)" }, step4: { id: "pa-m-1-4", title: "1호 월간계획안 (STEP4)" } },
      2: { step2: { id: "pa-m-2-2", title: "2호 월간계획안 (STEP2)" }, step3: { id: "pa-m-2-3", title: "2호 월간계획안 (STEP3)" }, step4: { id: "pa-m-2-4", title: "2호 월간계획안 (STEP4)" } },
    },
    yearly: {
      step2: { id: "pa-y-2", title: "미술 연간계획안 (STEP2)" },
      step3: { id: "pa-y-3", title: "미술 연간계획안 (STEP3)" },
      step4: { id: "pa-y-4", title: "미술 연간계획안 (STEP4)" },
    },
  },
  reading: {
    monthly: {
      1: { step3: { id: "pr-m-1-3", title: "1호 월간계획안 (STEP3)" }, step4: { id: "pr-m-1-4", title: "1호 월간계획안 (STEP4)" }, step5: { id: "pr-m-1-5", title: "1호 월간계획안 (STEP5)" } },
      2: { step3: { id: "pr-m-2-3", title: "2호 월간계획안 (STEP3)" }, step4: { id: "pr-m-2-4", title: "2호 월간계획안 (STEP4)" } },
    },
    yearly: {
      step3: { id: "pr-y-3", title: "독서 연간계획안 (STEP3)" },
      step4: { id: "pr-y-4", title: "독서 연간계획안 (STEP4)" },
      step5: { id: "pr-y-5", title: "독서 연간계획안 (STEP5)" },
    },
  },
  math: {
    monthly: {
      1: { step3: { id: "pmt-m-1-3", title: "1호 월간계획안 (STEP3)" }, step4: { id: "pmt-m-1-4", title: "1호 월간계획안 (STEP4)" }, step5: { id: "pmt-m-1-5", title: "1호 월간계획안 (STEP5)" } },
      2: { step3: { id: "pmt-m-2-3", title: "2호 월간계획안 (STEP3)" }, step4: { id: "pmt-m-2-4", title: "2호 월간계획안 (STEP4)" } },
    },
    yearly: {
      step3: { id: "pmt-y-3", title: "수학 연간계획안 (STEP3)" },
      step4: { id: "pmt-y-4", title: "수학 연간계획안 (STEP4)" },
      step5: { id: "pmt-y-5", title: "수학 연간계획안 (STEP5)" },
    },
  },
  hangul: {
    monthly: {
      1: { step3: { id: "ph-m-1-3", title: "1호 월간계획안 (STEP3)" }, step4: { id: "ph-m-1-4", title: "1호 월간계획안 (STEP4)" }, step5: { id: "ph-m-1-5", title: "1호 월간계획안 (STEP5)" } },
      2: { step3: { id: "ph-m-2-3", title: "2호 월간계획안 (STEP3)" }, step4: { id: "ph-m-2-4", title: "2호 월간계획안 (STEP4)" }, step5: { id: "ph-m-2-5", title: "2호 월간계획안 (STEP5)" } },
    },
    yearly: {
      step3: { id: "ph-y-3", title: "한글 연간계획안 (STEP3)" },
      step4: { id: "ph-y-4", title: "한글 연간계획안 (STEP4)" },
      step5: { id: "ph-y-5", title: "한글 연간계획안 (STEP5)" },
    },
  },
};

// 그림자료(pictures): tracks/videos와 동일한 구조 — subject → (program) → issue → step → [items]
// 실제 자료는 Firestore "pictures" 컬렉션에서 로드된다. 샘플은 비워둔다.
const SAMPLE_PICTURES = {};

const SAMPLE_EVENTS = {
  english: [
    // Let's Go America 시리즈를 12개 행사로 평면화 (공통 테마 상속)
    {
      id: "ev1-1", title: "California", themeEmoji: "🌴", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-1a", title: "California Dreamin'", duration: "2:30" }, { id: "evt-1-1b", title: "Beach Song", duration: "2:00" }],
      videos: [{ id: "evv-1-1a", title: "California 체험 가이드", duration: "8:00" }],
      plans: [{ id: "evp-1-1a", title: "California 수업 계획안" }],
    },
    {
      id: "ev1-2", title: "New York", themeEmoji: "🗽", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-2a", title: "New York New York", duration: "2:45" }],
      videos: [{ id: "evv-1-2a", title: "New York 체험 가이드", duration: "7:00" }],
      plans: [{ id: "evp-1-2a", title: "New York 수업 계획안" }],
    },
    {
      id: "ev1-3", title: "Texas", themeEmoji: "🤠", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-3a", title: "Cowboy Song", duration: "2:20" }],
      videos: [],
      plans: [{ id: "evp-1-3a", title: "Texas 수업 계획안" }],
    },
    {
      id: "ev1-4", title: "Hawaii", themeEmoji: "🌺", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-4a", title: "Aloha Song", duration: "3:00" }, { id: "evt-1-4b", title: "Hula Dance", duration: "2:30" }],
      videos: [{ id: "evv-1-4a", title: "Hawaii 체험 가이드", duration: "6:00" }],
      plans: [{ id: "evp-1-4a", title: "Hawaii 수업 계획안" }],
    },
    {
      id: "ev1-5", title: "Florida", themeEmoji: "🐊", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-5a", title: "Florida Fun", duration: "2:10" }],
      videos: [],
      plans: [{ id: "evp-1-5a", title: "Florida 수업 계획안" }],
    },
    {
      id: "ev1-6", title: "Alaska", themeEmoji: "🏔", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-6a", title: "Northern Lights", duration: "3:15" }],
      videos: [{ id: "evv-1-6a", title: "Alaska 체험 가이드", duration: "5:00" }],
      plans: [{ id: "evp-1-6a", title: "Alaska 수업 계획안" }],
    },
    {
      id: "ev1-7", title: "Illinois", themeEmoji: "🏙", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-7a", title: "Chicago Jazz", duration: "2:50" }],
      videos: [],
      plans: [{ id: "evp-1-7a", title: "Illinois 수업 계획안" }],
    },
    {
      id: "ev1-8", title: "Colorado", themeEmoji: "⛰", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-8a", title: "Mountain Song", duration: "2:35" }],
      videos: [],
      plans: [{ id: "evp-1-8a", title: "Colorado 수업 계획안" }],
    },
    {
      id: "ev1-9", title: "Louisiana", themeEmoji: "🎺", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-9a", title: "Jazz Parade", duration: "3:00" }],
      videos: [{ id: "evv-1-9a", title: "Louisiana 체험 가이드", duration: "7:00" }],
      plans: [{ id: "evp-1-9a", title: "Louisiana 수업 계획안" }],
    },
    {
      id: "ev1-10", title: "Washington", themeEmoji: "🏛", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-10a", title: "Capital March", duration: "2:15" }],
      videos: [],
      plans: [{ id: "evp-1-10a", title: "Washington 수업 계획안" }],
    },
    {
      id: "ev1-11", title: "Arizona", themeEmoji: "🏜", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-11a", title: "Canyon Echo", duration: "2:40" }],
      videos: [],
      plans: [{ id: "evp-1-11a", title: "Arizona 수업 계획안" }],
    },
    {
      id: "ev1-12", title: "Massachusetts", themeEmoji: "📚", themeColor: "#3b82f6",
      themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)",
      tracks: [{ id: "evt-1-12a", title: "Study Song", duration: "2:00" }],
      videos: [],
      plans: [{ id: "evp-1-12a", title: "Massachusetts 수업 계획안" }],
    },
    {
      id: "ev2", title: "Phonics Day",
      themeEmoji: "🔤", themeColor: "#8b5cf6", themeBg: "linear-gradient(135deg, #4c1d95 0%, #8b5cf6 50%, #c4b5fd 100%)",
      tracks: [{ id: "evt-2a", title: "Phonics Chant", duration: "2:15" }],
      videos: [{ id: "evv-2a", title: "파닉스 데이 시연 영상", duration: "12:00" }],
      plans: [{ id: "evp-2a", title: "Phonics Day 계획안" }],
    },
  ],
  coding: [
    {
      id: "ev3", title: "코딩 로봇 체험",
      themeEmoji: "🤖", themeColor: "#10b981", themeBg: "linear-gradient(135deg, #064e3b 0%, #10b981 50%, #6ee7b7 100%)",
      tracks: [{ id: "evt-3a", title: "로봇 댄스 음악", duration: "2:45" }],
      videos: [{ id: "evv-3a", title: "로봇 코딩 체험 가이드", duration: "8:00" }],
      plans: [{ id: "evp-3a", title: "코딩 로봇 체험 계획안" }],
    },
  ],
  music: [
    {
      id: "ev4", title: "봄 음악회",
      themeEmoji: "🌸", themeColor: "#ec4899", themeBg: "linear-gradient(135deg, #831843 0%, #ec4899 50%, #fbcfe8 100%)",
      tracks: [{ id: "evt-4a", title: "음악회 오프닝", duration: "3:00" }, { id: "evt-4b", title: "봄의 왈츠", duration: "4:15" }, { id: "evt-4c", title: "음악회 피날레", duration: "2:50" }],
      videos: [{ id: "evv-4a", title: "음악회 무대 세팅 가이드", duration: "6:00" }],
      plans: [{ id: "evp-4a", title: "봄 음악회 운영 계획안" }, { id: "evp-4b", title: "봄 음악회 프로그램 순서" }],
    },
  ],
  piano: [
    {
      id: "ev5", title: "피아노 미니 콘서트",
      themeEmoji: "🎹", themeColor: "#f59e0b", themeBg: "linear-gradient(135deg, #78350f 0%, #f59e0b 50%, #fde68a 100%)",
      tracks: [{ id: "evt-5a", title: "콘서트 인트로", duration: "1:30" }],
      videos: [],
      plans: [{ id: "evp-5a", title: "미니 콘서트 계획안" }],
    },
  ],
  senses: [
    {
      id: "ev7", title: "오감 놀이 체험전",
      themeEmoji: "🖐", themeColor: "#f97316", themeBg: "linear-gradient(135deg, #7c2d12 0%, #f97316 50%, #fed7aa 100%)",
      tracks: [{ id: "evt-7a", title: "오감 탐험 배경음악", duration: "2:30" }],
      videos: [{ id: "evv-7a", title: "오감 놀이 활동 가이드", duration: "10:00" }],
      plans: [{ id: "evp-7a", title: "오감 놀이 체험전 운영 계획안" }],
    },
  ],
  science: [
    {
      id: "ev6", title: "과학의 날 행사",
      themeEmoji: "🔬", themeColor: "#06b6d4", themeBg: "linear-gradient(135deg, #164e63 0%, #06b6d4 50%, #a5f3fc 100%)",
      tracks: [{ id: "evt-6a", title: "실험 시작 음악", duration: "1:30" }],
      videos: [{ id: "evv-6a", title: "과학 실험 시연", duration: "15:00" }],
      plans: [{ id: "evp-6a", title: "과학의 날 운영 계획안" }],
    },
  ],
  art: [],
  reading: [],
  math: [],
  hangul: [],
};

// ─── Styles ──────────────────────────────────────────────────────────────────
// 리디자인 포인트 색 (A안 · 토스 스타일)
const ACCENT = "#4F46E5";
const ACCENT_DARK = "#3730A3";
const ACCENT_TINT = "#EEF0FF";
// 오프라인 저장 포인트 색 (주황)
const OFFLINE = "#F2711C";
const OFFLINE_DARK = "#C2410C";
const OFFLINE_TINT = "#FFF1E7";

const font = `'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

const S = {
  app: { fontFamily: font, background: "#F2F4F6", color: "#191F28", minHeight: "100vh", maxWidth: 480, margin: "0 auto", position: "relative", overflow: "hidden" },
  // Header
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 12px 10px 20px", background: "#F2F4F6", position: "sticky", top: 0, zIndex: 100 },
  logoWrap: { display: "flex", alignItems: "center", gap: 10 },
  logoText: { fontSize: 18, fontWeight: 800, letterSpacing: -0.5, color: "#111" },
  logoSub: { fontSize: 9, fontWeight: 500, color: "#8B95A1", letterSpacing: 2, textTransform: "uppercase" },
  // Bottom Nav
  nav: { display: "flex", position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, background: "#fff", borderTop: "1px solid #E5E8EB", zIndex: 100, padding: "4px 0 env(safe-area-inset-bottom, 8px)" },
  navItem: (active) => ({ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "10px 0 6px", minHeight: 48, background: "none", border: "none", cursor: "pointer", color: active ? ACCENT : "#8B95A1", fontSize: 11, fontWeight: active ? 800 : 600, fontFamily: font, transition: "color 0.15s" }),
  navIcon: (active) => ({ fontSize: 22, lineHeight: 1, display: "flex" }),
  // Section
  section: { padding: "20px 20px 100px" },
  sectionTitle: { fontSize: 22, fontWeight: 800, marginBottom: 16, letterSpacing: -0.6, color: "#191F28" },
  sectionSub: { fontSize: 13, fontWeight: 600, color: "#6B7684", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 },
  // Cards
  card: { background: "#fff", border: "none", borderRadius: 18, padding: "16px 18px", marginBottom: 10, cursor: "pointer", transition: "all 0.15s" },
  cardTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4, color: "#191F28" },
  cardMeta: { fontSize: 12, color: "#8B95A1", fontWeight: 500 },
  cardContent: { fontSize: 14, color: "#4E5968", marginTop: 8, lineHeight: 1.6 },
  // Buttons
  btn: (variant = "default") => ({
    padding: variant === "sm" ? "8px 14px" : "12px 20px",
    background: variant === "primary" ? "#111" : variant === "outline" ? "#fff" : "#E8EBEE",
    color: variant === "primary" ? "#fff" : "#333D4B",
    border: variant === "outline" ? "1.5px solid #D1D6DB" : "none",
    borderRadius: 12,
    fontSize: variant === "sm" ? 12 : 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: font,
    transition: "all 0.15s",
  }),
  // Subject grid
  subjectGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 },
  subjectBtn: (active) => ({
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
    padding: "16px 4px 12px", background: active ? "#111" : "#fff",
    color: active ? "#fff" : "#191F28", border: "none",
    borderRadius: 16, cursor: "pointer", fontFamily: font, transition: "all 0.2s",
  }),
  subjectIcon: { fontSize: 22, lineHeight: 1 },
  subjectName: { fontSize: 12, fontWeight: 700, letterSpacing: -0.3 },
  // Badge
  badge: (color = "#111") => ({ display: "inline-block", padding: "2px 8px", background: color, color: color === "#111" ? "#fff" : "#111", borderRadius: 20, fontSize: 10, fontWeight: 700, marginRight: 6 }),
  // Track item
  trackItem: (isPlaying) => ({
    display: "flex", alignItems: "center", gap: 12, padding: "10px 8px 10px 12px",
    background: isPlaying ? ACCENT_TINT : "#fff", color: isPlaying ? ACCENT_DARK : "#191F28",
    border: "none", borderRadius: 18,
    marginBottom: 8, cursor: "pointer", transition: "all 0.2s",
  }),
  trackPlay: (isPlaying) => ({ width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: isPlaying ? ACCENT : "#F2F4F6", color: isPlaying ? "#fff" : "#333D4B", fontSize: 15, flexShrink: 0, border: "none", cursor: "pointer", fontFamily: font }),
  trackInfo: { flex: 1, minWidth: 0 },
  trackTitle: { fontSize: 15, fontWeight: 700, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" },
  trackDuration: (isPlaying) => ({ fontSize: 12, color: isPlaying ? "#6B7684" : "#8B95A1", marginTop: 2 }),
  favBtn: (fav, isPlaying) => ({ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: fav ? "#F04452" : "#C5CBD2", padding: 12, lineHeight: 1, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }),
  // Pill tabs
  pillWrap: { display: "flex", gap: 4, marginBottom: 16, overflowX: "auto", background: "#E5E8EB", borderRadius: 14, padding: 4 },
  pill: (active) => ({ flex: "1 0 auto", padding: "8px 14px", minHeight: 40, borderRadius: 11, border: "none", background: active ? "#fff" : "transparent", color: active ? "#191F28" : "#4E5968", boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none", fontSize: 13, fontWeight: active ? 800 : 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: font }),
  // Textarea
  textarea: { width: "100%", padding: "12px 14px", border: "1.5px solid #E5E8EB", borderRadius: 12, background: "#fff", fontSize: 13, fontFamily: font, resize: "vertical", minHeight: 80, outline: "none", boxSizing: "border-box" },
  input: { width: "100%", padding: "12px 14px", border: "1.5px solid #E5E8EB", borderRadius: 12, background: "#fff", fontSize: 13, fontFamily: font, outline: "none", boxSizing: "border-box", marginBottom: 8 },
  // Pin
  pinBadge: { display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", background: "#fee2e2", color: "#dc2626", borderRadius: 20, fontSize: 10, fontWeight: 700 },
  // Empty
  empty: { textAlign: "center", padding: "40px 20px", color: "#8B95A1", fontSize: 13, fontWeight: 500 },
  // Mini player
  miniPlayer: { position: "fixed", bottom: 58, left: "50%", transform: "translateX(-50%)", width: "calc(100% - 32px)", maxWidth: 448, background: "#111", color: "#fff", borderRadius: 20, padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, zIndex: 99, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" },
  // Back button
  backBtn: { background: "none", border: "none", fontSize: 22, cursor: "pointer", padding: "8px 8px 8px 0", minHeight: 44, color: "#191F28", fontFamily: font, display: "flex", alignItems: "center", gap: 4 },
  backText: { fontSize: 15, fontWeight: 700 },
  // 지금 위치 표시 (예: "1호 · STEP 4") — 뒤로가기는 상단 버튼 하나로 통일
  crumb: { fontSize: 15, fontWeight: 800, color: "#191F28", margin: "2px 0 12px", letterSpacing: -0.3 },
  // Floating action button
  fab: { position: "fixed", bottom: 76, right: "calc(50% - 220px)", width: 48, height: 48, borderRadius: "50%", background: "#111", color: "#fff", border: "none", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 98 },
  // Tag
  tag: { display: "inline-block", padding: "3px 10px", background: "#F2F4F6", borderRadius: 8, fontSize: 11, fontWeight: 600, color: "#4E5968", marginRight: 6, marginBottom: 4 },
  // Divider
  divider: { height: 1, background: "#E5E8EB", margin: "20px 0" },
  // Modal overlay
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  modal: { background: "#fff", borderRadius: "24px 24px 0 0", padding: "24px 20px 40px", width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto" },
  modalTitle: { fontSize: 17, fontWeight: 800, marginBottom: 16 },
};

// Touch target wrapper: ensures minimum 44x44 hit area
const touch44 = { minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" };

// 리디자인: 과목별 아이콘 타일 색 (아이콘 칸에만 연하게 사용)
const SUBJECT_STYLE = {
  english: { color: "#2563EB", tint: "#E8F0FE", glyph: "A" },
  coding: { color: "#0F766E", tint: "#E0F5F2", glyph: "</>" },
  music: { color: "#7C3AED", tint: "#F1EAFE", svg: <><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></> },
  senses: { color: "#C2410C", tint: "#FFEDE3", svg: <><path d="M8 13V5.5a1.5 1.5 0 013 0V12" /><path d="M11 11.5V4a1.5 1.5 0 013 0v7.5" /><path d="M14 11.5V6a1.5 1.5 0 013 0v8a6 6 0 01-6 6h-1a6 6 0 01-4.6-2.2L3 15.5a1.6 1.6 0 012.4-2L8 15" /></> },
  piano: { color: "#4F46E5", tint: "#EEF0FF", svg: <><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M8 4v16M12 4v16M16 4v16" /><path d="M6.5 4v8h3V4zM14.5 4v8h3V4z" fill="currentColor" /></> },
  science: { color: "#0369A1", tint: "#E3F2FB", glyph: "Sc" },
  art: { color: "#BE185D", tint: "#FDE8F1", svg: <><path d="M12 3a9 9 0 100 18c1.1 0 1.8-.9 1.8-1.9 0-.5-.2-.9-.5-1.3-.3-.3-.5-.8-.5-1.3 0-1 .8-1.8 1.8-1.8H17a4 4 0 004-4c0-4.3-4-7.7-9-7.7z" /><circle cx="7.5" cy="11" r="1.2" fill="currentColor" /><circle cx="10.5" cy="7.5" r="1.2" fill="currentColor" /><circle cx="15" cy="7.5" r="1.2" fill="currentColor" /></> },
  reading: { color: "#15803D", tint: "#E5F5EA", glyph: "책" },
  math: { color: "#B45309", tint: "#FEF3DC", glyph: "∑" },
  hangul: { color: "#B91C1C", tint: "#FDE8E8", glyph: "ㄱ" },
};
const subjectStyle = (id) => SUBJECT_STYLE[id] || { color: "#4E5968", tint: "#E8EBEE", glyph: null };
const SubjectTile = ({ id, icon, size = 44 }) => {
  const st = subjectStyle(id);
  const g = st.glyph;
  const box = { width: size, height: size, borderRadius: Math.round(size * 0.3), background: st.tint, color: st.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1, fontFamily: font };
  if (st.svg) return (
    <span style={box}><svg width={Math.round(size * 0.52)} height={Math.round(size * 0.52)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{st.svg}</svg></span>
  );
  return (
    <span style={{ ...box, fontSize: g ? Math.round(size * (g.length > 2 ? 0.3 : 0.4)) : Math.round(size * 0.55), fontWeight: 900, letterSpacing: -0.5 }}>{g || icon}</span>
  );
};

// ─── Icons (simple text) ────────────────────────────────────────────────────
// 선 아이콘 (SVG) — 글자 크기(fontSize)에 맞춰 1em 크기로 그려지고, 색은 글자색(currentColor)을 따름
const svgIcon = (children, { fill = "none", sw = 2 } = {}) => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill={fill} stroke={fill === "none" ? "currentColor" : "none"} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block", verticalAlign: "-0.125em", flexShrink: 0 }} aria-hidden="true">{children}</svg>
);
const Icons = {
  home: svgIcon(<path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1z" />),
  subjects: svgIcon(<><rect x="3" y="3" width="7.5" height="7.5" rx="2" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="2" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="2" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" /></>),
  favorites: svgIcon(<path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />),
  events: svgIcon(<><path d="M12 3L3 20h18z" /><path d="M12 3v17M9 20l3-6 3 6" /></>),
  recent: svgIcon(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  play: svgIcon(<path d="M7 4.5v15l13-7.5z" />, { fill: "currentColor" }),
  pause: svgIcon(<><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></>, { fill: "currentColor" }),
  heart: svgIcon(<path d="M12 21s-7.5-4.6-9.5-9.2C1.2 8.6 3.3 5 6.8 5c2 0 3.4 1.1 5.2 3 1.8-1.9 3.2-3 5.2-3 3.5 0 5.6 3.6 4.3 6.8C19.5 16.4 12 21 12 21z" />, { fill: "currentColor" }),
  heartEmpty: svgIcon(<path d="M12 21s-7.5-4.6-9.5-9.2C1.2 8.6 3.3 5 6.8 5c2 0 3.4 1.1 5.2 3 1.8-1.9 3.2-3 5.2-3 3.5 0 5.6 3.6 4.3 6.8C19.5 16.4 12 21 12 21z" />),
  back: svgIcon(<path d="M15 18l-6-6 6-6" />, { sw: 2.4 }),
  pin: svgIcon(<><path d="M9 4h6l-1 5 3 3v2H7v-2l3-3z" /><path d="M12 14v7" /></>),
  add: svgIcon(<path d="M12 5v14M5 12h14" />, { sw: 2.4 }),
  admin: svgIcon(<><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></>),
  send: svgIcon(<path d="M7 17L17 7M9 7h8v8" />),
  music: svgIcon(<><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></>),
  video: svgIcon(<><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M10 9l5 3-5 3z" /></>),
  plan: svgIcon(<><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V3h6v1M9 10h6M9 14h6M9 18h3" /></>),
  close: svgIcon(<path d="M6 6l12 12M18 6L6 18" />, { sw: 2.2 }),
  search: svgIcon(<><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>),
  repeat: svgIcon(<><path d="M17 2l4 4-4 4" /><path d="M3 11V9a3 3 0 013-3h15" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a3 3 0 01-3 3H3" /></>),
  repeatOne: svgIcon(<><path d="M17 2l4 4-4 4" /><path d="M3 11V9a3 3 0 013-3h15" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a3 3 0 01-3 3H3" /></>),
  download: svgIcon(<path d="M12 4v11M7 10.5l5 5 5-5M5 20h14" />),
  downloaded: svgIcon(<path d="M5 12.5l4.5 4.5L19 7.5" />, { sw: 2.6 }),
  searchLg: svgIcon(<><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>),
  volumeHigh: svgIcon(<><path d="M11 5L6 9H3v6h3l5 4z" /><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" /></>),
  volumeLow: svgIcon(<><path d="M11 5L6 9H3v6h3l5 4z" /><path d="M15.5 8.5a5 5 0 010 7" /></>),
  volumeMute: svgIcon(<><path d="M11 5L6 9H3v6h3l5 4z" /><path d="M16 9l5 6M21 9l-5 6" /></>),
};

// ─── Logo Component ──────────────────────────────────────────────────────────
const Logo = ({ color = "#111", size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    {[20, 30, 40, 50].map((r, i) => (
      <path key={i} d={`M ${50 - r} 60 A ${r} ${r} 0 0 1 ${50 + r} 60`} stroke={color} strokeWidth="4" fill="none" />
    ))}
    <text x="10" y="88" fontSize="20" fontWeight="900" fill={color} fontFamily="sans-serif">Wollim</text>
  </svg>
);

// ─── Main App ────────────────────────────────────────────────────────────────
export default function UllimApp() {
  // ─── Load Pretendard font + keyframes once ─────────────────────────────────
  useEffect(() => {
    // 리디자인: 화면 바깥(넓은 화면의 좌우 여백)도 앱 배경색과 맞춤
    try { document.body.style.background = "#F2F4F6"; } catch (e) {}
    if (!document.querySelector('link[href*="pretendard"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css";
      document.head.appendChild(link);
    }
    if (!document.querySelector('style[data-ullim-keyframes]')) {
      const style = document.createElement("style");
      style.setAttribute("data-ullim-keyframes", "true");
      style.textContent = "@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";
      document.head.appendChild(style);
    }
  }, []);

  // ─── Auth states ────────────────────────────────────────────────────────────
  const [authState, setAuthState] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("ullimAuth") || "null"); return s; } catch { return null; }
  }); // null = not logged in, { name, status, id, role, firestoreId }
  const [authScreen, setAuthScreen] = useState("login"); // "login" | "signup"
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupSubject, setSignupSubject] = useState("");
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  // Admin: user management (loaded from Firestore)
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Firebase Auth: 로그아웃 처리
  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await firebaseSignOut(auth);
      console.log("Firebase Auth 로그아웃 완료");
    } catch (err) {
      console.error("로그아웃 오류:", err);
    }
    setAuthState(null);
  };

  // Firebase Auth: 로그인 처리
  const handleSignIn = async () => {
    if (!loginEmail || !loginPassword) return;
    setLoginError("");
    try {
      const auth = getAuth();
      const cred = await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
      const uid = cred.user.uid;
      // Firestore에서 사용자 정보 조회
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, "users", uid));
      if (!userDoc.exists()) {
        setLoginError("사용자 정보를 찾을 수 없습니다. 관리자에게 문의하세요.");
        await firebaseSignOut(auth);
        return;
      }
      const userData = userDoc.data();
      if (userData.status === "rejected") {
        setLoginError("차단된 계정입니다. 관리자에게 문의하세요.");
        await firebaseSignOut(auth);
        return;
      }
      console.log("로그인 성공:", uid, userData.role, userData.status);
      setAuthState({
        name: userData.name,
        status: userData.status,
        firestoreId: uid,
        role: userData.role,
        email: userData.email,
      });
      setLoginEmail(""); setLoginPassword(""); setLoginError("");
    } catch (err) {
      console.error("로그인 실패:", err);
      if (err.code === "auth/wrong-password" || err.code === "auth/user-not-found" || err.code === "auth/invalid-credential" || err.code === "auth/invalid-email") {
        setLoginError("이메일 또는 비밀번호가 올바르지 않습니다.");
      } else if (err.code === "auth/too-many-requests") {
        setLoginError("로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.");
      } else {
        setLoginError("로그인 중 오류: " + (err.message || ""));
      }
    }
  };

  // Firebase Auth: 자동 세션 복구
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // 로그아웃 상태 — localStorage authState 정리
        if (authState) {
          console.log("Firebase Auth 로그아웃 감지 → 로컬 상태 정리");
          setAuthState(null);
        }
        return;
      }
      // 로그인된 상태인데 authState가 없거나 다른 uid면 → Firestore에서 정보 가져와 동기화
      if (!authState || authState.firestoreId !== user.uid) {
        try {
          const db = getFirestore();
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            const ud = snap.data();
            setAuthState({
              name: ud.name,
              status: ud.status,
              firestoreId: user.uid,
              role: ud.role,
              email: ud.email,
            });
            console.log("Firebase Auth 세션 복구:", user.uid, ud.role);
          }
        } catch (err) {
          console.error("Auth 세션 복구 오류:", err);
        }
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save auth state to localStorage
  useEffect(() => {
    if (authState) {
      localStorage.setItem("ullimAuth", JSON.stringify(authState));
    } else {
      localStorage.removeItem("ullimAuth");
    }
  }, [authState]);

  // Load users from Firestore
  const loadUsersFromFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "users"));
      const users = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      console.log("Firestore 사용자 로드:", users.length, "명");
      setRegisteredUsers(users);
      setUsersLoaded(true);

      // Sync current user's latest status/role from Firestore
      setAuthState(prev => {
        if (!prev?.firestoreId) return prev;
        const currentUser = users.find(u => u.firestoreId === prev.firestoreId);
        if (currentUser && (currentUser.status !== prev.status || currentUser.role !== prev.role)) {
          return { ...prev, name: currentUser.name, status: currentUser.status, role: currentUser.role };
        }
        return prev;
      });
    } catch (err) {
      console.error("사용자 로드 오류:", err);
      setUsersLoaded(true);
    }
  }, []);

  // Load users on mount (시드 로직 제거 — Firebase Auth 기반에서는 일반 회원가입으로 owner 등록)
  useEffect(() => {
    loadUsersFromFirestore();
  }, [loadUsersFromFirestore]);

  // Firestore user helpers
  const addUserToFirestore = async (userData) => {
    try {
      const db = getFirestore();
      const docRef = await addDoc(collection(db, "users"), userData);
      console.log("사용자 등록:", docRef.id, userData.name);
      return docRef.id;
    } catch (err) {
      console.error("사용자 등록 오류:", err);
      return null;
    }
  };

  const updateUserInFirestore = async (firestoreId, updates) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, "users", firestoreId), updates);
      console.log("사용자 업데이트:", firestoreId, updates);
    } catch (err) {
      console.error("사용자 업데이트 오류:", err);
    }
  };

  // ─── Firestore: subjects / programs / events 동적 로딩 ───────────────────
  // Fallback: 로드 전에는 하드코딩 상수 사용 → 기존 기능 안 깨짐
  const [subjects, setSubjects] = useState(SUBJECTS);
  const [programs, setPrograms] = useState(PROGRAMS);
  const [events, setEvents] = useState(SAMPLE_EVENTS);
  // 행사별 콘텐츠 (eventId → {tracks, videos, plans}). Firestore에서 로드.
  const [eventContent, setEventContent] = useState({});
  const [metaLoaded, setMetaLoaded] = useState(false);

  // 과목별 프로그램 조회 헬퍼: 기존 PROGRAMS[subjectId] 사용처를 대체
  // HIDDEN_PROGRAM_IDS: 운영에서 더 이상 쓰지 않아 화면에서 숨기는 프로그램.
  // (Firestore 데이터는 보존되며 화면 노출만 차단 → 나중에 되살리기 쉬움)
  const HIDDEN_PROGRAM_IDS = ["jemplay", "metakinder", "ai-thinking"]; // 코딩: AI엔터코딩만 사용
  const getProgramsBySubject = useCallback(
    (subjectId) => {
      if (!subjectId) return undefined;
      // programs가 배열이면 (Firestore 로드 후) 필터링, 객체면 (fallback) 키 접근
      if (Array.isArray(programs)) {
        const list = programs.filter(p => p.subjectId === subjectId && !HIDDEN_PROGRAM_IDS.includes(p.id));
        return list.length > 0 ? list : undefined;
      }
      if (subjectId === "senses") return SCANDI_YEAR_PROGRAMS;
      const arr = programs[subjectId];
      if (Array.isArray(arr)) {
        const list = arr.filter(p => !HIDDEN_PROGRAM_IDS.includes(p.id));
        return list.length > 0 ? list : undefined;
      }
      return arr;
    },
    [programs]
  );

  // 과목별 허용 STEP 조회 헬퍼: 기존 getStepsForSubject 대체
  const getStepsForSubjectDyn = useCallback(
    (subjectId) => {
      const subj = (Array.isArray(subjects) ? subjects : []).find(s => s.id === subjectId);
      const allowed = subj?.allowedSteps;
      if (allowed === null || allowed === undefined) {
        // 동적 데이터에 정의 없으면 하드코딩 fallback 시도
        const fallback = SUBJECT_STEPS[subjectId];
        if (!fallback) return STEPS;
        if (fallback.length === 0) return [];
        return STEPS.filter(s => fallback.includes(s.id));
      }
      if (allowed.length === 0) return [];
      return STEPS.filter(s => allowed.includes(s.id));
    },
    [subjects]
  );

  // 과목이 STEP 단계를 건너뛰는지 (오감처럼 연령 구분 없는 과목)
  const subjectSkipsStepsDyn = useCallback(
    (subjectId) => {
      const subj = (Array.isArray(subjects) ? subjects : []).find(s => s.id === subjectId);
      if (subj?.allowedSteps !== undefined && subj?.allowedSteps !== null) {
        return subj.allowedSteps.length === 0;
      }
      return SUBJECT_STEPS[subjectId]?.length === 0;
    },
    [subjects]
  );

  // 프로그램의 커스텀 단계 조회 (플레이멜로디의 터치/플레이 같은)
  const getProgramCustomStages = useCallback(
    (programId) => {
      const list = Array.isArray(programs) ? programs : [];
      const prog = list.find(p => p.id === programId);
      if (prog?.customStages && prog.customStages.length > 0) return prog.customStages;
      // Fallback: 기존 하드코딩 처리 (현재는 play-melody만 존재)
      if (programId === "play-melody") return PLAY_MELODY_STAGES;
      return null;
    },
    [programs]
  );

  // Firestore 로드 함수들
  const loadSubjectsFromFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "subjects"));
      if (snapshot.empty) return null; // 시드 트리거
      const list = snapshot.docs
        .map(d => ({ ...d.data(), id: d.data().id || d.id }))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      console.log("Firestore 과목 로드:", list.length, "개");
      setSubjects(list);
      return list;
    } catch (err) {
      console.error("과목 로드 오류:", err);
      return null;
    }
  }, []);

  const loadProgramsFromFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "programs"));
      if (snapshot.empty) return null;
      const list = withScandiYears(snapshot.docs
        .map(d => ({ ...d.data(), id: d.data().id || d.id })))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      console.log("Firestore 프로그램 로드:", list.length, "개");
      setPrograms(list);
      return list;
    } catch (err) {
      console.error("프로그램 로드 오류:", err);
      return null;
    }
  }, []);

  // ─── 발표회 (음악 과목 전용) ───────────────────────────────────────────────
  // recitals 컬렉션 = 발표회 항목(제목만). recitalTracks/Videos/Scores = 각 발표회 하위 콘텐츠.
  // 기존 tracks/videos/plans 컬렉션과 완전히 분리되어 있어 서로 영향이 없다.
  const loadRecitalsFromFirebase = useCallback(async () => {
    try {
      const db = getFirestore();
      const [recSnap, tSnap, vSnap, sSnap] = await Promise.all([
        getDocs(collection(db, "recitals")),
        getDocs(collection(db, "recitalTracks")),
        getDocs(collection(db, "recitalVideos")),
        getDocs(collection(db, "recitalScores")),
      ]);
      const list = recSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setRecitals(list);

      const content = {};
      const ensure = (rid) => { if (!content[rid]) content[rid] = { tracks: [], videos: [], scores: [] }; return content[rid]; };
      tSnap.docs.forEach(d => { const x = { id: d.id, ...d.data() }; if (x.recitalId) ensure(x.recitalId).tracks.push(x); });
      vSnap.docs.forEach(d => { const x = { id: d.id, ...d.data() }; if (x.recitalId) ensure(x.recitalId).videos.push(x); });
      sSnap.docs.forEach(d => { const x = { id: d.id, ...d.data() }; if (x.recitalId) ensure(x.recitalId).scores.push(x); });
      Object.values(content).forEach(c => {
        ["tracks", "videos", "scores"].forEach(k => c[k].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
      });
      setRecitalContent(content);
      console.log("발표회 로드:", list.length, "개");
    } catch (err) {
      console.error("발표회 로드 오류:", err);
    }
  }, []);

  // 새 발표회 항목 추가 (제목만)
  const addRecital = async (title) => {
    const t = (title || "").trim();
    if (!t) return false;
    try {
      const db = getFirestore();
      await addDoc(collection(db, "recitals"), { title: t, order: recitals.length, createdAt: new Date().toISOString() });
      await loadRecitalsFromFirebase();
      return true;
    } catch (err) { console.error("발표회 추가 오류:", err); alert("발표회 추가 실패: " + (err?.message || "")); return false; }
  };

  // 발표회 삭제 (하위 음원/영상/악보 + Storage 파일 모두 정리)
  const deleteRecital = async (rid) => {
    try {
      const db = getFirestore();
      const storage = getStorage();
      const content = recitalContent[rid] || { tracks: [], videos: [], scores: [] };
      const batch = writeBatch(db);
      for (const t of (content.tracks || [])) { if (t.storagePath) { try { await deleteObject(ref(storage, t.storagePath)); } catch (e) {} } batch.delete(doc(db, "recitalTracks", t.id)); }
      for (const v of (content.videos || [])) { batch.delete(doc(db, "recitalVideos", v.id)); }
      for (const s of (content.scores || [])) { if (s.storagePath) { try { await deleteObject(ref(storage, s.storagePath)); } catch (e) {} } batch.delete(doc(db, "recitalScores", s.id)); }
      batch.delete(doc(db, "recitals", rid));
      await batch.commit();
      await loadRecitalsFromFirebase();
      return true;
    } catch (err) { console.error("발표회 삭제 오류:", err); alert("발표회 삭제 실패: " + (err?.message || "")); return false; }
  };

  // 발표회에 음원/영상/악보 추가 (음원·악보는 여러 개 한번에)
  const uploadRecitalItem = async (rid) => {
    if (!recitalEditItem || !rid) return;
    setRecitalUploading(true);
    try {
      const db = getFirestore();
      const cur = recitalContent[rid] || { tracks: [], videos: [], scores: [] };
      if (recitalEditItem.kind === "video") {
        // 영상은 유튜브 URL 1개
        if (!recitalEditItem.title?.trim()) { alert("제목을 입력해주세요"); setRecitalUploading(false); return; }
        if (!recitalEditItem.youtubeUrl?.trim()) { alert("YouTube URL을 입력해주세요"); setRecitalUploading(false); return; }
        if (!getYoutubeId(recitalEditItem.youtubeUrl)) { alert("유튜브 주소가 올바르지 않습니다."); setRecitalUploading(false); return; }
        await addDoc(collection(db, "recitalVideos"), { recitalId: rid, title: recitalEditItem.title.trim(), youtubeUrl: recitalEditItem.youtubeUrl.trim(), order: (cur.videos?.length || 0), createdAt: new Date().toISOString() });
      } else if (recitalEditItem.kind === "track" || recitalEditItem.kind === "score") {
        // 음원·악보는 여러 파일 일괄 업로드
        if (!recitalBulkFiles.length) { alert(recitalEditItem.kind === "track" ? "음원 파일을 선택해주세요" : "악보 파일을 선택해주세요"); setRecitalUploading(false); return; }
        const storage = getStorage();
        const isTrack = recitalEditItem.kind === "track";
        const sub = isTrack ? "tracks" : "scores";
        const coll = isTrack ? "recitalTracks" : "recitalScores";
        const base = isTrack ? (cur.tracks?.length || 0) : (cur.scores?.length || 0);
        for (let i = 0; i < recitalBulkFiles.length; i++) {
          const bf = recitalBulkFiles[i];
          if (!bf.rawFile) continue;
          const safeName = bf.rawFile.name.replace(/[\s\/\\'"]/g, '_');
          const storagePath = `recitals/${rid}/${sub}/${Date.now()}_${i}_${safeName}`;
          const fileRef = ref(storage, storagePath);
          await uploadBytes(fileRef, bf.rawFile);
          const downloadUrl = await getDownloadURL(fileRef);
          const title = (bf.title?.trim()) || bf.rawFile.name.replace(/\.[^.]+$/, "");
          if (isTrack) {
            let duration = bf.duration || "";
            if (!duration) { try { duration = await extractAudioDuration(bf.rawFile); } catch (e) {} }
            await addDoc(collection(db, coll), { recitalId: rid, title, duration, storagePath, downloadUrl, order: base + i, createdAt: new Date().toISOString() });
          } else {
            await addDoc(collection(db, coll), { recitalId: rid, title, storagePath, downloadUrl, order: base + i, createdAt: new Date().toISOString() });
          }
        }
      }
      setRecitalEditItem(null);
      setRecitalBulkFiles([]);
      await loadRecitalsFromFirebase();
    } catch (err) { console.error("발표회 콘텐츠 업로드 오류:", err); alert("업로드 실패: " + (err?.message || "알 수 없는 오류")); }
    finally { setRecitalUploading(false); }
  };

  // 발표회 콘텐츠(음원/영상/악보) 개별 삭제
  const deleteRecitalItem = async (kind, item) => {
    if (!window.confirm(`"${item.title}"\n\n삭제하시겠습니까?`)) return;
    try {
      const db = getFirestore();
      const coll = kind === "track" ? "recitalTracks" : kind === "video" ? "recitalVideos" : "recitalScores";
      if (item.storagePath) { try { await deleteObject(ref(getStorage(), item.storagePath)); } catch (e) {} }
      await deleteDoc(doc(db, coll, item.id));
      await loadRecitalsFromFirebase();
    } catch (err) { console.error("발표회 콘텐츠 삭제 오류:", err); alert("삭제 실패: " + (err?.message || "")); }
  };

  const loadEventsFromFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "events"));
      if (snapshot.empty) return null;
      // events는 과목별 그룹핑된 객체로 변환 (기존 SAMPLE_EVENTS 구조 호환)
      const grouped = {};
      snapshot.docs.forEach(d => {
        const data = { ...d.data(), id: d.data().id || d.id };
        const subj = data.subjectId;
        if (!subj) return;
        if (!grouped[subj]) grouped[subj] = [];
        grouped[subj].push(data);
      });
      // 과목별로 order 정렬
      Object.keys(grouped).forEach(k => {
        grouped[k].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      });
      console.log("Firestore 행사 로드:", snapshot.size, "개");
      setEvents(grouped);
      return grouped;
    } catch (err) {
      console.error("행사 로드 오류:", err);
      return null;
    }
  }, []);

  // 시드 함수들 — 컬렉션이 비어있을 때만 실행됨
  const seedSubjectsToFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      SUBJECTS.forEach((s, idx) => {
        const ref = doc(db, "subjects", s.id);
        batch.set(ref, {
          id: s.id,
          name: s.name,
          nameEn: s.nameEn,
          icon: s.icon,
          category: s.category,
          parent: s.parent ?? null,
          order: idx,
          // SUBJECT_STEPS 흡수: 미지정 과목은 null (전 연령 허용)
          allowedSteps: SUBJECT_STEPS[s.id] !== undefined ? SUBJECT_STEPS[s.id] : null,
        });
      });
      await batch.commit();
      console.log("subjects 시드 완료:", SUBJECTS.length, "개");
    } catch (err) {
      console.error("subjects 시드 오류:", err);
    }
  }, []);

  const seedProgramsToFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      let order = 0;
      Object.entries(PROGRAMS).forEach(([subjectId, progList]) => {
        progList.forEach(p => {
          const ref = doc(db, "programs", p.id);
          batch.set(ref, {
            id: p.id,
            subjectId,
            name: p.name,
            icon: p.icon,
            desc: p.desc ?? "",
            order: order++,
            // PLAY_MELODY_STAGES 흡수
            customStages: p.id === "play-melody" ? PLAY_MELODY_STAGES : null,
          });
        });
      });
      await batch.commit();
      console.log("programs 시드 완료");
    } catch (err) {
      console.error("programs 시드 오류:", err);
    }
  }, []);

  // 코드의 PROGRAMS 목록 중 Firestore에 아직 없는 프로그램만 추가한다.
  // 기존 프로그램은 건드리지 않아 안전하며, 새 프로그램을 배포 후 관리자가 한 번 눌러 반영한다.
  const [programSyncMsg, setProgramSyncMsg] = useState("");
  const [programSyncing, setProgramSyncing] = useState(false);
  const syncProgramsToFirestore = useCallback(async () => {
    setProgramSyncing(true);
    setProgramSyncMsg("");
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "programs"));
      const existingIds = new Set(snapshot.docs.map(d => d.id));
      // 현재 order 최댓값 뒤로 새 프로그램을 붙임
      let maxOrder = -1;
      snapshot.docs.forEach(d => { const o = d.data().order; if (typeof o === "number" && o > maxOrder) maxOrder = o; });

      const toAdd = [];
      Object.entries(PROGRAMS).forEach(([subjectId, progList]) => {
        progList.forEach(p => {
          if (!existingIds.has(p.id)) toAdd.push({ subjectId, p });
        });
      });

      if (toAdd.length === 0) {
        setProgramSyncMsg("이미 모든 프로그램이 등록되어 있습니다.");
        setProgramSyncing(false);
        return;
      }

      const batch = writeBatch(db);
      let order = maxOrder + 1;
      toAdd.forEach(({ subjectId, p }) => {
        batch.set(doc(db, "programs", p.id), {
          id: p.id,
          subjectId,
          name: p.name,
          icon: p.icon,
          desc: p.desc ?? "",
          order: order++,
          customStages: p.id === "play-melody" ? PLAY_MELODY_STAGES : null,
        });
      });
      await batch.commit();
      await loadProgramsFromFirestore();
      setProgramSyncMsg(`${toAdd.length}개 프로그램을 추가했습니다: ${toAdd.map(x => x.p.name).join(", ")}`);
    } catch (err) {
      console.error("프로그램 동기화 오류:", err);
      setProgramSyncMsg("동기화 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
    setProgramSyncing(false);
  }, [loadProgramsFromFirestore]);

  const seedEventsToFirestore = useCallback(async () => {
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      let order = 0;
      Object.entries(SAMPLE_EVENTS).forEach(([subjectId, evList]) => {
        evList.forEach(ev => {
          const ref = doc(db, "events", ev.id);
          // date/desc/subEvents 필드는 단순화 정책에 따라 저장하지 않음
          batch.set(ref, {
            id: ev.id,
            subjectId,
            title: ev.title,
            themeEmoji: ev.themeEmoji ?? "",
            themeColor: ev.themeColor ?? "#666",
            themeBg: ev.themeBg ?? "",
            tracks: ev.tracks ?? [],
            videos: ev.videos ?? [],
            plans: ev.plans ?? [],
            order: order++,
          });
        });
      });
      await batch.commit();
      console.log("events 시드 완료");
    } catch (err) {
      console.error("events 시드 오류:", err);
    }
  }, []);

  // 앱 시작 시 metadata 로드 + 비어있으면 시드
  useEffect(() => {
    const initMeta = async () => {
      // 1차 로드 시도
      const [s, p, e] = await Promise.all([
        loadSubjectsFromFirestore(),
        loadProgramsFromFirestore(),
        loadEventsFromFirestore(),
      ]);
      // 비어있는 컬렉션 시드
      const seedTasks = [];
      if (s === null) seedTasks.push(seedSubjectsToFirestore().then(loadSubjectsFromFirestore));
      if (p === null) seedTasks.push(seedProgramsToFirestore().then(loadProgramsFromFirestore));
      if (e === null) seedTasks.push(seedEventsToFirestore().then(loadEventsFromFirestore));
      if (seedTasks.length > 0) {
        console.log("빈 컬렉션 시드 시작:", seedTasks.length, "개");
        await Promise.all(seedTasks);
      }
      setMetaLoaded(true);
    };
    initMeta();
  }, [
    loadSubjectsFromFirestore, loadProgramsFromFirestore, loadEventsFromFirestore,
    seedSubjectsToFirestore, seedProgramsToFirestore, seedEventsToFirestore,
  ]);

  // ─── Firebase: auto-load tracks (Firestore metadata + Storage fallback) ──
  const [firebaseLoading, setFirebaseLoading] = useState(false);

  // Helper: iOS Safari 호환 파일 선택창.
  // document.createElement로 만든 input은 화면(DOM)에 붙어있지 않으면 iOS에서 onchange가 무시되어
  // 파일을 골라도 목록에 뜨지 않는다. input을 잠깐 body에 붙였다가 선택 후 제거한다.
  const openFilePicker = ({ accept, multiple = true, onFiles }) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = multiple;
    if (accept) input.accept = accept;
    // 화면에는 안 보이지만 DOM에는 존재하도록 배치 (iOS 호환 핵심)
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "0";
    input.style.opacity = "0";
    const cleanup = () => { try { document.body.removeChild(input); } catch (e) {} };
    input.onchange = (e) => {
      const files = Array.from(e.target.files || []);
      cleanup();
      if (files.length) onFiles(files);
    };
    // 사용자가 취소하고 다시 열 때 남지 않도록 포커스 복귀 시 정리
    const onFocusBack = () => {
      window.removeEventListener("focus", onFocusBack);
      setTimeout(() => { if (document.body.contains(input) && (!input.files || input.files.length === 0)) cleanup(); }, 500);
    };
    window.addEventListener("focus", onFocusBack);
    document.body.appendChild(input);
    input.click();
  };

  // Helper: extract audio duration from a file
  const extractAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener("loadedmetadata", () => {
        const totalSec = Math.round(audio.duration);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        resolve(`${min}:${sec.toString().padStart(2, "0")}`);
        URL.revokeObjectURL(audio.src);
      });
      audio.addEventListener("error", () => resolve("0:00"));
      audio.src = URL.createObjectURL(file);
    });
  };

  // Helper: extract duration from a URL
  const extractDurationFromUrl = (url) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener("loadedmetadata", () => {
        const totalSec = Math.round(audio.duration);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        resolve(`${min}:${sec.toString().padStart(2, "0")}`);
      });
      audio.addEventListener("error", () => resolve("0:00"));
      audio.crossOrigin = "anonymous";
      audio.src = url;
    });
  };

  // Add track metadata to Firestore
  const addTrackToFirestore = async (trackData) => {
    try {
      const db = getFirestore();
      const docRef = await addDoc(collection(db, "tracks"), {
        ...trackData,
        createdAt: new Date().toISOString(),
      });
      console.log("트랙 메타데이터 등록 완료:", docRef.id, trackData.title);
      return docRef.id;
    } catch (err) {
      console.error("트랙 메타데이터 등록 오류:", err);
      return null;
    }
  };

  // Delete track metadata from Firestore
  const deleteTrackFromFirestore = async (firestoreId) => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, "tracks", firestoreId));
      console.log("트랙 메타데이터 삭제 완료:", firestoreId);
    } catch (err) {
      console.error("트랙 메타데이터 삭제 오류:", err);
    }
  };

  // 트랙 메타데이터 업데이트 (제목 등 수정)
  const updateTrackInFirestore = async (firestoreId, updates) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, "tracks", firestoreId), updates);
      console.log("트랙 업데이트 완료:", firestoreId);
      return true;
    } catch (err) {
      console.error("트랙 업데이트 오류:", err);
      return false;
    }
  };

  // 관리자: 콘텐츠 목록에서 항목을 위/아래로 한 칸 이동 (음원/영상/그림자료 공통)
  // 현재 화면 목록(list) 안에서 idx 위치 항목을 dir(-1=위, +1=아래)로 옮기고,
  // 두 항목의 order 값을 맞바꿔 Firestore와 로컬에 반영한다.
  const [reordering, setReordering] = useState(false);
  const reorderAdminItem = async (list, idx, dir) => {
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    if (reordering) return;
    setReordering(true);
    try {
      // 스왑 대신: 현재 화면 순서에서 두 항목의 위치를 바꾼 뒤, 목록 전체에 order를 0,1,2...로 다시 매긴다.
      // 이렇게 하면 order 값이 없거나 중복(발표회 폴더 등)이어도 항상 정확히 반영된다.
      const reordered = list.slice();
      const [moved] = reordered.splice(idx, 1);
      reordered.splice(targetIdx, 0, moved);

      // id → 새 order 매핑
      const orderById = {};
      reordered.forEach((it, i) => { orderById[it.id] = i; });

      const db = getFirestore();
      const coll = adminContentType === "track" ? "tracks" : adminContentType === "video" ? "videos" : adminContentType === "picture" ? "pictures" : null;
      if (coll) {
        const batch = writeBatch(db);
        reordered.forEach((it) => {
          if (it.firestoreId) batch.update(doc(db, coll, it.firestoreId), { order: orderById[it.id] });
        });
        await batch.commit();
      }

      // 로컬 state에도 새 order 반영: 과목/프로그램 아래 모든 issue·step 배열을 훑어 id로 찾아 order 갱신
      const subj = adminContentSubject, prog = adminContentProgram;
      const hasProgs = getProgramsBySubject(subj);
      const applyOrder = (prev) => {
        const u = JSON.parse(JSON.stringify(prev));
        const bump = (arr) => Array.isArray(arr) ? arr.map(x => (x.id in orderById) ? { ...x, order: orderById[x.id] } : x) : arr;
        const root = (hasProgs && prog) ? u?.[subj]?.[prog] : u?.[subj];
        if (root && typeof root === "object") {
          Object.keys(root).forEach(issueKey => {
            const issueData = root[issueKey];
            if (issueData && typeof issueData === "object") {
              Object.keys(issueData).forEach(stepKey => { issueData[stepKey] = bump(issueData[stepKey]); });
            }
          });
        }
        return u;
      };
      if (adminContentType === "track") setTracks(applyOrder);
      else if (adminContentType === "video") setVideos(applyOrder);
      else if (adminContentType === "picture") setPictures(applyOrder);
    } catch (err) {
      console.error("순서 변경 오류:", err);
      alert("순서 변경 중 오류가 발생했습니다.");
    }
    setReordering(false);
  };

  // ─── 행사 콘텐츠 업로드/삭제 ───────────────────────────────────────────
  const uploadEventItem = async (eventSource) => {
    if (!eventEditItem || !eventSource?.id) return;
    if (!eventEditItem.title?.trim()) {
      alert("제목을 입력해주세요");
      return;
    }
    const eid = eventSource.id;
    setEventUploading(true);
    try {
      const db = getFirestore();
      if (eventEditItem.kind === "track") {
        if (!eventEditItem.rawFile) {
          alert("음원 파일을 선택해주세요");
          setEventUploading(false);
          return;
        }
        const storage = getStorage();
        const safeName = eventEditItem.rawFile.name.replace(/[\s\/\\'"]/g, '_');
        const storagePath = `events/${eid}/tracks/${Date.now()}_${safeName}`;
        const fileRef = ref(storage, storagePath);
        await uploadBytes(fileRef, eventEditItem.rawFile);
        const downloadUrl = await getDownloadURL(fileRef);
        let duration = eventEditItem.duration || "";
        if (!duration) {
          try { duration = await extractAudioDuration(eventEditItem.rawFile); } catch {}
        }
        await addDoc(collection(db, "tracks"), {
          eventId: eid,
          title: eventEditItem.title,
          duration,
          storagePath,
          downloadUrl,
          order: (eventContent[eid]?.tracks?.length || 0),
          createdAt: new Date().toISOString(),
        });
        console.log("행사 음원 등록 완료:", eid, eventEditItem.title);
        await loadTracksFromFirebase();
      } else if (eventEditItem.kind === "video") {
        if (!eventEditItem.youtubeUrl?.trim()) {
          alert("YouTube URL을 입력해주세요");
          setEventUploading(false);
          return;
        }
        await addDoc(collection(db, "videos"), {
          eventId: eid,
          title: eventEditItem.title,
          duration: eventEditItem.duration || "",
          youtubeUrl: eventEditItem.youtubeUrl,
          order: (eventContent[eid]?.videos?.length || 0),
          createdAt: new Date().toISOString(),
        });
        console.log("행사 영상 등록 완료:", eid, eventEditItem.title);
        await loadVideosFromFirestore();
      } else if (eventEditItem.kind === "plan") {
        if (!eventEditItem.rawFile) {
          alert("PDF 파일을 선택해주세요");
          setEventUploading(false);
          return;
        }
        const storage = getStorage();
        const safeName = eventEditItem.rawFile.name.replace(/[\s\/\\'"]/g, '_');
        const storagePath = `events/${eid}/plans/${Date.now()}_${safeName}`;
        const fileRef = ref(storage, storagePath);
        await uploadBytes(fileRef, eventEditItem.rawFile);
        await addDoc(collection(db, "plans"), {
          eventId: eid,
          title: eventEditItem.title,
          storagePath,
          order: (eventContent[eid]?.plans?.length || 0),
          createdAt: new Date().toISOString(),
        });
        console.log("행사 계획안 등록 완료:", eid, eventEditItem.title);
        await loadPlansFromFirebase();
      }
      setEventEditItem(null);
    } catch (err) {
      console.error("행사 콘텐츠 업로드 오류:", err);
      alert("업로드 실패: " + (err?.message || "알 수 없는 오류"));
    } finally {
      setEventUploading(false);
    }
  };

  // 행사 콘텐츠 일괄 업로드 (음원/계획안만 — 영상은 YouTube URL이라 단일)
  const uploadEventBulk = async (eventSource, kind) => {
    if (!eventBulkFiles.length || !eventSource?.id) return;
    const eid = eventSource.id;
    setEventUploading(true);
    setEventBulkProgress({ current: 0, total: eventBulkFiles.length });
    let success = 0, failed = 0;
    try {
      const db = getFirestore();
      const storage = getStorage();
      const baseOrder = kind === "track"
        ? (eventContent[eid]?.tracks?.length || 0)
        : (eventContent[eid]?.plans?.length || 0);
      for (let i = 0; i < eventBulkFiles.length; i++) {
        const bf = eventBulkFiles[i];
        setEventBulkProgress({ current: i + 1, total: eventBulkFiles.length });
        try {
          const safeName = bf.rawFile.name.replace(/[\s\/\\'"]/g, '_');
          const subFolder = kind === "track" ? "tracks" : "plans";
          const storagePath = `events/${eid}/${subFolder}/${Date.now()}_${i}_${safeName}`;
          const fileRef = ref(storage, storagePath);
          await uploadBytes(fileRef, bf.rawFile);
          if (kind === "track") {
            const downloadUrl = await getDownloadURL(fileRef);
            let duration = bf.duration || "";
            if (!duration) {
              try { duration = await extractAudioDuration(bf.rawFile); } catch {}
            }
            await addDoc(collection(db, "tracks"), {
              eventId: eid,
              title: bf.title || bf.rawFile.name.replace(/\.[^/.]+$/, ""),
              duration,
              storagePath,
              downloadUrl,
              order: baseOrder + i,
              createdAt: new Date().toISOString(),
            });
          } else {
            await addDoc(collection(db, "plans"), {
              eventId: eid,
              title: bf.title || bf.rawFile.name.replace(/\.[^/.]+$/, ""),
              storagePath,
              order: baseOrder + i,
              createdAt: new Date().toISOString(),
            });
          }
          success++;
          console.log(`[행사 일괄 업로드] ${i + 1}/${eventBulkFiles.length} 완료: ${bf.title}`);
        } catch (err) {
          failed++;
          console.error(`[행사 일괄 업로드] ${i + 1} 실패:`, err);
        }
      }
      console.log(`행사 일괄 업로드 완료: 성공 ${success} / 실패 ${failed}`);
      if (kind === "track") await loadTracksFromFirebase();
      else await loadPlansFromFirebase();
      setEventBulkFiles([]);
      setEventEditItem(null);
      if (failed > 0) alert(`업로드 완료: 성공 ${success}건, 실패 ${failed}건`);
    } catch (err) {
      console.error("일괄 업로드 오류:", err);
      alert("업로드 중 오류 발생: " + (err?.message || ""));
    } finally {
      setEventUploading(false);
      setEventBulkProgress(null);
    }
  };

  const deleteEventItem = async (kind, item) => {
    if (!item?.firestoreId) return;
    if (!window.confirm(`"${item.title}" 을(를) 삭제하시겠습니까?`)) return;
    try {
      const db = getFirestore();
      const collectionName = kind === "track" ? "tracks" : kind === "video" ? "videos" : "plans";
      // Storage 파일 삭제 (있는 경우)
      if (item.storagePath) {
        try {
          const storage = getStorage();
          await deleteObject(ref(storage, item.storagePath));
          console.log("Storage 파일 삭제:", item.storagePath);
        } catch (err) {
          console.warn("Storage 삭제 실패 (무시 가능):", err.message);
        }
      }
      // Firestore 문서 삭제
      await deleteDoc(doc(db, collectionName, item.firestoreId));
      console.log(`${collectionName} 삭제 완료:`, item.firestoreId);
      // 새로고침
      if (kind === "track") await loadTracksFromFirebase();
      else if (kind === "video") await loadVideosFromFirestore();
      else await loadPlansFromFirebase();
    } catch (err) {
      console.error("행사 콘텐츠 삭제 오류:", err);
      alert("삭제 실패: " + (err?.message || "알 수 없는 오류"));
    }
  };

  // ─── 행사 자체 CRUD (events 컬렉션) ────────────────────────────────────
  const saveEventToFirestore = async (eventData, isNew) => {
    try {
      const db = getFirestore();
      const subjectId = eventData.subjectId || adminEventSubject;
      if (!subjectId) {
        alert("과목을 선택해주세요");
        return false;
      }
      if (!eventData.title?.trim()) {
        alert("행사명을 입력해주세요");
        return false;
      }

      if (isNew) {
        // 신규: 문서 ID 자동 생성 (ev_타임스탬프)
        const newId = `ev_${Date.now()}`;
        const existing = events[subjectId] || [];
        const order = existing.length;
        await setDoc(doc(db, "events", newId), {
          id: newId,
          subjectId,
          title: eventData.title.trim(),
          themeEmoji: eventData.themeEmoji || "🎉",
          themeColor: eventData.themeColor || "#666",
          themeBg: eventData.themeBg || "linear-gradient(135deg, #1a1a2e 0%, #4a4a6e 50%, #6e6e8e 100%)",
          tracks: [],
          videos: [],
          plans: [],
          order,
          createdAt: new Date().toISOString(),
        });
        console.log("행사 등록 완료:", newId, eventData.title);
      } else {
        // 수정
        await updateDoc(doc(db, "events", eventData.id), {
          title: eventData.title.trim(),
          themeEmoji: eventData.themeEmoji || "🎉",
          themeColor: eventData.themeColor || "#666",
          themeBg: eventData.themeBg || "",
          updatedAt: new Date().toISOString(),
        });
        console.log("행사 수정 완료:", eventData.id);
      }
      await loadEventsFromFirestore();
      return true;
    } catch (err) {
      console.error("행사 저장 오류:", err);
      alert("저장 실패: " + (err?.message || "알 수 없는 오류"));
      return false;
    }
  };

  const deleteEventFromFirestore = async (ev) => {
    if (!ev?.id) return;
    if (!window.confirm(`"${ev.title}" 행사를 삭제하시겠습니까?\n\n주의: 이 행사에 등록된 음원/영상/계획안도 함께 삭제됩니다.`)) return;
    try {
      const db = getFirestore();
      const storage = getStorage();
      // 행사 콘텐츠 삭제 (tracks, videos, plans)
      const ec = eventContent[ev.id] || {};
      const allItems = [
        ...(ec.tracks || []).map(t => ({ ...t, _kind: "tracks" })),
        ...(ec.videos || []).map(v => ({ ...v, _kind: "videos" })),
        ...(ec.plans || []).map(p => ({ ...p, _kind: "plans" })),
      ];
      for (const item of allItems) {
        if (!item.firestoreId) continue;
        try {
          if (item.storagePath) {
            try { await deleteObject(ref(storage, item.storagePath)); } catch {}
          }
          await deleteDoc(doc(db, item._kind, item.firestoreId));
        } catch (err) {
          console.warn("콘텐츠 삭제 실패 (계속 진행):", err.message);
        }
      }
      // 행사 문서 삭제
      await deleteDoc(doc(db, "events", ev.id));
      console.log("행사 삭제 완료:", ev.id);
      // 새로고침
      await Promise.all([
        loadEventsFromFirestore(),
        loadTracksFromFirebase(),
        loadVideosFromFirestore(),
        loadPlansFromFirebase(),
      ]);
    } catch (err) {
      console.error("행사 삭제 오류:", err);
      alert("삭제 실패: " + (err?.message || "알 수 없는 오류"));
    }
  };

  const loadTracksFromFirebase = useCallback(async () => {
    setFirebaseLoading(true);
    console.log("Firestore 트랙 로딩 시작...");
    try {
      const db = getFirestore();
      const trackSnapshot = await getDocs(collection(db, "tracks"));
      const firestoreTracks = trackSnapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      console.log("Firestore 트랙 로드 완료:", firestoreTracks.length, "개");

      // Build nested structure from flat Firestore docs
      const newTracks = {};
      const newEventTracks = {}; // eventId → tracks[]
      firestoreTracks.forEach(t => {
        // 행사 트랙: eventId 필드가 있으면 events 그룹으로 분류
        if (t.eventId) {
          if (!newEventTracks[t.eventId]) newEventTracks[t.eventId] = [];
          newEventTracks[t.eventId].push({
            id: t.firestoreId,
            firestoreId: t.firestoreId,
            title: t.title || "제목 없음",
            duration: t.duration || "0:00",
            storagePath: t.storagePath || null,
            downloadUrl: t.downloadUrl || null,
            eventId: t.eventId,
            order: t.order ?? 999,
          });
          return;
        }
        const { subject, program, issue, step } = t;
        if (!subject || !issue || !step) return;
        console.log(`[트랙 병합] ${subject}/${program || "-"}/${issue}/${step} → "${t.title}"`);
        const hasProgs = getProgramsBySubject(subject);

        if (hasProgs && program) {
          if (!newTracks[subject]) newTracks[subject] = {};
          if (!newTracks[subject][program]) newTracks[subject][program] = {};
          if (!newTracks[subject][program][issue]) newTracks[subject][program][issue] = {};
          if (!newTracks[subject][program][issue][step]) newTracks[subject][program][issue][step] = [];
          newTracks[subject][program][issue][step].push({
            id: t.firestoreId,
            firestoreId: t.firestoreId,
            title: t.title || "제목 없음",
            duration: t.duration || "0:00",
            storagePath: t.storagePath || null,
            downloadUrl: t.downloadUrl || null,
            subject,
            program,
            order: t.order ?? 999,
            createdAt: t.createdAt || null,
          });
        } else {
          if (!newTracks[subject]) newTracks[subject] = {};
          if (!newTracks[subject][issue]) newTracks[subject][issue] = {};
          if (!newTracks[subject][issue][step]) newTracks[subject][issue][step] = [];
          newTracks[subject][issue][step].push({
            id: t.firestoreId,
            firestoreId: t.firestoreId,
            title: t.title || "제목 없음",
            duration: t.duration || "0:00",
            storagePath: t.storagePath || null,
            downloadUrl: t.downloadUrl || null,
            subject,
            order: t.order ?? 999,
            createdAt: t.createdAt || null,
          });
        }
      });

      // 행사 트랙 정렬 후 eventContent state에 병합
      Object.keys(newEventTracks).forEach(eid => {
        newEventTracks[eid].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      });
      setEventContent(prev => {
        const next = { ...prev };
        Object.keys(newEventTracks).forEach(eid => {
          if (!next[eid]) next[eid] = { tracks: [], videos: [], plans: [] };
          next[eid] = { ...next[eid], tracks: newEventTracks[eid] };
        });
        return next;
      });

      // Sort each step's tracks by order (order가 겹치면 올린 시각 순)
      const sortTracks = (obj) => {
        for (const key of Object.keys(obj)) {
          if (Array.isArray(obj[key])) {
            obj[key] = sortByUploadOrder(obj[key]);
          } else if (typeof obj[key] === "object" && obj[key] !== null) {
            sortTracks(obj[key]);
          }
        }
      };
      sortTracks(newTracks);

      // Merge with existing sample data: Firestore data overrides
      setTracks(prev => {
        const merged = JSON.parse(JSON.stringify(prev));
        for (const [subj, subjData] of Object.entries(newTracks)) {
          if (!merged[subj]) merged[subj] = {};
          const hasProgs = getProgramsBySubject(subj);
          if (hasProgs) {
            for (const [prog, progData] of Object.entries(subjData)) {
              if (!merged[subj][prog]) merged[subj][prog] = {};
              for (const [issue, issueData] of Object.entries(progData)) {
                if (!merged[subj][prog][issue]) merged[subj][prog][issue] = {};
                for (const [step, stepTracks] of Object.entries(issueData)) {
                  merged[subj][prog][issue][step] = stepTracks;
                }
              }
            }
          } else {
            for (const [issue, issueData] of Object.entries(subjData)) {
              if (!merged[subj][issue]) merged[subj][issue] = {};
              for (const [step, stepTracks] of Object.entries(issueData)) {
                merged[subj][issue][step] = stepTracks;
              }
            }
          }
        }
        return merged;
      });
    } catch (err) {
      console.error("Firestore 트랙 로드 오류:", err);
    }
    setFirebaseLoading(false);
  }, []);

  // ─── Firestore: 그림자료(pictures) ──────────────────────────────────────────
  // 그림자료 메타데이터를 Firestore "pictures" 컬렉션에 저장 (계획안/트랙과 동일 패턴)
  const addPictureToFirestore = async (pictureData) => {
    try {
      const db = getFirestore();
      const docRef = await addDoc(collection(db, "pictures"), {
        ...pictureData,
        createdAt: new Date().toISOString(),
      });
      console.log("그림자료 메타데이터 등록 완료:", docRef.id);
      return docRef.id;
    } catch (err) {
      console.error("그림자료 메타데이터 등록 오류:", err);
      return null;
    }
  };

  // 그림자료 로드: tracks 로더와 동일 구조(subject → (program) → issue → step → [items])
  const loadPicturesFromFirebase = useCallback(async () => {
    console.log("Firestore 그림자료 로딩 시작...");
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "pictures"));
      const firestorePics = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      console.log("Firestore 그림자료 로드 완료:", firestorePics.length, "개");

      const newPics = {};
      firestorePics.forEach(p => {
        const { subject, program, issue, step } = p;
        if (!subject || !issue || !step) return;
        const hasProgs = getProgramsBySubject(subject);
        const item = {
          id: p.firestoreId,
          firestoreId: p.firestoreId,
          title: p.title || "제목 없음",
          storagePath: p.storagePath || null,
          fileUrl: null,
          subject,
          program: (hasProgs && program) ? program : undefined,
          order: p.order ?? 999,
        };
        if (hasProgs && program) {
          if (!newPics[subject]) newPics[subject] = {};
          if (!newPics[subject][program]) newPics[subject][program] = {};
          if (!newPics[subject][program][issue]) newPics[subject][program][issue] = {};
          if (!newPics[subject][program][issue][step]) newPics[subject][program][issue][step] = [];
          newPics[subject][program][issue][step].push(item);
        } else {
          if (!newPics[subject]) newPics[subject] = {};
          if (!newPics[subject][issue]) newPics[subject][issue] = {};
          if (!newPics[subject][issue][step]) newPics[subject][issue][step] = [];
          newPics[subject][issue][step].push(item);
        }
      });

      // 각 step의 항목을 order 기준 정렬
      const sortPics = (obj) => {
        for (const key of Object.keys(obj)) {
          if (Array.isArray(obj[key])) {
            obj[key].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
          } else if (typeof obj[key] === "object" && obj[key] !== null) {
            sortPics(obj[key]);
          }
        }
      };
      sortPics(newPics);

      setPictures(prev => {
        const merged = JSON.parse(JSON.stringify(prev));
        for (const [subj, subjData] of Object.entries(newPics)) {
          if (!merged[subj]) merged[subj] = {};
          const hasProgs = getProgramsBySubject(subj);
          if (hasProgs) {
            for (const [prog, progData] of Object.entries(subjData)) {
              if (!merged[subj][prog]) merged[subj][prog] = {};
              for (const [issue, issueData] of Object.entries(progData)) {
                if (!merged[subj][prog][issue]) merged[subj][prog][issue] = {};
                for (const [step, items] of Object.entries(issueData)) {
                  merged[subj][prog][issue][step] = items;
                }
              }
            }
          } else {
            for (const [issue, issueData] of Object.entries(subjData)) {
              if (!merged[subj][issue]) merged[subj][issue] = {};
              for (const [step, items] of Object.entries(issueData)) {
                merged[subj][issue][step] = items;
              }
            }
          }
        }
        return merged;
      });
    } catch (err) {
      console.error("Firestore 그림자료 로드 오류:", err);
    }
  }, []);

  // ─── Firestore: 방문 기록(visits) ──────────────────────────────────────────
  // 강사가 앱에 접속하면 "하루 1회" 방문을 기록한다.
  // 문서 ID를 {userId}_{날짜}로 지정 → 같은 날 여러 번 접속해도 같은 문서에 덮어써져 자동으로 하루 1회 처리.
  // 관리자만 조회하는 통계용 데이터 (강사별 누적 방문일 수 등).
  const lastVisitLogRef = useRef(0); // 마지막으로 사용 기록을 남긴 시각 (중복 집계 방지)
  const logVisit = useCallback(async (user) => {
    try {
      if (!user?.firestoreId) return;
      lastVisitLogRef.current = Date.now();
      const now = new Date();
      // 로컬 시간 기준 YYYY-MM-DD (KST 환경 기준으로 그날 날짜)
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;
      const docId = `${user.firestoreId}_${dateStr}`;

      const db = getFirestore();
      const ref = doc(db, "visits", docId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        // 오늘 이미 방문 기록 있음 → 같은 날 재접속 시 count만 증가 (참고용, 하루 1명=1건은 문서 존재로 판단)
        await updateDoc(ref, {
          count: (snap.data().count || 1) + 1,
          lastVisitAt: now.toISOString(),
        });
      } else {
        await setDoc(ref, {
          userId: user.firestoreId,
          userName: user.name || "이름없음",
          date: dateStr,
          count: 1,
          firstVisitAt: now.toISOString(),
          lastVisitAt: now.toISOString(),
        });
        console.log("방문 기록:", user.name, dateStr);
      }
    } catch (err) {
      // 방문 기록 실패는 앱 사용에 영향 주지 않도록 조용히 처리
      console.error("방문 기록 오류:", err);
    }
  }, []);

  // 관리자 통계용: 방문 기록 전체 로드
  const loadVisitStats = useCallback(async () => {
    setVisitStatsLoading(true);
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "visits"));
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setVisitStats(list);
      console.log("방문 통계 로드 완료:", list.length, "건");
    } catch (err) {
      console.error("방문 통계 로드 오류:", err);
    }
    setVisitStatsLoading(false);
  }, []);

  // ─── Firestore: 공유 게시판(posts) ─────────────────────────────────────────
  // 강사들이 직접 수업 노하우/팁을 공유하는 게시판.
  // 문서 구조: { subject, program, title, body, authorId, authorName, createdAt, updatedAt }
  const loadPostsFromFirebase = useCallback(async () => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, "posts"));
      const list = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      // 최신 글이 위로 오도록 정렬
      list.sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      setPosts(list);
      console.log("공유 게시글 로드 완료:", list.length, "개");
    } catch (err) {
      console.error("공유 게시글 로드 오류:", err);
    }
  }, []);

  const savePost = async ({ id, title, body, youtubeUrl, subject, program }) => {
    try {
      const db = getFirestore();
      const now = new Date().toISOString();
      if (id) {
        // 수정
        await updateDoc(doc(db, "posts", id), { title, body, youtubeUrl: youtubeUrl || "", updatedAt: now });
      } else {
        // 신규 작성
        await addDoc(collection(db, "posts"), {
          subject,
          program,
          title,
          body,
          youtubeUrl: youtubeUrl || "",
          authorId: authState?.firestoreId || null,
          authorName: authState?.name || "익명",
          createdAt: now,
          updatedAt: now,
        });
      }
      await loadPostsFromFirebase();
      return true;
    } catch (err) {
      console.error("공유 게시글 저장 오류:", err);
      alert("저장 중 오류가 발생했습니다. 다시 시도해주세요.");
      return false;
    }
  };

  const deletePost = async (postId) => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, "posts", postId));
      await loadPostsFromFirebase();
      return true;
    } catch (err) {
      console.error("공유 게시글 삭제 오류:", err);
      alert("삭제 중 오류가 발생했습니다.");
      return false;
    }
  };

  // ─── Firestore: auto-load plans ──────────────────────────────────────────────
  // Add plan metadata to Firestore
  const addPlanToFirestore = async (planData) => {
    try {
      const db = getFirestore();
      const docRef = await addDoc(collection(db, "plans"), {
        ...planData,
        createdAt: new Date().toISOString(),
      });
      console.log("계획안 메타데이터 등록 완료:", docRef.id);
      return docRef.id;
    } catch (err) {
      console.error("계획안 메타데이터 등록 오류:", err);
      return null;
    }
  };

  const loadPlansFromFirebase = useCallback(async () => {
    console.log("Firestore 계획안 로딩 시작...");
    try {
      const db = getFirestore();
      const planSnapshot = await getDocs(collection(db, "plans"));
      const firestorePlans = planSnapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      console.log("Firestore 계획안 로드 완료:", firestorePlans.length, "개");

      const newPlans = {};
      const eventPlans = {}; // eventId → plans[]
      firestorePlans.forEach(p => {
        // 행사 계획안: eventId 필드가 있으면 별도 그룹
        if (p.eventId) {
          if (!eventPlans[p.eventId]) eventPlans[p.eventId] = [];
          eventPlans[p.eventId].push({
            id: p.firestoreId,
            firestoreId: p.firestoreId,
            title: p.title || "제목 없음",
            storagePath: p.storagePath || null,
            fileUrl: null,
            eventId: p.eventId,
            order: p.order ?? 999,
          });
          return;
        }
        const { subject, program, issue, step } = p;
        if (!subject || !issue || !step) return;
        const hasProgs = getProgramsBySubject(subject);
        const planItem = {
          id: p.firestoreId,
          firestoreId: p.firestoreId,
          title: p.title || "제목 없음",
          storagePath: p.storagePath || null,
          fileUrl: null,
          order: p.order ?? 999,
        };

        if (hasProgs && program) {
          if (!newPlans[subject]) newPlans[subject] = {};
          if (!newPlans[subject][program]) newPlans[subject][program] = { monthly: {}, yearly: {} };
          if (!newPlans[subject][program].monthly[issue]) newPlans[subject][program].monthly[issue] = {};
          if (!Array.isArray(newPlans[subject][program].monthly[issue][step])) newPlans[subject][program].monthly[issue][step] = [];
          newPlans[subject][program].monthly[issue][step].push(planItem);
        } else {
          if (!newPlans[subject]) newPlans[subject] = { monthly: {}, yearly: {} };
          if (!newPlans[subject].monthly) newPlans[subject].monthly = {};
          if (!newPlans[subject].monthly[issue]) newPlans[subject].monthly[issue] = {};
          if (!Array.isArray(newPlans[subject].monthly[issue][step])) newPlans[subject].monthly[issue][step] = [];
          newPlans[subject].monthly[issue][step].push(planItem);
        }
      });

      // 각 호+STEP의 계획안 목록을 order → 제목 순으로 정렬
      const sortPlanArrays = (obj) => {
        for (const key of Object.keys(obj)) {
          if (Array.isArray(obj[key])) {
            obj[key].sort((a, b) => {
              const oa = a?.order ?? 999, ob = b?.order ?? 999;
              if (oa !== ob) return oa - ob;
              return (a?.title || "").localeCompare(b?.title || "", "ko");
            });
          } else if (typeof obj[key] === "object" && obj[key] !== null) {
            sortPlanArrays(obj[key]);
          }
        }
      };
      sortPlanArrays(newPlans);

      // 행사 계획안 정렬 + state 병합
      Object.keys(eventPlans).forEach(eid => {
        eventPlans[eid].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      });
      setEventContent(prev => {
        const next = { ...prev };
        Object.keys(eventPlans).forEach(eid => {
          if (!next[eid]) next[eid] = { tracks: [], videos: [], plans: [] };
          next[eid] = { ...next[eid], plans: eventPlans[eid] };
        });
        return next;
      });

      // Merge into plans state
      setPlans(prev => {
        const merged = JSON.parse(JSON.stringify(prev));
        for (const [subj, subjData] of Object.entries(newPlans)) {
          const hasProgs = getProgramsBySubject(subj);
          if (hasProgs) {
            if (!merged[subj]) merged[subj] = {};
            for (const [prog, progData] of Object.entries(subjData)) {
              if (!merged[subj][prog]) merged[subj][prog] = { monthly: {}, yearly: {} };
              for (const [issue, issueData] of Object.entries(progData?.monthly || {})) {
                if (!merged[subj][prog].monthly) merged[subj][prog].monthly = {};
                if (!merged[subj][prog].monthly[issue]) merged[subj][prog].monthly[issue] = {};
                for (const [step, plan] of Object.entries(issueData || {})) {
                  merged[subj][prog].monthly[issue][step] = plan;
                }
              }
            }
          } else {
            if (!merged[subj]) merged[subj] = { monthly: {}, yearly: {} };
            for (const [issue, issueData] of Object.entries(subjData?.monthly || {})) {
              if (!merged[subj].monthly) merged[subj].monthly = {};
              if (!merged[subj].monthly[issue]) merged[subj].monthly[issue] = {};
              for (const [step, plan] of Object.entries(issueData || {})) {
                merged[subj].monthly[issue][step] = plan;
              }
            }
          }
        }
        return merged;
      });
    } catch (err) {
      console.error("Firestore 계획안 로드 오류:", err);
    }
  }, []);

  useEffect(() => {
    if (authState?.status === "approved") {
      loadTracksFromFirebase();
      loadVideosFromFirestore();
      loadPlansFromFirebase();
      loadPicturesFromFirebase();
      loadPostsFromFirebase();
      loadRecitalsFromFirebase();
      logVisit(authState); // 방문 기록 (하루 1회, 관리자 통계용)
    }
  }, [authState, loadTracksFromFirebase, loadPlansFromFirebase, loadPicturesFromFirebase, loadPostsFromFirebase, loadRecitalsFromFirebase, logVisit]);

  // 홈 화면 앱(PWA)은 껐다 켜도 페이지가 새로고침되지 않는 경우가 많아 사용이 집계되지 않는다.
  // 앱이 백그라운드에 있다가 다시 화면에 나타나면 새로운 사용으로 보고 집계한다.
  // (탭 전환 등으로 과다 집계되지 않도록 30분 이내 재진입은 같은 사용으로 간주)
  useEffect(() => {
    const handleVisible = () => {
      if (typeof document === "undefined" || document.visibilityState !== "visible") return;
      if (authState?.status !== "approved") return;
      if (Date.now() - lastVisitLogRef.current < 30 * 60 * 1000) return;
      logVisit(authState);
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, [authState, logVisit]);

  // ─── Firestore: Videos (YouTube links) ────────────────────────────────────
  const [firestoreVideos, setFirestoreVideos] = useState([]);

  const loadVideosFromFirestore = async () => {
    try {
      const db = getFirestore();
      const q = query(collection(db, "videos"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      const vids = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      setFirestoreVideos(vids);
      console.log("Firestore 영상 로드 완료:", vids.length, "개");

      // 행사 영상 분류
      const eventVideos = {};

      // Merge into videos state
      setVideos(prev => {
        const merged = JSON.parse(JSON.stringify(prev));
        vids.forEach(v => {
          // 행사 영상: eventId 필드가 있으면 별도 그룹으로
          if (v.eventId) {
            if (!eventVideos[v.eventId]) eventVideos[v.eventId] = [];
            eventVideos[v.eventId].push({
              id: v.firestoreId,
              firestoreId: v.firestoreId,
              title: v.title,
              duration: v.duration || "",
              youtubeUrl: v.youtubeUrl,
              eventId: v.eventId,
              order: v.order ?? 999,
            });
            return;
          }
          const { subject, program, issue, step } = v;
          const hasProgs = getProgramsBySubject(subject);
          if (hasProgs && program) {
            if (!merged[subject]) merged[subject] = {};
            if (!merged[subject][program]) merged[subject][program] = {};
            if (!merged[subject][program][issue]) merged[subject][program][issue] = {};
            if (!merged[subject][program][issue][step]) merged[subject][program][issue][step] = [];
            // Avoid duplicates
            if (!merged[subject][program][issue][step].find(x => x.id === v.firestoreId)) {
              merged[subject][program][issue][step].push({
                id: v.firestoreId,
                title: v.title,
                duration: v.duration || "",
                youtubeUrl: v.youtubeUrl,
              });
            }
          } else {
            if (!merged[subject]) merged[subject] = {};
            if (!merged[subject][issue]) merged[subject][issue] = {};
            if (!merged[subject][issue][step]) merged[subject][issue][step] = [];
            if (!merged[subject][issue][step].find(x => x.id === v.firestoreId)) {
              merged[subject][issue][step].push({
                id: v.firestoreId,
                title: v.title,
                duration: v.duration || "",
                youtubeUrl: v.youtubeUrl,
              });
            }
          }
        });
        return merged;
      });

      // 행사 영상 정렬 + state 병합
      Object.keys(eventVideos).forEach(eid => {
        eventVideos[eid].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      });
      setEventContent(prev => {
        const next = { ...prev };
        Object.keys(eventVideos).forEach(eid => {
          if (!next[eid]) next[eid] = { tracks: [], videos: [], plans: [] };
          next[eid] = { ...next[eid], videos: eventVideos[eid] };
        });
        return next;
      });
    } catch (err) {
      console.error("Firestore 영상 로드 오류:", err);
    }
  };

  const addVideoToFirestore = async (videoData) => {
    try {
      const db = getFirestore();
      const docRef = await addDoc(collection(db, "videos"), {
        ...videoData,
        createdAt: new Date().toISOString(),
      });
      console.log("영상 등록 완료:", docRef.id);
      await loadVideosFromFirestore(); // 새로고침
      return docRef.id;
    } catch (err) {
      console.error("영상 등록 오류:", err);
      return null;
    }
  };

  const deleteVideoFromFirestore = async (firestoreId) => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, "videos", firestoreId));
      console.log("영상 삭제 완료:", firestoreId);
      await loadVideosFromFirestore(); // 새로고침
    } catch (err) {
      console.error("영상 삭제 오류:", err);
    }
  };

  // 영상 메타데이터 업데이트 (제목 등 수정)
  const updateVideoInFirestore = async (firestoreId, updates) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, "videos", firestoreId), updates);
      console.log("영상 업데이트 완료:", firestoreId);
      await loadVideosFromFirestore(); // 새로고침
      return true;
    } catch (err) {
      console.error("영상 업데이트 오류:", err);
      return false;
    }
  };

  // ─── App states ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState("home");
  const [subPage, setSubPage] = useState(null);
  const [subjectOrigin, setSubjectOrigin] = useState("home"); // 과목 화면에 들어오기 전 화면 (뒤로가기 목적지)
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  // User-specific storage key helper
  const userKey = (key) => authState?.firestoreId ? `ullim_${authState.firestoreId}_${key}` : `ullim_guest_${key}`;

  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ullim_favs") || "[]"); } catch { return []; }
  });
  const [playingTrack, setPlayingTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // 한곡 반복: 휴대폰에 저장해 두어 새로고침·앱 재실행 후에도 유지
  const [repeatMode, setRepeatMode] = useState(() => {
    try { return localStorage.getItem("ullim_repeatMode") === "1"; } catch (e) { return false; }
  }); // true = 한곡 반복
  const repeatModeRef = useRef(repeatMode);
  useEffect(() => {
    repeatModeRef.current = repeatMode;
    try { localStorage.setItem("ullim_repeatMode", repeatMode ? "1" : "0"); } catch (e) {}
  }, [repeatMode]);

  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [playerCollapsed, setPlayerCollapsed] = useState(false); // 플레이어를 아래로 내려 작은 버튼만 보이게
  const PLAYBACK_RATES = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
  const selectPlaybackRate = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
    setShowSpeedMenu(false);
  };
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminPw, setAdminPw] = useState("");
  const [adminPwError, setAdminPwError] = useState(false);
  const [adminTab, setAdminTab] = useState("dashboard");

  // 관리자가 '통계' 탭을 열면 방문 기록을 불러온다.
  // (showAdmin/adminLoggedIn/adminTab 선언 이후에 위치해야 함 — 선언 전 참조 시 초기화 오류 발생)
  useEffect(() => {
    if (showAdmin && adminLoggedIn && adminTab === "stats") {
      loadVisitStats();
    }
  }, [showAdmin, adminLoggedIn, adminTab, loadVisitStats]);
  const [adminContentSubject, setAdminContentSubject] = useState("english");
  const [adminContentProgram, setAdminContentProgram] = useState(null);
  const [adminContentType, setAdminContentType] = useState("track");
  const [adminContentIssue, setAdminContentIssue] = useState(null);
  const [adminContentStep, setAdminContentStep] = useState(null);
  const [adminEditItem, setAdminEditItem] = useState(null);
  // 행사 콘텐츠 편집용
  const [eventEditItem, setEventEditItem] = useState(null);
  const [eventUploading, setEventUploading] = useState(false);
  // 행사 다중 업로드용
  const [eventBulkFiles, setEventBulkFiles] = useState([]);
  const [eventBulkProgress, setEventBulkProgress] = useState(null);
  // 이모지 피커 (행사 추가/수정 폼)
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [emojiCategoryIdx, setEmojiCategoryIdx] = useState(0);
  const [adminUploadedFiles, setAdminUploadedFiles] = useState({});
  // Play count tracking
  const [playCounts, setPlayCounts] = useState({});
  const [visitStats, setVisitStats] = useState([]); // 방문 기록 목록 (관리자 통계용)
  const [visitStatsLoading, setVisitStatsLoading] = useState(false);
  // Recent play history: [{ track, timestamp }]
  const [recentHistory, setRecentHistory] = useState([]);
  // Bulk upload staging
  const [bulkFiles, setBulkFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  // Upload progress state
  const [uploadStatus, setUploadStatus] = useState(null); // null | { type: "uploading"|"success"|"error", current: n, total: n, message: "" }
  const [singleUploadStatus, setSingleUploadStatus] = useState(null); // null | "uploading" | "success" | "error"
  const [bulkVideoLinks, setBulkVideoLinks] = useState("");
  const [adminEventSubject, setAdminEventSubject] = useState("english");
  const [adminNewTitle, setAdminNewTitle] = useState("");
  const [adminNewDuration, setAdminNewDuration] = useState("");
  const [adminNewEventTitle, setAdminNewEventTitle] = useState("");
  const [adminNewEventDate, setAdminNewEventDate] = useState("");
  const [adminNewEventDesc, setAdminNewEventDesc] = useState("");
  const [adminConfirmDelete, setAdminConfirmDelete] = useState(null);
  // events state는 위쪽 metadata 시스템에서 정의함
  const [tracks, setTracks] = useState(SAMPLE_TRACKS);
  const [videos, setVideos] = useState(SAMPLE_VIDEOS);
  const [plans, setPlans] = useState(SAMPLE_PLANS);
  const [pictures, setPictures] = useState(SAMPLE_PICTURES); // 그림자료: 호 → step → [파일] (계획안 monthly와 동일 구조)
  const [posts, setPosts] = useState([]); // 공유 게시판: 강사들이 직접 작성하는 수업 노하우/팁 글
  const [postEditing, setPostEditing] = useState(null); // { id?, title, body } — 작성/수정 중인 글 (null이면 목록 보기)
  const [postViewing, setPostViewing] = useState(null); // 상세보기 중인 글
  const [subjectTab, setSubjectTab] = useState("tracks");
  const [contentIssue, setContentIssue] = useState(null);
  const [contentStep, setContentStep] = useState(null);
  const [planType, setPlanType] = useState("monthly");
  const [searchTerm, setSearchTerm] = useState("");

  // 탭/이슈/스텝/과목 변경 시 검색어 자동 초기화 (사용자가 헷갈리지 않도록)
  // 탭/이슈/스텝/과목 변경 시 검색어 자동 초기화 (사용자가 헷갈리지 않도록)
  // 함수형 업데이트로 React 렌더링 사이클 안전하게 처리
  useEffect(() => {
    setSearchTerm(prev => prev === "" ? prev : "");
  }, [subjectTab, contentIssue, contentStep, selectedSubject, selectedProgram]);
  // Player progress
  const [playerProgress, setPlayerProgress] = useState(0); // 0~100
  const [isDragging, setIsDragging] = useState(false);
  const [volume, setVolume] = useState(80); // 0~100
  const [showVolume, setShowVolume] = useState(false);
  const volumeRef = useRef(null);
  // Playlists: { subjectId: [trackId, trackId, ...] }
  const [playlists, setPlaylists] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ullim_playlists") || "{}"); } catch { return {}; }
  });
  const [favSubjectFilter, setFavSubjectFilter] = useState("all");
  const [reorderMode, setReorderMode] = useState(false);
  // Drag & drop refs for favorites reorder
  const dragItemRef = useRef(null);
  const dragOverItemRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  // Event detail
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventTab, setEventTab] = useState("tracks");
  const [eventSubject, setEventSubject] = useState(null); // 행사 탭에서 선택한 과목
  // 발표회 (음악 과목 전용): 음악 곡명으로 항목을 만들고 그 안에 음원/영상/악보를 관리
  const [recitals, setRecitals] = useState([]); // [{id, title, order}]
  const [recitalContent, setRecitalContent] = useState({}); // { recitalId: { tracks:[], videos:[], scores:[] } }
  const [showRecitals, setShowRecitals] = useState(false); // 음악 과목에서 발표회 목록 보기
  const [selectedRecital, setSelectedRecital] = useState(null); // 열람 중인 발표회
  const [recitalTab, setRecitalTab] = useState("tracks"); // tracks / videos / scores
  const [recitalEditItem, setRecitalEditItem] = useState(null); // 관리자 콘텐츠 추가 폼 { kind, title, ... }
  const [recitalUploading, setRecitalUploading] = useState(false);
  const [recitalBulkFiles, setRecitalBulkFiles] = useState([]); // 음원/악보 여러 개 한번에 업로드 { id, rawFile, title, duration }
  const [newRecitalTitle, setNewRecitalTitle] = useState(""); // 새 발표회 제목 입력
  // Video player
  const [playingVideo, setPlayingVideo] = useState(null);
  // Document viewer
  const [viewingDoc, setViewingDoc] = useState(null); // { title, fileUrl, fileType }
  // Global search
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  // Offline downloads
  const [downloadedFiles, setDownloadedFiles] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ullim_downloads") || "[]"); } catch { return []; }
  });
  const [downloadingFiles, setDownloadingFiles] = useState([]);
  const progressRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollRef = useRef(null);

  // Track whether user-specific data has been loaded (prevents save-before-load race condition)
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  // Firestore 동기화 디바운스용 ref
  const favsSaveTimerRef = useRef(null);
  const plSaveTimerRef = useRef(null);

  // Save: 변경 시 localStorage(즉시) + Firestore(디바운스 1.2초)
  useEffect(() => {
    if (!userDataLoaded || !authState?.firestoreId) return;
    // 1) localStorage 즉시 백업 (오프라인 대응)
    localStorage.setItem(userKey("favs"), JSON.stringify(favorites));
    // 2) Firestore 디바운스 저장 (연속 클릭 시 호출 합치기)
    if (favsSaveTimerRef.current) clearTimeout(favsSaveTimerRef.current);
    favsSaveTimerRef.current = setTimeout(async () => {
      try {
        const db = getFirestore();
        await updateDoc(doc(db, "users", authState.firestoreId), { favorites });
        console.log("[동기화] 즐겨찾기 Firestore 저장:", favorites.length, "개");
      } catch (err) {
        console.warn("즐겨찾기 Firestore 저장 실패 (localStorage는 저장됨):", err.message);
      }
    }, 1200);
    return () => { if (favsSaveTimerRef.current) clearTimeout(favsSaveTimerRef.current); };
  }, [favorites, userDataLoaded, authState?.firestoreId]);

  useEffect(() => {
    if (!userDataLoaded || !authState?.firestoreId) return;
    localStorage.setItem(userKey("playlists"), JSON.stringify(playlists));
    if (plSaveTimerRef.current) clearTimeout(plSaveTimerRef.current);
    plSaveTimerRef.current = setTimeout(async () => {
      try {
        const db = getFirestore();
        await updateDoc(doc(db, "users", authState.firestoreId), { playlists });
        console.log("[동기화] 플레이리스트 Firestore 저장");
      } catch (err) {
        console.warn("플레이리스트 Firestore 저장 실패:", err.message);
      }
    }, 1200);
    return () => { if (plSaveTimerRef.current) clearTimeout(plSaveTimerRef.current); };
  }, [playlists, userDataLoaded, authState?.firestoreId]);

  useEffect(() => {
    if (!userDataLoaded || !authState?.firestoreId) return;
    // 다운로드 목록은 기기별로 다르므로 localStorage만 사용
    localStorage.setItem(userKey("downloads"), JSON.stringify(downloadedFiles));
  }, [downloadedFiles, userDataLoaded, authState?.firestoreId]);

  // Load user-specific favorites/playlists when auth changes
  // 우선순위: Firestore → localStorage 백업 → 빈 배열
  useEffect(() => {
    setUserDataLoaded(false);
    if (!authState?.firestoreId) {
      setFavorites([]);
      setPlaylists({});
      setUserDataLoaded(false);
      return;
    }
    const uid = authState.firestoreId;
    const loadUserData = async () => {
      // 1) localStorage에서 즉시 로드 (UI 빠른 반응)
      let favs = [];
      let pls = {};
      try {
        favs = JSON.parse(localStorage.getItem(userKey("favs")) || "[]");
        pls = JSON.parse(localStorage.getItem(userKey("playlists")) || "{}");
        const savedDL = JSON.parse(localStorage.getItem(userKey("downloads")) || "[]");
        setDownloadedFiles(savedDL);
      } catch { }
      setFavorites(favs);
      setPlaylists(pls);

      // 2) Firestore에서 최신 데이터 가져오기
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          const data = snap.data();
          const fsFavs = Array.isArray(data.favorites) ? data.favorites : null;
          const fsPls = (data.playlists && typeof data.playlists === "object") ? data.playlists : null;

          // 마이그레이션: Firestore에 없으면 localStorage 데이터 업로드
          const needsMigration = (fsFavs === null && favs.length > 0) || (fsPls === null && Object.keys(pls).length > 0);

          if (needsMigration) {
            console.log("[마이그레이션] localStorage 즐겨찾기/플레이리스트를 Firestore로 업로드");
            const update = {};
            if (fsFavs === null) update.favorites = favs;
            if (fsPls === null) update.playlists = pls;
            try { await updateDoc(doc(db, "users", uid), update); } catch (err) { console.warn("마이그레이션 실패:", err.message); }
          } else {
            // Firestore가 진실의 원천
            if (fsFavs !== null) {
              setFavorites(fsFavs);
              localStorage.setItem(userKey("favs"), JSON.stringify(fsFavs));
              console.log("[동기화] Firestore 즐겨찾기 로드:", fsFavs.length, "개");
            }
            if (fsPls !== null) {
              setPlaylists(fsPls);
              localStorage.setItem(userKey("playlists"), JSON.stringify(fsPls));
              console.log("[동기화] Firestore 플레이리스트 로드");
            }
          }
        }
      } catch (err) {
        console.warn("Firestore 사용자 데이터 로드 실패 (localStorage 백업 사용):", err.message);
      }

      // Mark loaded after a tick to ensure state is set before saves begin
      setTimeout(() => setUserDataLoaded(true), 100);
    };
    loadUserData();
  }, [authState?.firestoreId]);

  // Validate cached files on startup
  useEffect(() => {
    const validateCache = async () => {
      try {
        const cache = await caches.open("ullim-audio-cache-v1");
        const keys = await cache.keys();
        const cachedIds = keys.map(k => {
          const parts = k.url.split("/offline-audio/");
          return parts.length > 1 ? parts[1] : null;
        }).filter(Boolean);
        if (cachedIds.length > 0) {
          setDownloadedFiles(prev => {
            const merged = [...new Set([...prev, ...cachedIds])];
            return merged;
          });
        }
      } catch (err) { /* cache not available */ }
    };
    validateCache();
  }, []);

  useEffect(() => {
    if (showGlobalSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showGlobalSearch]);

  const toggleFav = (trackId, subjectId) => {
    setFavorites(f => {
      if (f.includes(trackId)) return f.filter(x => x !== trackId);
      return [...f, trackId];
    });
    // Also manage playlist
    if (subjectId) {
      setPlaylists(pl => {
        const list = pl[subjectId] || [];
        if (list.includes(trackId)) {
          const newList = list.filter(x => x !== trackId);
          const newPl = { ...pl, [subjectId]: newList };
          if (newList.length === 0) delete newPl[subjectId];
          return newPl;
        }
        return { ...pl, [subjectId]: [...list, trackId] };
      });
    } else {
      // Find subject from allTracksFlat
      const trk = allTracksFlat.find(t => t.id === trackId);
      if (trk) {
        setPlaylists(pl => {
          const list = pl[trk.subject] || [];
          if (list.includes(trackId)) {
            const newList = list.filter(x => x !== trackId);
            const newPl = { ...pl, [trk.subject]: newList };
            if (newList.length === 0) delete newPl[trk.subject];
            return newPl;
          }
          return { ...pl, [trk.subject]: [...list, trackId] };
        });
      }
    }
  };

  // 즐겨찾기 순서 이동: 특정 과목 플레이리스트에서 idx 항목을 dir(-1=위, +1=아래)로 한 칸 이동
  const movePlaylistItem = (subjectId, idx, dir) => {
    const targetIdx = idx + dir;
    setPlaylists(prev => {
      const arr = [...(prev[subjectId] || [])];
      if (targetIdx < 0 || targetIdx >= arr.length) return prev;
      const [moved] = arr.splice(idx, 1);
      arr.splice(targetIdx, 0, moved);
      return { ...prev, [subjectId]: arr };
    });
  };

  // Flatten all tracks from nested structure for favorites
  const getAllTracksFlat = () => {
    const result = [];
    Object.entries(tracks).forEach(([subj, subjData]) => {
      if (!subjData) return;
      const hasPrograms = getProgramsBySubject(subj);
      if (hasPrograms) {
        // program -> issue -> step -> tracks
        Object.entries(subjData).forEach(([prog, issues]) => {
          if (!issues || typeof issues !== "object") return;
          Object.entries(issues).forEach(([issue, steps]) => {
            if (!steps || typeof steps !== "object") return;
            Object.entries(steps).forEach(([step, trks]) => {
              if (!Array.isArray(trks)) return;
              trks.forEach(t => result.push({ ...t, subject: subj, program: prog, issue: normalizeIssue(issue), step }));
            });
          });
        });
      } else {
        // issue -> step -> tracks
        Object.entries(subjData).forEach(([issue, steps]) => {
          if (!steps || typeof steps !== "object") return;
          Object.entries(steps).forEach(([step, trks]) => {
            if (!Array.isArray(trks)) return;
            trks.forEach(t => result.push({ ...t, subject: subj, issue: normalizeIssue(issue), step }));
          });
        });
      }
    });
    // 행사(Events) 음원도 포함시킨다.
    // 행사 음원은 tracks가 아닌 eventContent/events에 저장되어, 예전에는 즐겨찾기·오프라인 저장 목록에서
    // 제목을 찾지 못해 문서 ID가 그대로 노출되고 재생 버튼도 비활성화되는 문제가 있었다.
    const seenIds = new Set(result.map(t => t?.id).filter(Boolean));
    const pushEventTrack = (t, ev) => {
      if (!t?.id || seenIds.has(t.id)) return;
      seenIds.add(t.id);
      result.push({
        ...t,
        isEvent: true,
        eventId: ev?.id ?? t.eventId ?? null,
        eventName: ev?.name ?? ev?.title ?? null,
        subject: t.subject ?? ev?.subjectId ?? undefined,
      });
    };
    // 1) Firestore에서 로드된 행사 콘텐츠 (우선)
    Object.entries(eventContent || {}).forEach(([eid, ec]) => {
      if (!Array.isArray(ec?.tracks)) return;
      // 행사 정보(이름/과목)를 events에서 찾아 부가 정보로 붙임
      let evInfo = null;
      Object.values(events || {}).forEach(list => {
        if (Array.isArray(list)) {
          const found = list.find(e => e?.id === eid);
          if (found) evInfo = found;
        }
      });
      ec.tracks.forEach(t => pushEventTrack(t, evInfo || { id: eid }));
    });
    // 2) 행사 문서 자체에 포함된 음원 (시드/폴백)
    Object.values(events || {}).forEach(list => {
      if (!Array.isArray(list)) return;
      list.forEach(ev => {
        if (!Array.isArray(ev?.tracks)) return;
        ev.tracks.forEach(t => pushEventTrack(t, ev));
      });
    });

    return result;
  };

  const allTracksFlat = getAllTracksFlat();

  const favTracks = allTracksFlat.filter(t => favorites.includes(t.id));

  const audioRef = useRef(null);
  const [audioDuration, setAudioDuration] = useState(0); // current playing track duration

  // Get display duration for a track — trust Firestore data directly
  const getTrackDuration = (track) => {
    if (track?.duration && track.duration !== "0:00") return track.duration;
    return "";
  };

  const playTrack = async (track) => {
    if (playingTrack?.id === track.id) {
      // Toggle play/pause for same track
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    } else {
      // Stop current audio
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlayingTrack(track);
      setIsPlaying(true);
      setPlayerProgress(0);
      setAudioDuration(0);
      // Track play count
      setPlayCounts(pc => ({ ...pc, [track.id]: (pc[track.id] || 0) + 1 }));
      // Record to recent history
      setRecentHistory(h => {
        const filtered = h.filter(item => item.track.id !== track.id);
        return [{ track, timestamp: new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }) }, ...filtered].slice(0, 50);
      });

      // Get audio URL with priority: cache > downloadUrl > local > getDownloadURL (last resort)
      let url = null;

      // 1. Check offline cache first
      if (downloadedFiles.includes(track.id)) {
        url = await getCachedAudioUrl(track.id);
        if (url) console.log("오프라인 캐시에서 재생:", track.title);
      }

      // 2. Use pre-stored downloadUrl from Firestore (instant, no network call)
      if (!url && track.downloadUrl) {
        url = track.downloadUrl;
      }

      // 3. Try local/admin URL
      if (!url) {
        url = track.fileUrl || adminUploadedFiles[track.id]?.url;
      }

      // 4. Last resort: fetch download URL from Firebase Storage
      if (!url && track.storagePath) {
        try {
          const storage = getStorage();
          const fileRef = ref(storage, track.storagePath);
          url = await getDownloadURL(fileRef);
        } catch (err) {
          console.error("Firebase Storage URL 오류:", err);
        }
      }

      if (url) {
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.volume = volume / 100;
        audio.playbackRate = playbackRate;
        audio.loop = repeatModeRef.current;

        // When metadata is loaded, get real duration
        audio.onloadedmetadata = () => {
          setAudioDuration(audio.duration);

          // Auto-update Firestore if duration or downloadUrl is missing
          if (track.firestoreId && audio.duration && isFinite(audio.duration)) {
            const m = Math.floor(audio.duration / 60);
            const s = Math.floor(audio.duration % 60);
            const realDuration = `${m}:${s.toString().padStart(2, "0")}`;
            const needsDurationUpdate = !track.duration || track.duration === "0:00" || track.duration === "";
            const needsUrlUpdate = !track.downloadUrl && url;

            if (needsDurationUpdate || needsUrlUpdate) {
              const updates = {};
              if (needsDurationUpdate) updates.duration = realDuration;
              if (needsUrlUpdate) updates.downloadUrl = url;
              try {
                const db = getFirestore();
                updateDoc(doc(db, "tracks", track.firestoreId), updates);
                console.log("[자동 보정]", track.title, updates);
                setTracks(prev => {
                  const u = JSON.parse(JSON.stringify(prev));
                  const findAndUpdate = (obj) => {
                    for (const key of Object.keys(obj)) {
                      if (Array.isArray(obj[key])) {
                        obj[key] = obj[key].map(t => t.id === track.id ? { ...t, ...updates } : t);
                      } else if (typeof obj[key] === "object" && obj[key] !== null) {
                        findAndUpdate(obj[key]);
                      }
                    }
                  };
                  findAndUpdate(u);
                  return u;
                });
              } catch (err) {
                console.warn("[자동 보정 실패]", err);
              }
            }
          }
        };

        // Real-time progress update from audio
        audio.ontimeupdate = () => {
          if (audio.duration && !isDragging) {
            setPlayerProgress((audio.currentTime / audio.duration) * 100);
          }
        };

        audio.onended = () => {
          if (repeatModeRef.current) { audio.currentTime = 0; audio.play().catch(() => {}); }
          else { setIsPlaying(false); setPlayerProgress(100); }
        };

        audio.play();
      }
    }
  };

  // Seek: when user drags or clicks progress bar, update audio position
  const seekAudio = (pct) => {
    setPlayerProgress(pct);
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = (pct / 100) * audioRef.current.duration;
    }
  };

  // Sync volume with audio
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
  }, [volume]);

  const handleVolumeClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    setVolume(Math.round(pct));
  };

  // Stop audio when closing player
  const stopPlayer = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingTrack(null);
    setIsPlaying(false);
    setPlayerProgress(0);
    setAudioDuration(0);
    setPlayerCollapsed(false);
  };

  // Playlist reorder
  const moveTrackInPlaylist = (subjectId, trackId, direction) => {
    setPlaylists(pl => {
      const list = [...(pl[subjectId] || [])];
      const idx = list.indexOf(trackId);
      if (idx < 0) return pl;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= list.length) return pl;
      [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
      return { ...pl, [subjectId]: list };
    });
  };

  // Parse duration string to seconds (for sample data fallback)
  const parseDuration = (dur) => {
    if (!dur) return 0;
    const parts = dur.split(":").map(Number);
    return parts.length === 2 ? parts[0] * 60 + parts[1] : 0;
  };
  const formatTime = (sec) => {
    if (!sec || isNaN(sec) || !isFinite(sec)) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    seekAudio(pct);
  };

  // ─── Download handler ───────────────────────────────────────────────────────
  const CACHE_NAME = "ullim-audio-cache-v1";

  const handleDownload = async (track) => {
    const trackId = typeof track === "string" ? track : track.id;
    if (downloadedFiles.includes(trackId)) return;
    if (downloadingFiles.includes(trackId)) return;
    if (typeof caches === "undefined") {
      alert("이 기기에서는 오프라인 저장을 사용할 수 없습니다.");
      return;
    }
    setDownloadingFiles(df => [...df, trackId]);

    try {
      // Get the audio URL: downloadUrl > fileUrl > getDownloadURL
      let url = track.downloadUrl || track.fileUrl;
      if (!url && track.storagePath) {
        const storage = getStorage();
        const fileRef = ref(storage, track.storagePath);
        url = await getDownloadURL(fileRef);
      }
      if (!url) throw new Error("재생 주소를 찾을 수 없습니다");

      // 실제 음원 바이트를 받아 blob으로 변환 후 저장.
      // (원본 응답을 그대로 cache.put 하면 CORS/opaque 응답일 때 실패하므로 blob으로 다시 감쌈)
      const cache = await caches.open(CACHE_NAME);
      const response = await fetch(url);
      if (!response.ok) throw new Error("다운로드 실패 (HTTP " + response.status + ")");
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error("받은 파일이 비어 있습니다. (저장소 CORS 설정이 필요할 수 있습니다)");
      }
      const cacheKey = `/offline-audio/${trackId}`;
      await cache.put(cacheKey, new Response(blob, { headers: { "Content-Type": blob.type || "audio/mpeg" } }));
      console.log("오프라인 저장 완료:", track.title || trackId, Math.round(blob.size / 1024) + "KB");

      setDownloadingFiles(df => df.filter(x => x !== trackId));
      setDownloadedFiles(df => df.includes(trackId) ? df : [...df, trackId]);
    } catch (err) {
      console.error("오프라인 저장 오류:", err);
      setDownloadingFiles(df => df.filter(x => x !== trackId));
      alert("오프라인 저장에 실패했습니다.\n" + (err && err.message ? err.message : err));
    }
  };

  // Get cached audio URL for offline playback
  const getCachedAudioUrl = async (trackId) => {
    try {
      const cache = await caches.open(CACHE_NAME);
      const cacheKey = `/offline-audio/${trackId}`;
      const response = await cache.match(cacheKey);
      if (response) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
    } catch (err) {
      console.error("캐시 읽기 오류:", err);
    }
    return null;
  };

  // 오프라인 저장 삭제 (캐시 정리)
  const removeDownload = async (trackId) => {
    try {
      if (typeof caches !== "undefined") {
        const cache = await caches.open(CACHE_NAME);
        await cache.delete(`/offline-audio/${trackId}`);
      }
    } catch (err) {
      console.warn("오프라인 삭제 오류:", err);
    }
    setDownloadedFiles(df => df.filter(x => x !== trackId));
  };

  const isDownloaded = (fileId) => downloadedFiles.includes(fileId);
  const isDownloading = (fileId) => downloadingFiles.includes(fileId);

  // ─── Global search ──────────────────────────────────────────────────────────
  const getGlobalSearchResults = () => {
    const q = globalSearchTerm.trim().toLowerCase();
    if (!q) return { tracks: [], videos: [], plans: [] };

    const trackResults = [];
    const videoResults = [];
    const planResults = [];

    const iterateContent = (src, callback) => {
      if (!src) return;
      Object.entries(src).forEach(([subj, subjData]) => {
        if (!subjData) return;
        const hasProgs = getProgramsBySubject(subj);
        if (hasProgs) {
          Object.entries(subjData).forEach(([prog, issues]) => {
            if (!issues || typeof issues !== "object") return;
            Object.entries(issues).forEach(([issue, steps]) => {
              if (!steps || typeof steps !== "object") return;
              Object.entries(steps).forEach(([step, items]) => {
                if (!Array.isArray(items)) return;
                items.forEach(item => callback(item, subj, prog, normalizeIssue(issue), step));
              });
            });
          });
        } else {
          Object.entries(subjData).forEach(([issue, steps]) => {
            if (!steps || typeof steps !== "object") return;
            Object.entries(steps).forEach(([step, items]) => {
              if (!Array.isArray(items)) return;
              items.forEach(item => callback(item, subj, null, normalizeIssue(issue), step));
            });
          });
        }
      });
    };

    iterateContent(tracks, (t, subj, prog, issue, step) => {
      if (t?.title?.toLowerCase().includes(q)) {
        trackResults.push({ ...t, subject: subj, program: prog, issue, step, type: "track" });
      }
    });

    iterateContent(videos, (v, subj, prog, issue, step) => {
      if (v?.title?.toLowerCase().includes(q)) {
        videoResults.push({ ...v, subject: subj, program: prog, issue, step, type: "video" });
      }
    });

    if (plans) {
      Object.entries(plans).forEach(([subj, subjData]) => {
        if (!subjData) return;
        const hasProgs = getProgramsBySubject(subj);
        const iteratePlans = (planData, prog) => {
          if (planData?.monthly) {
            Object.entries(planData.monthly).forEach(([issue, steps]) => {
              if (!steps) return;
              Object.entries(steps).forEach(([step, plan]) => {
                // 배열/단일객체 모두 처리
                const arr = Array.isArray(plan) ? plan : (plan ? [plan] : []);
                arr.forEach(pl => {
                  if (pl?.title?.toLowerCase().includes(q)) {
                    planResults.push({ ...pl, subject: subj, program: prog, issue: normalizeIssue(issue), step, type: "plan" });
                  }
                });
              });
            });
          }
          if (planData?.yearly) {
            Object.entries(planData.yearly).forEach(([step, plan]) => {
              const arr = Array.isArray(plan) ? plan : (plan ? [plan] : []);
              arr.forEach(pl => {
                if (pl?.title?.toLowerCase().includes(q)) {
                  planResults.push({ ...pl, subject: subj, program: prog, step, type: "plan", yearly: true });
                }
              });
            });
          }
        };
        if (hasProgs) {
          Object.entries(subjData).forEach(([prog, planData]) => iteratePlans(planData, prog));
        } else {
          iteratePlans(subjData, null);
        }
      });
    }

    return { tracks: trackResults, videos: videoResults, plans: planResults };
  };

  const goToSubject = (subj) => {
    if (page !== "subjectDetail") setSubjectOrigin(page);
    setSelectedSubject(subj);
    // 프로그램이 1개뿐인 과목(예: 오감→스칸디오감)은 프로그램 선택 화면을 건너뛰고 바로 진입
    const progs = getProgramsBySubject(subj);
    setSelectedProgram(progs && progs.length === 1 ? progs[0].id : null);
    setSubjectTab("tracks");
    setContentIssue(null);
    setContentStep(null);
    setPlanType("monthly");
    setSearchTerm("");
    setShowRecitals(false); setSelectedRecital(null); setRecitalEditItem(null); // 발표회 상태 초기화
    setPage("subjectDetail");
  };

  // ─── 화면 이동: 한 단계 위로 (상단 뒤로 버튼) ─────────────────────────────
  // 항상 "지금 화면의 바로 위 단계"로만 이동한다.
  const navUp = () => {
    if (playingVideo) { setPlayingVideo(null); return; }
    if (viewingDoc) { setViewingDoc(null); return; }
    if (showGlobalSearch) { setShowGlobalSearch(false); return; }
    if (page === "subjectDetail") {
      if (postEditing) { setPostEditing(null); return; }
      if (postViewing) { setPostViewing(null); return; }
      if (showRecitals) {
        if (selectedRecital) { setSelectedRecital(null); setRecitalEditItem(null); }
        else { setShowRecitals(false); setNewRecitalTitle(""); }
        return;
      }
      const skips = subjectSkipsStepsDyn(selectedSubject);
      if (subjectTab === "plans" && planType === "yearly" && contentStep) { setContentStep(null); return; }
      if (contentStep && !skips) { setContentStep(null); return; }
      if (contentIssue) { setContentIssue(null); setContentStep(null); return; }
      const progs = getProgramsBySubject(selectedSubject);
      if (selectedProgram && progs && progs.length > 1) { setSelectedProgram(null); return; }
      setPage(subjectOrigin || "home"); setSelectedSubject(null); setSelectedProgram(null);
      setContentIssue(null); setContentStep(null); setShowRecitals(false); setSelectedRecital(null);
      return;
    }
    if (page === "events") {
      if (selectedEvent) { setSelectedEvent(null); setEventTab("tracks"); setEventEditItem(null); return; }
      if (eventSubject) { setEventSubject(null); return; }
    }
    if (subPage) { setSubPage(null); return; }
    if (page !== "home") setPage("home");
  };
  const goBack = navUp;

  // 상단 뒤로 버튼에 표시할 "돌아갈 곳" 이름
  const navUpLabel = () => {
    const subjName = subjects.find(s => s.id === selectedSubject)?.name;
    const progs = getProgramsBySubject(selectedSubject);
    const progName = progs?.find(p => p.id === selectedProgram)?.name;
    const originLabel = { home: "홈", subjects: "과목", favorites: "즐겨찾기", recent: "최근", events: "행사" }[subjectOrigin] || "뒤로";
    if (page === "subjectDetail") {
      if (postEditing || postViewing) return "공유";
      if (showRecitals) return selectedRecital ? "발표회" : (subjName || "뒤로");
      const skips = subjectSkipsStepsDyn(selectedSubject);
      if (subjectTab === "plans" && planType === "yearly" && contentStep) return progName || subjName || "뒤로";
      if (contentStep && !skips) return issueLabel(contentIssue, selectedSubject);
      if (contentIssue) return progName || subjName || "뒤로";
      if (selectedProgram && progs && progs.length > 1) return subjName || "뒤로";
      return originLabel;
    }
    if (page === "events") {
      if (selectedEvent) return subjects.find(s => s.id === eventSubject)?.name || "행사";
      if (eventSubject) return "행사";
    }
    return "홈";
  };
  const canNavUp = page === "subjectDetail" || (page === "events" && (selectedEvent || eventSubject)) || !!subPage;

  // ─── 화면 이동 기록 (휴대폰 뒤로가기 · 새로고침 후 위치 복원) ─────────────
  // 화면이 바뀔 때마다 브라우저 기록(history)에 남겨서, 안드로이드 뒤로 버튼/스와이프가
  // 앱을 끄지 않고 이전 화면으로 돌아가게 한다. 탭 전환은 기록을 새로 쌓지 않는다.
  const navSnap = {
    page, subjectOrigin, selectedSubject, selectedProgram, subjectTab, contentIssue, contentStep, planType,
    showRecitals, selectedRecital: selectedRecital || null, eventSubject, selectedEventId: selectedEvent?.id ?? null, eventTab, subPage,
    ov: { admin: !!showAdmin, search: !!showGlobalSearch, video: !!playingVideo, doc: !!viewingDoc, post: !!(postViewing || postEditing) },
  };
  const navKeyOf = (n) => JSON.stringify(n.page === "subjectDetail"
    ? [n.page, n.subjectOrigin, n.selectedSubject, n.selectedProgram, n.contentIssue, n.contentStep, n.showRecitals, n.selectedRecital, n.ov]
    : n.page === "events" ? [n.page, n.eventSubject, n.selectedEventId, n.ov]
    : [n.page, n.subPage, n.ov]);
  const navSnapJson = JSON.stringify(navSnap);
  const navRef = useRef(null);            // 마지막으로 기록한 화면 { snap, key }
  const navRestoringRef = useRef(null);   // 뒤로가기로 복원 중인 화면 key
  const navIgnorePopRef = useRef(false);  // 앱이 스스로 history.back()을 부른 경우
  const navReadyRef = useRef(false);
  const navApplyRef = useRef(null);

  // 기록된 화면 상태를 실제 화면에 적용
  navApplyRef.current = (n, keepTab) => {
    if (!n) return;
    const cur = navRef.current?.snap;
    setPage(n.page || "home");
    setSubjectOrigin(n.subjectOrigin || "home");
    setSelectedSubject(n.selectedSubject ?? null);
    setSelectedProgram(n.selectedProgram ?? null);
    const sameContext = cur && cur.selectedSubject === n.selectedSubject && cur.selectedProgram === n.selectedProgram;
    if (!(keepTab && sameContext)) { setSubjectTab(n.subjectTab || "tracks"); setPlanType(n.planType || "monthly"); }
    setContentIssue(n.contentIssue ?? null);
    setContentStep(n.contentStep ?? null);
    setShowRecitals(!!n.showRecitals);
    setSelectedRecital(n.selectedRecital ?? null);
    setRecitalEditItem(null);
    setEventSubject(n.eventSubject ?? null);
    const ev = n.selectedEventId ? Object.values(events || {}).flat().find(e => e && e.id === n.selectedEventId) : null;
    setSelectedEvent(ev || null);
    setEventTab(n.eventTab || "tracks");
    setSubPage(n.subPage ?? null);
    // 열려 있는 창(영상·문서·검색·관리자·게시글)은 기록에 없으면 닫는다
    const ov = n.ov || {};
    if (!ov.video) setPlayingVideo(null);
    if (!ov.doc) setViewingDoc(null);
    if (!ov.search) setShowGlobalSearch(false);
    if (!ov.admin) setShowAdmin(false);
    if (!ov.post) { setPostViewing(null); setPostEditing(null); }
    setShowSpeedMenu(false);
  };

  const restoreScroll = (y) => {
    if (typeof y !== "number") return;
    setTimeout(() => { try { window.scrollTo(0, y); } catch (e) {} }, 60);
  };

  // 처음 열릴 때: 새로고침이면 기록에서, 앱을 다시 켠 경우엔 최근 위치(6시간 이내)에서 복원
  useEffect(() => {
    let saved = null;
    try { saved = window.history.state?.ullimNav?.snap || null; } catch (e) {}
    if (!saved) {
      try {
        const raw = JSON.parse(localStorage.getItem("ullim_lastNav") || "null");
        if (raw && raw.snap && Date.now() - (raw.t || 0) < 6 * 60 * 60 * 1000) saved = raw.snap;
      } catch (e) {}
    }
    if (saved && saved.page && saved.page !== "home") {
      const clean = { ...saved, ov: {} };
      navRestoringRef.current = { key: navKeyOf(clean), t: Date.now() };
      navApplyRef.current(clean, false);
    }
    navReadyRef.current = true;
    const onPop = (e) => {
      const entry = e.state?.ullimNav;
      if (navIgnorePopRef.current) {
        navIgnorePopRef.current = false;
        restoreScroll(entry?.scrollY);
        return;
      }
      const target = entry?.snap || { page: "home", ov: {} };
      navRestoringRef.current = { key: navKeyOf(target), t: Date.now() };
      navApplyRef.current(target, true);
      restoreScroll(entry?.scrollY ?? 0);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 화면이 바뀔 때마다 기록
  useEffect(() => {
    if (!navReadyRef.current) return;
    const snap = JSON.parse(navSnapJson);
    const key = navKeyOf(snap);
    const prev = navRef.current;
    const hs = (() => { try { return window.history.state?.ullimNav || null; } catch (e) { return null; } })();
    const depth = hs?.depth ?? 0;
    const restoring = navRestoringRef.current;
    if (restoring && Date.now() - restoring.t < 800 && restoring.key !== key) return; // 복원 적용 전 화면 — 기록하지 않음
    try {
      if (restoring) {
        // 뒤로가기/새로고침 복원 결과 — 기록은 그대로 두고 현재 항목만 갱신
        navRestoringRef.current = null;
        window.history.replaceState({ ullimNav: { ...(hs || {}), snap, depth, prevKey: hs?.prevKey ?? null } }, "");
      } else if (!prev || prev.key === key) {
        // 같은 화면 안의 변화(탭 전환 등)는 현재 기록만 갱신
        window.history.replaceState({ ullimNav: { ...(hs || {}), snap, depth, prevKey: hs?.prevKey ?? null } }, "");
      } else if (hs?.prevKey && hs.prevKey === key) {
        // 바로 이전 화면으로 돌아가는 이동(상단 뒤로·닫기 버튼) → 기록도 한 칸 되돌림
        navIgnorePopRef.current = true;
        window.history.back();
      } else if (prev.snap.page === "subjectDetail" && snap.page === "subjectDetail" && !prev.snap.selectedProgram && snap.selectedProgram
        && prev.snap.selectedSubject === snap.selectedSubject && (getProgramsBySubject(snap.selectedSubject)?.length === 1)) {
        // 프로그램이 하나뿐인 과목의 자동 선택은 새 기록으로 치지 않음
        window.history.replaceState({ ullimNav: { ...(hs || {}), snap, depth, prevKey: hs?.prevKey ?? null } }, "");
      } else {
        // 새 화면으로 이동 → 현재 스크롤 위치를 저장하고 기록 추가
        window.history.replaceState({ ullimNav: { ...(hs || {}), scrollY: window.scrollY } }, "");
        window.history.pushState({ ullimNav: { snap, depth: depth + 1, prevKey: prev.key } }, "");
        const ovOnly = JSON.stringify({ ...prev.snap, ov: null, subjectTab: null, planType: null, eventTab: null }) === JSON.stringify({ ...snap, ov: null, subjectTab: null, planType: null, eventTab: null });
        if (!ovOnly) window.scrollTo(0, 0);
      }
    } catch (e) { /* history 사용 불가 환경 — 무시 */ }
    navRef.current = { snap, key };
    try { localStorage.setItem("ullim_lastNav", JSON.stringify({ snap: { ...snap, ov: {} }, t: Date.now() })); } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSnapJson]);

  // ─── 자동 업데이트 ────────────────────────────────────────────────────────
  // 새 버전이 배포되면 선생님이 아무것도 하지 않아도 새 화면으로 바뀌게 한다.
  // 앱을 다시 볼 때(화면 켜기·앱 전환)와 10분마다 서버의 최신 버전을 확인하고,
  // 음원 재생 중이 아니면 바로 새로 고친다(보던 화면은 위치 기억으로 그대로 복원).
  // 재생 중이면 수업을 끊지 않도록 상단에 "업데이트" 안내만 띄운다.
  const [updateReady, setUpdateReady] = useState(false);
  const isPlayingRef = useRef(false);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => {
    const mainRe = /\/static\/js\/main\.[\w]+\.js/;
    const loaded = Array.from(document.scripts || []).map(sc => sc.src || "").find(src => mainRe.test(src));
    if (!loaded) return; // 개발 환경·앱 내장 버전 등에서는 동작하지 않음
    const loadedMain = loaded.match(mainRe)[0];
    let busy = false;
    const check = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        const res = await fetch("/index.html?_=" + Date.now(), { cache: "no-store" });
        if (!res.ok) return;
        const m = (await res.text()).match(mainRe);
        if (!m || m[0] === loadedMain) return;
        let tried = null;
        try { tried = sessionStorage.getItem("ullim_reloadedFor"); } catch (e) {}
        if (!isPlayingRef.current && tried !== m[0]) {
          try { sessionStorage.setItem("ullim_reloadedFor", m[0]); } catch (e) {}
          window.location.reload();
        } else {
          setUpdateReady(true);
        }
      } catch (e) { /* 오프라인 등 — 다음에 다시 확인 */ }
      finally { busy = false; }
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = setInterval(check, 10 * 60 * 1000);
    const first = setTimeout(check, 3000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(timer); clearTimeout(first);
    };
  }, []);

  // ─── Renderers ──────────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div style={S.header}>
      {(showAdmin && adminLoggedIn) ? (
        <button style={S.backBtn} onClick={() => {
          if (selectedEvent) { setSelectedEvent(null); setEventTab("tracks"); setEventEditItem(null); }
          else { setShowAdmin(false); setAdminTab("dashboard"); setAdminContentIssue(null); setAdminContentStep(null); setAdminContentProgram(null); }
        }}>
          {Icons.back} <span style={S.backText}>{selectedEvent ? "행사 관리" : "관리자 모드"}</span>
        </button>
      ) : showAdmin && !adminLoggedIn ? (
        <button style={S.backBtn} onClick={() => setShowAdmin(false)}>
          {Icons.back} <span style={S.backText}>돌아가기</span>
        </button>
      ) : canNavUp ? (
        <button style={S.backBtn} onClick={navUp} aria-label={`${navUpLabel()}(으)로 돌아가기`}>
          {Icons.back} <span style={S.backText}>{navUpLabel()}</span>
        </button>
      ) : (
        <div style={{ ...S.logoWrap, cursor: "pointer" }} onClick={() => { setPage("home"); setSelectedSubject(null); setSelectedProgram(null); setSubPage(null); setSelectedEvent(null); setEventSubject(null); }}>
          <Logo />
          <div>
            <div style={S.logoText}>울림티쳐</div>
            <div style={S.logoSub}>Wollim Teacher</div>
          </div>
        </div>
      )}
      {!showAdmin && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button aria-label="검색" style={{ ...touch44, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#333D4B" }} onClick={() => { setShowGlobalSearch(true); setGlobalSearchTerm(""); }}>
            {Icons.searchLg}
          </button>
          {isAdmin() && (
          <button style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "0 12px", background: "#E5E8EB", border: "none",
            borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
            fontFamily: font, color: "#4E5968", minWidth: 44, height: 36,
            justifyContent: "center",
          }} onClick={() => setShowAdmin(!showAdmin)}>
            {Icons.admin} <span>관리자</span>
          </button>
          )}
          <button style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "0 14px", background: "#E5E8EB", border: "none",
            borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
            fontFamily: font, color: "#4E5968", height: 36,
          }} onClick={() => { handleSignOut(); setShowAdmin(false); setAdminLoggedIn(false); }}>
            로그아웃
          </button>
        </div>
      )}
      {showAdmin && adminLoggedIn && (
        <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11 }} onClick={() => { setAdminLoggedIn(false); setShowAdmin(false); setAdminPw(""); }}>
          로그아웃
        </button>
      )}
    </div>
  );

  const renderNav = () => (
    <div style={S.nav}>
      {[
        { id: "home", icon: Icons.home, label: "홈" },
        { id: "subjects", icon: Icons.subjects, label: "과목" },
        { id: "favorites", icon: Icons.favorites, label: "즐겨찾기" },
        { id: "recent", icon: Icons.recent, label: "최근" },
        { id: "events", icon: Icons.events, label: "행사" },
      ].map(nav => (
        <button key={nav.id} style={S.navItem(page === nav.id || (page === "subjectDetail" && subjectOrigin === nav.id))} onClick={() => { setPage(nav.id); setSelectedSubject(null); setSelectedProgram(null); setSubPage(null); setSelectedEvent(null); setEventSubject(null); }}>
          <span style={S.navIcon(page === nav.id)}>{nav.icon}</span>
          {Bi(nav.label)}
        </button>
      ))}
    </div>
  );

  const toggleRepeat = () => {
    setRepeatMode(m => {
      const next = !m;
      if (audioRef.current) audioRef.current.loop = next;
      return next;
    });
  };

  const renderMiniPlayer = () => {
    if (!playingTrack) return null;
    const totalSec = audioDuration || parseDuration(playingTrack.duration);
    const currentSec = (playerProgress / 100) * totalSec;
    const togglePlay = () => {
      if (audioRef.current) {
        if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
        else { audioRef.current.play(); setIsPlaying(true); }
      }
    };
    const commitSeek = () => { if (audioRef.current && audioRef.current.duration) audioRef.current.currentTime = (playerProgress / 100) * audioRef.current.duration; };
    const ctrlBtn = { width: 36, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", flexShrink: 0, padding: 0, fontFamily: font };

    // 내려 둔 상태: 오른쪽 아래 작은 버튼 (재생/정지 + 다시 올리기)
    if (playerCollapsed) {
      return (
        <div style={{ position: "fixed", bottom: 72, right: "max(16px, calc(50% - 224px))", zIndex: 99, display: "flex", alignItems: "center", gap: 2, padding: 4, background: "#111", borderRadius: 28, boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}>
          <button aria-label={isPlaying ? "일시정지" : "재생"} onClick={togglePlay}
            style={{ width: 44, height: 44, borderRadius: 22, border: "none", background: ACCENT, color: "#fff", fontSize: 17, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" }}>
            {isPlaying ? Icons.pause : Icons.play}
          </button>
          <button aria-label="플레이어 올리기" onClick={() => setPlayerCollapsed(false)}
            style={{ height: 44, padding: "0 12px 0 8px", border: "none", background: "none", color: "#fff", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: font, maxWidth: 150 }}>
            <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{playingTrack.title}</span>
            <span style={{ fontSize: 16, display: "flex", transform: "rotate(90deg)", flexShrink: 0 }}>{Icons.back}</span>
          </button>
        </div>
      );
    }

    // 한 덩어리 컴팩트 플레이바: 모든 조작(재생/정지·볼륨·구간이동·배속·반복)을 항상 노출, 높이만 줄임
    return (
      <div style={{ ...S.miniPlayer, flexDirection: "column", gap: 4, padding: "8px 10px 6px" }}>
        {/* 아래로 내리기 손잡이 */}
        <button aria-label="플레이어 내리기" onClick={() => { setPlayerCollapsed(true); setShowSpeedMenu(false); }}
          style={{ position: "absolute", top: -22, left: "50%", transform: "translateX(-50%)", width: 64, height: 24, borderRadius: "12px 12px 0 0", border: "none", background: "#111", color: "rgba(255,255,255,0.75)", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
          <span style={{ display: "flex", transform: "rotate(-90deg)" }}>{Icons.back}</span>
        </button>
        {/* 1줄: 재생 · 제목/시간 · 배속 · 반복 · 즐겨찾기 · 닫기 */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
          <button style={{ width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: ACCENT, color: "#fff", fontSize: 18, flexShrink: 0, border: "none", cursor: "pointer", fontFamily: font, marginRight: 6 }}
            onClick={togglePlay}>
            {isPlaying ? Icons.pause : Icons.play}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{playingTrack.title}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
              {formatTime(currentSec)} / {totalSec > 0 ? formatTime(totalSec) : playingTrack.duration || ""}
            </div>
          </div>
          {/* 배속 */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <button
              style={{ ...ctrlBtn, width: 40, borderRadius: 10, fontSize: 11, fontWeight: 900, color: playbackRate !== 1.0 ? "#fff" : "rgba(255,255,255,0.35)", background: playbackRate !== 1.0 ? "rgba(255,255,255,0.15)" : "none" }}
              onClick={() => setShowSpeedMenu(!showSpeedMenu)}
            >
              {playbackRate}x
            </button>
            {showSpeedMenu && (
              <div style={{ position: "absolute", bottom: 48, left: "50%", transform: "translateX(-50%)", background: "#222", borderRadius: 12, padding: "6px 0", minWidth: 80, boxShadow: "0 4px 20px rgba(0,0,0,0.4)", zIndex: 100 }}>
                {PLAYBACK_RATES.map(rate => (
                  <button key={rate} onClick={() => selectPlaybackRate(rate)} style={{
                    display: "block", width: "100%", padding: "10px 16px", background: rate === playbackRate ? "rgba(255,255,255,0.15)" : "none",
                    border: "none", color: rate === playbackRate ? "#fff" : "rgba(255,255,255,0.6)", fontSize: 13, fontWeight: rate === playbackRate ? 800 : 500,
                    cursor: "pointer", textAlign: "center", fontFamily: font
                  }}>
                    {rate === 1.0 ? "보통" : `${rate}x`}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* 한곡 반복 */}
          <button
            style={{ ...ctrlBtn, borderRadius: 10, fontSize: 17, lineHeight: 1, position: "relative", color: repeatMode ? "#fff" : "rgba(255,255,255,0.35)", background: repeatMode ? "rgba(255,255,255,0.15)" : "none" }}
            onClick={toggleRepeat}
          >
            {Icons.repeat}
            {repeatMode && <span style={{ position: "absolute", top: 3, right: 1, fontSize: 8, fontWeight: 900, background: "#fff", color: "#111", borderRadius: 8, width: 13, height: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>1</span>}
          </button>
          {/* 즐겨찾기 */}
          <button style={{ ...S.favBtn(favorites.includes(playingTrack.id), true), flexShrink: 0 }} onClick={() => toggleFav(playingTrack.id)}>
            {favorites.includes(playingTrack.id) ? Icons.heart : Icons.heartEmpty}
          </button>
          {/* 닫기 */}
          <button style={{ ...ctrlBtn, color: "rgba(255,255,255,0.5)", fontSize: 17 }} onClick={stopPlayer}>
            {Icons.close}
          </button>
        </div>

        {/* 2줄: 구간 이동 바 + 볼륨 (한 줄에 나란히) */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
          <div
            ref={progressRef}
            style={{ flex: 1, height: 26, cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={handleProgressClick}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => { setIsDragging(false); commitSeek(); }}
            onMouseLeave={() => { if (isDragging) { setIsDragging(false); commitSeek(); } }}
            onMouseMove={(e) => { if (isDragging) handleProgressClick(e); }}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => { setIsDragging(false); commitSeek(); }}
            onTouchMove={(e) => {
              if (isDragging && progressRef.current) {
                const rect = progressRef.current.getBoundingClientRect();
                const touch = e.touches[0];
                const pct = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
                seekAudio(pct);
              }
            }}
          >
            <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2, position: "relative" }}>
              <div style={{ width: `${playerProgress}%`, height: "100%", background: "#A5B4FC", borderRadius: 2, transition: isDragging ? "none" : "width 0.3s linear" }} />
              <div style={{
                position: "absolute", top: "50%", left: `${playerProgress}%`, transform: "translate(-50%, -50%)",
                width: 16, height: 16, borderRadius: "50%", background: "#fff",
                boxShadow: "0 0 6px rgba(0,0,0,0.3)", transition: isDragging ? "none" : "left 0.3s linear"
              }} />
            </div>
          </div>
          {/* 볼륨 */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, width: "34%", flexShrink: 0 }}>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", flexShrink: 0, display: "flex" }}>
              {volume === 0 ? Icons.volumeMute : volume < 50 ? Icons.volumeLow : Icons.volumeHigh}
            </span>
            <div
              ref={volumeRef}
              style={{ flex: 1, height: 26, cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={handleVolumeClick}
              onMouseMove={(e) => { if (e.buttons === 1) handleVolumeClick(e); }}
              onTouchMove={(e) => {
                if (volumeRef.current) {
                  const rect = volumeRef.current.getBoundingClientRect();
                  const touch = e.touches[0];
                  const pct = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
                  setVolume(Math.round(pct));
                }
              }}
            >
              <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2, position: "relative" }}>
                <div style={{ width: `${volume}%`, height: "100%", background: "rgba(255,255,255,0.7)", borderRadius: 2 }} />
                <div style={{
                  position: "absolute", top: "50%", left: `${volume}%`, transform: "translate(-50%, -50%)",
                  width: 14, height: 14, borderRadius: "50%", background: "#fff",
                  boxShadow: "0 0 4px rgba(0,0,0,0.2)"
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 음원 위치 표시 (검색·즐겨찾기용): 예) "레디셋 · 1호 · STEP 3"
  const trackLocation = (t) => {
    const subjName = subjects.find(s => s.id === t.subject)?.name;
    const progName = t.program ? getProgramsBySubject(t.subject)?.find(p => p.id === t.program)?.name : null;
    const parts = [progName || subjName];
    if (t.issue != null && t.issue !== "") parts.push(issueLabel(t.issue, t.subject));
    if (t.step) {
      const st = STEPS.find(x => x.id === t.step) || (getProgramCustomStages(t.program) || []).find(x => x.id === t.step);
      if (st && !subjectSkipsStepsDyn(t.subject)) parts.push(st.label);
    }
    return parts.filter(Boolean).join(" · ");
  };

  const renderTrackItem = (track, showSubject = false) => {
    const playing = playingTrack?.id === track.id && isPlaying;
    const active = playingTrack?.id === track.id;
    const dlState = isDownloaded(track.id) ? "done" : isDownloading(track.id) ? "loading" : "none";
    return (
      <div key={track.id} style={S.trackItem(active)}>
        <button style={S.trackPlay(active)} onClick={() => playTrack(track)}>
          {playing ? Icons.pause : Icons.play}
        </button>
        <div style={S.trackInfo} onClick={() => playTrack(track)}>
          <div style={S.trackTitle}>{track.title}</div>
          <div style={S.trackDuration(active)}>
            {showSubject && track.subject && <span>{trackLocation(track)} · </span>}
            {getTrackDuration(track)}
            {dlState === "done" && <span> · 오프라인</span>}
          </div>
        </div>
        <button
          style={{ ...touch44, background: "none", border: "none", fontSize: 16, cursor: dlState === "done" ? "default" : "pointer", color: dlState === "done" ? "#16A34A" : dlState === "loading" ? "#f59e0b" : "#B0B8C1", flexShrink: 0 }}
          onClick={(e) => { e.stopPropagation(); handleDownload(track); }}
        >
          {dlState === "done" ? "✓" : dlState === "loading" ? (
            <span style={{ display: "inline-block", animation: "spin 1s linear infinite", fontSize: 14 }}>⟳</span>
          ) : "↓"}
        </button>
        <button style={{ ...S.favBtn(favorites.includes(track.id), active), flexShrink: 0 }} onClick={() => toggleFav(track.id, track.subject || selectedSubject)}>
          {favorites.includes(track.id) ? Icons.heart : Icons.heartEmpty}
        </button>
      </div>
    );
  };

  // ─── Pages ──────────────────────────────────────────────────────────────────

  const renderHome = () => {
    const subjectCardStyle = {
      display: "flex", alignItems: "center", gap: 14,
      padding: "12px 14px", background: "#fff", border: "none",
      borderRadius: 18, cursor: "pointer", fontFamily: font,
      transition: "all 0.15s", color: "#191F28",
      textAlign: "left", minHeight: 68,
    };
    return (
    <div style={S.section} ref={scrollRef}>
      {/* Welcome */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: "#191F28", marginBottom: 6, letterSpacing: -0.6, lineHeight: 1.3 }}>안녕하세요,<br />{authState?.name} 선생님</div>
        <div style={{ fontSize: 14, color: "#4E5968", fontWeight: 500 }}>오늘도 즐거운 수업 준비하세요!</div>
        <div style={{ fontSize: 12, color: "#8B95A1", fontWeight: 400, marginTop: 2 }}>Hello, Teacher {authState?.name}!</div>
      </div>

      {/* All subjects - unified 2-column grid */}
      <div style={{ marginBottom: 36 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#191F28", marginBottom: 12, letterSpacing: -0.3 }}>과목 바로가기</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {subjects.map(s => (
            <button key={s.id} style={subjectCardStyle} onClick={() => goToSubject(s.id)}>
              <SubjectTile id={s.id} icon={s.icon} size={42} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#191F28" }}>{s.name}</div>
                <div style={{ fontSize: 11, color: "#8B95A1", fontWeight: 500, marginTop: 1 }}>{s.nameEn}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Favorites */}
      {favTracks.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#191F28" }}>즐겨찾기</div>
            <button style={{ background: "none", border: "none", color: "#6B7684", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font, padding: "10px 0 10px 12px" }} onClick={() => setPage("favorites")}>전체보기</button>
          </div>
          {favTracks.slice(0, 3).map(t => renderTrackItem(t, true))}
        </div>
      )}

      {/* Recent plays */}
      {recentHistory.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#191F28" }}>최근 재생</div>
            <button style={{ background: "none", border: "none", color: "#6B7684", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font, padding: "10px 0 10px 12px" }} onClick={() => setPage("recent")}>전체보기</button>
          </div>
          {recentHistory.slice(0, 3).map((item, idx) => {
            const t = item.track;
            const playing = playingTrack?.id === t.id && isPlaying;
            const active = playingTrack?.id === t.id;
            return (
              <div key={t.id + "-home-" + idx} style={S.trackItem(active)}>
                <button style={S.trackPlay(active)} onClick={() => playTrack(t)}>
                  {playing ? Icons.pause : Icons.play}
                </button>
                <div style={S.trackInfo} onClick={() => playTrack(t)}>
                  <div style={S.trackTitle}>{t.title}</div>
                  <div style={S.trackDuration(active)}>
                    {t.subject && <span>{subjects.find(s => s.id === t.subject)?.name} · </span>}
                    {item.timestamp}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  const renderSubjects = () => (
    <div style={S.section}>
      <div style={S.sectionTitle}>과목 <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>Subjects</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {subjects.map(s => (
          <button key={s.id} style={{ ...S.subjectBtn(false), gap: 6, padding: "14px 4px 12px" }} onClick={() => goToSubject(s.id)}>
            <SubjectTile id={s.id} icon={s.icon} size={40} />
            <span style={{ ...S.subjectName, fontSize: 13 }}>{s.name}</span>
            <span style={{ fontSize: 9, color: "#8B95A1", fontWeight: 500 }}>{s.nameEn}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // 발표회 악보 카드 (PDF/이미지 미리보기 + 다운로드)
  const renderScoreCard = (s, admin) => {
    const ftype = getFileType(s);
    const openScore = async () => {
      let url = s.downloadUrl;
      if (!url && s.storagePath) {
        try { url = await getDownloadURL(ref(getStorage(), s.storagePath)); } catch (e) {}
      }
      if (url) setViewingDoc({ title: s.title, fileUrl: url, fileType: ftype });
    };
    return (
      <div key={s.id} style={{ ...S.card, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div onClick={openScore} style={{ width: 44, height: 44, background: fileTypeColor[ftype] || "#111", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>
            <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>{fileTypeLabel[ftype]}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={openScore}>
            <div style={S.cardTitle}>{s.title}</div>
            <div style={S.cardMeta}>터치하여 열기</div>
          </div>
          <a href={s.downloadUrl} download target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            style={{ ...S.btn("sm"), background: "#111", color: "#fff", fontSize: 11, padding: "6px 12px", textDecoration: "none", flexShrink: 0 }}>다운로드</a>
          {admin && (
            <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "6px 10px", flexShrink: 0 }}
              onClick={() => deleteRecitalItem("score", s)}>삭제</button>
          )}
        </div>
      </div>
    );
  };

  const renderRecitalScreen = () => {
    const admin = isAdmin();
    const delBtn = (kind, item) => admin && (
      <div style={{ textAlign: "right", marginTop: -4, marginBottom: 8 }}>
        <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "4px 10px" }}
          onClick={() => deleteRecitalItem(kind, item)}>삭제</button>
      </div>
    );

    // ── 발표회 상세 (음원/영상/악보) ──
    if (selectedRecital) {
      const rec = recitals.find(r => r.id === selectedRecital);
      if (!rec) { setSelectedRecital(null); return null; }
      const content = recitalContent[selectedRecital] || { tracks: [], videos: [], scores: [] };
      const list = content[recitalTab] || [];
      return (
        <div style={S.section}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
            <span style={{ width: 52, height: 52, borderRadius: 16, background: ACCENT_TINT, color: ACCENT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{Icons.music}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.25 }}>{rec.title}</div>
              <div style={{ fontSize: 13, color: "#6B7684", fontWeight: 500 }}>발표회 · Recital</div>
            </div>
          </div>

          {/* 음원 / 영상 / 악보 탭 */}
          <div style={S.pillWrap}>
            {[["tracks", "음원"], ["videos", "영상"], ["scores", "악보"]].map(([tab, label]) => (
              <button key={tab} style={S.pill(recitalTab === tab)} onClick={() => { setRecitalTab(tab); setRecitalEditItem(null); setRecitalBulkFiles([]); }}>
                {label} ({(content[tab] || []).length})
              </button>
            ))}
          </div>

          {/* 관리자: 추가 버튼 */}
          {admin && !recitalEditItem && (
            <div style={{ marginBottom: 10 }}>
              <button style={S.btn("primary")} onClick={() => {
                setRecitalBulkFiles([]);
                if (recitalTab === "tracks") setRecitalEditItem({ kind: "track" });
                else if (recitalTab === "videos") setRecitalEditItem({ kind: "video", title: "", youtubeUrl: "" });
                else setRecitalEditItem({ kind: "score" });
              }}>
                {Icons.add} {recitalTab === "tracks" ? "음원" : recitalTab === "videos" ? "영상" : "악보"} 추가
              </button>
            </div>
          )}

          {/* 관리자: 편집 폼 */}
          {admin && recitalEditItem && (
            <div style={{ ...S.card, border: "1.5px solid #111", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {recitalEditItem.kind === "track" ? "음원" : recitalEditItem.kind === "video" ? "영상" : "악보"} 추가
                </div>
                <button style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999" }}
                  onClick={() => { setRecitalEditItem(null); setRecitalBulkFiles([]); }} disabled={recitalUploading}>×</button>
              </div>
              {recitalEditItem.kind === "video" ? (
                <>
                  <input style={S.input} placeholder="제목" value={recitalEditItem.title || ""}
                    onChange={e => setRecitalEditItem(ei => ({ ...ei, title: e.target.value }))} />
                  <input style={S.input} placeholder="https://www.youtube.com/watch?v=..." value={recitalEditItem.youtubeUrl || ""}
                    onChange={e => setRecitalEditItem(ei => ({ ...ei, youtubeUrl: e.target.value }))} />
                </>
              ) : (() => {
                const isTrack = recitalEditItem.kind === "track";
                const addFiles = (fileList) => {
                  const arr = Array.from(fileList || []).filter(Boolean);
                  if (!arr.length) return;
                  const mapped = arr.map(f => ({
                    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    rawFile: f,
                    title: f.name.replace(/\.[^/.]+$/, "").replace(/_/g, " "),
                    duration: "",
                  }));
                  setRecitalBulkFiles(prev => [...prev, ...mapped]);
                  if (isTrack) mapped.forEach(m => {
                    extractAudioDuration(m.rawFile)
                      .then(d => setRecitalBulkFiles(prev => prev.map(x => x.id === m.id ? { ...x, duration: d } : x)))
                      .catch(() => {});
                  });
                };
                return (
                  <>
                    <div
                      onDragOver={e => { e.preventDefault(); if (!recitalUploading) setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={e => { e.preventDefault(); setDragOver(false); if (recitalUploading) return; addFiles(e.dataTransfer.files); }}
                      onClick={() => {
                        if (recitalUploading) return;
                        openFilePicker({
                          accept: isTrack ? "*/*" : ".pdf,.png,.jpg,.jpeg,.gif,.webp",
                          multiple: true,
                          onFiles: (files) => addFiles(files),
                        });
                      }}
                      style={{ border: dragOver ? "2px dashed #22c55e" : "2px dashed #ddd", borderRadius: 12, padding: dragOver ? "22px 16px" : "18px 16px", textAlign: "center", cursor: "pointer", background: dragOver ? "#f0fdf4" : "#fafafa", marginBottom: 10, transition: "all 0.15s" }}>
                      <div style={{ fontSize: 24, color: dragOver ? "#22c55e" : "#ccc" }}>↑</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: dragOver ? "#22c55e" : "#666", marginTop: 6 }}>
                        {isTrack ? "음원 파일 선택 또는 드래그 (여러 개 가능)" : "악보 파일 선택 또는 드래그 (PDF / 이미지, 여러 개 가능)"}
                      </div>
                    </div>
                    {recitalBulkFiles.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: "#888", fontWeight: 700, marginBottom: 6 }}>선택된 파일 {recitalBulkFiles.length}개 (제목 수정 가능)</div>
                        {recitalBulkFiles.map((bf, idx) => (
                          <div key={bf.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, background: "#f7f7f7", borderRadius: 8, padding: "6px 8px" }}>
                            <span style={{ fontSize: 11, color: "#aaa", flexShrink: 0, width: 16 }}>{idx + 1}</span>
                            <input style={{ ...S.input, marginBottom: 0, flex: 1, fontSize: 12, padding: "6px 8px" }} value={bf.title}
                              placeholder="제목"
                              onChange={e => { const v = e.target.value; setRecitalBulkFiles(prev => prev.map(x => x.id === bf.id ? { ...x, title: v } : x)); }} />
                            <button style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#e11", flexShrink: 0 }} disabled={recitalUploading}
                              onClick={() => setRecitalBulkFiles(prev => prev.filter(x => x.id !== bf.id))}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                <button style={S.btn("outline")} onClick={() => { setRecitalEditItem(null); setRecitalBulkFiles([]); }} disabled={recitalUploading}>취소</button>
                <button style={S.btn("primary")} disabled={recitalUploading} onClick={() => uploadRecitalItem(selectedRecital)}>
                  {recitalUploading ? "업로드 중..." : (recitalEditItem.kind === "video" ? "등록" : `${recitalBulkFiles.length || ""}${recitalBulkFiles.length ? "개 " : ""}등록`)}
                </button>
              </div>
            </div>
          )}

          {/* 콘텐츠 목록 */}
          {list.length === 0 ? (
            <div style={S.empty}>등록된 {recitalTab === "tracks" ? "음원" : recitalTab === "videos" ? "영상" : "악보"}가 없습니다</div>
          ) : recitalTab === "tracks" ? (
            list.map(t => (<div key={t.id}>{renderTrackItem(t)}{delBtn("track", t)}</div>))
          ) : recitalTab === "videos" ? (
            list.map(v => (<div key={v.id}>{renderVideoCard(v, "#111")}{delBtn("video", v)}</div>))
          ) : (
            list.map(s => renderScoreCard(s, admin))
          )}
        </div>
      );
    }

    // ── 발표회 목록 ──
    return (
      <div style={S.section}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <span style={{ width: 52, height: 52, borderRadius: 16, background: ACCENT_TINT, color: ACCENT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{Icons.music}</span>
          <div>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.2 }}>발표회 <span style={{ fontSize: 12, fontWeight: 500, color: "#8B95A1" }}>Recital</span></div>
            <div style={{ fontSize: 13, color: "#6B7684", fontWeight: 500 }}>발표회 음악을 선택하세요</div>
          </div>
        </div>

        {/* 관리자: 새 발표회 추가 */}
        {admin && (
          <div style={{ ...S.card, marginBottom: 14, display: "flex", gap: 8, alignItems: "center" }}>
            <input style={{ ...S.input, marginBottom: 0, flex: 1 }} placeholder="새 발표회 음악 제목"
              value={newRecitalTitle} onChange={e => setNewRecitalTitle(e.target.value)} />
            <button style={{ ...S.btn("primary"), flexShrink: 0, opacity: newRecitalTitle.trim() ? 1 : 0.4 }}
              disabled={!newRecitalTitle.trim()}
              onClick={async () => { const ok = await addRecital(newRecitalTitle); if (ok) setNewRecitalTitle(""); }}>추가</button>
          </div>
        )}

        {recitals.length === 0 ? (
          <div style={S.empty}>등록된 발표회가 없습니다{admin ? "" : ""}</div>
        ) : (
          recitals.map(rec => {
            const c = recitalContent[rec.id] || { tracks: [], videos: [], scores: [] };
            return (
              <div key={rec.id} style={{ ...S.card, marginBottom: 8, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                onClick={() => { setSelectedRecital(rec.id); setRecitalTab("tracks"); setRecitalEditItem(null); }}>
                <span style={{ fontSize: 22 }}>🎵</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.cardTitle}>{rec.title}</div>
                  <div style={S.cardMeta}>음원 {c.tracks.length} · 영상 {c.videos.length} · 악보 {c.scores.length}</div>
                </div>
                {admin && (
                  <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "6px 10px", flexShrink: 0 }}
                    onClick={async (e) => { e.stopPropagation(); if (window.confirm(`"${rec.title}" 발표회를 삭제하시겠습니까?\n하위 음원·영상·악보도 모두 삭제됩니다.`)) await deleteRecital(rec.id); }}>삭제</button>
                )}
                <span style={{ fontSize: 16, color: "#999", flexShrink: 0 }}>→</span>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderSubjectDetail = () => {
    const subj = subjects.find(s => s.id === selectedSubject);
    if (!subj) return null;
    const hasPrograms = getProgramsBySubject(selectedSubject);

    // 음악 과목 발표회 화면 (프로그램과 별개)
    if (selectedSubject === "music" && showRecitals) {
      return renderRecitalScreen();
    }

    // If subject has programs and none selected
    if (hasPrograms && !selectedProgram) {
      // If only one program, auto-select it
      if (hasPrograms.length === 1) {
        setSelectedProgram(hasPrograms[0].id);
        return null;
      }
      return (
        <div style={S.section}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <SubjectTile id={subj.id} icon={subj.icon} size={52} />
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.2 }}>{subj.name}</div>
              <div style={{ fontSize: 13, color: "#6B7684", fontWeight: 500 }}>{selectedSubject === "senses" ? "연도를 선택하세요" : "프로그램을 선택하세요"}</div>
            </div>
          </div>
          {/* 음악 과목: 발표회 진입 배너 (프로그램과 별개로 음악 곡명으로 관리) */}
          {selectedSubject === "music" && (
            <button
              onClick={() => { setShowRecitals(true); setSelectedRecital(null); setRecitalEditItem(null); setNewRecitalTitle(""); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                padding: "18px 20px", marginBottom: 14, background: ACCENT, border: "none",
                borderRadius: 18, cursor: "pointer", fontFamily: font, textAlign: "left",
              }}>
              <span style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(255,255,255,0.18)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{Icons.music}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>발표회 <span style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.6)" }}>Recital</span></div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>발표회 음악별 음원 · 영상 · 악보 모음</div>
              </div>
              <span style={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }}>→</span>
            </button>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {hasPrograms.map(prog => (
              <button key={prog.id} style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                padding: "24px 16px 20px", background: "#fff", border: "none",
                borderRadius: 18, cursor: "pointer", fontFamily: font, transition: "all 0.2s", color: "#191F28",
              }} onClick={() => { setSelectedProgram(prog.id); setSubjectTab("tracks"); setContentIssue(null); setContentStep(null); }}>
                {prog.logo
                  ? <img src={prog.logo} alt="" style={{ width: 64, height: 56, objectFit: "contain", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.12))" }} />
                  : <span style={{ fontSize: 36 }}>{prog.icon}</span>}
                <span style={{ fontSize: 15, fontWeight: 800 }}>{prog.name}</span>
                <span style={{ fontSize: 12, color: "#6B7684", textAlign: "center", lineHeight: 1.4 }}>{prog.desc}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Get data scoped to program (if applicable)
    const getSubjectData = (dataSource) => {
      const subjData = dataSource[selectedSubject];
      if (!subjData) return null;
      if (hasPrograms && selectedProgram) return subjData[selectedProgram] || null;
      return subjData;
    };

    const tracksData = getSubjectData(tracks);
    const videosData = getSubjectData(videos);
    const rawPlansData = getSubjectData(plans);
    // Plans might have monthly/yearly structure directly, or be nested under program
    const plansData = rawPlansData;
    const picturesData = getSubjectData(pictures); // 그림자료: tracks와 동일 구조 (issue → step → [items])

    const programInfo = hasPrograms ? hasPrograms.find(p => p.id === selectedProgram) : null;

    // Get the current data source based on active tab
    const getCurrentData = () => {
      if (subjectTab === "tracks") return tracksData;
      if (subjectTab === "videos") return videosData;
      if (subjectTab === "pictures") return picturesData;
      return null; // plans handled separately
    };

    const currentData = getCurrentData();

    // Check which issues have data for current tab
    const issueHasData = (issueId) => {
      if (subjectTab === "plans") return plansData?.monthly?.[issueId];
      return currentData?.[issueId];
    };

    // Check which steps have data for current issue
    const stepHasData = (stepId) => {
      if (subjectTab === "plans") return plansData?.monthly?.[contentIssue]?.[stepId];
      return currentData?.[contentIssue]?.[stepId];
    };

    // Render issue grid (shared by tracks, videos, plans-monthly)
    const renderIssueGrid = () => {
      const isMonthLabel = MONTH_LABELED_SUBJECTS.includes(selectedSubject);
      const allIssues = getIssuesForContext(selectedProgram, subjectTab);
      const normalIssues = allIssues.filter(i => typeof i.id === "number");
      const specialIssues = allIssues.filter(i => typeof i.id !== "number"); // 발표회/그외 등 특수 폴더
      const sectionTitle = { fontSize: 15, fontWeight: 800, marginBottom: 10, color: "#191F28" };
      return (
        <>
          <div style={sectionTitle}>
            {isMonthLabel
              ? <>월 선택 <span style={{ fontSize: 10, color: "#8B95A1", fontWeight: 500 }}>Select Month</span></>
              : <>호수 선택 <span style={{ fontSize: 10, color: "#8B95A1", fontWeight: 500 }}>Select Issue</span></>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {normalIssues.map(issue => {
              const has = issueHasData(issue.id);
              const month = isMonthLabel ? issueToMonth(issue.id) : null;
              return (
                <button key={issue.id} style={{ height: 64, border: "none", borderRadius: 16, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, fontFamily: font, color: "#191F28", opacity: has ? 1 : 0.35, cursor: has ? "pointer" : "default" }} onClick={() => { if (has) { setContentIssue(issue.id); setContentStep(null); } }} disabled={!has}>
                  <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>{isMonthLabel ? month : issue.id}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7684" }}>{isMonthLabel ? "월" : "호"}</span>
                </button>
              );
            })}
          </div>
          {specialIssues.length > 0 && (
            <>
              <div style={{ ...sectionTitle, marginTop: 22 }}>특별 폴더</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {specialIssues.map(issue => {
                  const has = issueHasData(issue.id);
                  const isRecital = String(issue.label || "").includes("발표회");
                  return (
                    <button key={issue.id} style={{ height: 56, border: "none", borderRadius: 16, background: isRecital ? ACCENT_TINT : "#fff", color: isRecital ? ACCENT : "#191F28", display: "flex", alignItems: "center", gap: 10, padding: "0 16px", fontFamily: font, fontSize: 15, fontWeight: 800, opacity: has ? 1 : 0.35, cursor: has ? "pointer" : "default", textAlign: "left" }} onClick={() => { if (has) { setContentIssue(issue.id); setContentStep(null); } }} disabled={!has}>
                      <span style={{ fontSize: 20, display: "flex", color: isRecital ? ACCENT : "#6B7684" }}>{isRecital ? Icons.music : svgIcon(<path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />)}</span>
                      {issue.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </>
      );
    };

    const isPlayMelody = selectedProgram === "play-melody";
    const skipsSteps = subjectSkipsStepsDyn(selectedSubject);
    const filteredSteps = getStepsForSubjectDyn(selectedSubject);

    // Render step grid (shared)
    const renderStepGrid = () => {
      // Subjects that skip steps entirely (senses, etc.)
      if (skipsSteps) return null;

      // Play Melody: show Touch/Play stages instead of STEP 2~5
      if (isPlayMelody) {
        const customStages = getProgramCustomStages(selectedProgram) || [];
        return (
          <>
            <div style={S.crumb}>{issueLabel(contentIssue, selectedSubject)}</div>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>단계 선택</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {customStages.map(stage => {
                const has = subjectTab === "plans"
                  ? !!plansData?.monthly?.[contentIssue]?.[stage.id]
                  : !!(currentData?.[contentIssue]?.[stage.id]);
                return (
                  <button key={stage.id} style={{ ...S.subjectBtn(false), opacity: has ? 1 : 0.35, cursor: has ? "pointer" : "default", padding: "18px 12px", borderRadius: 18 }} onClick={() => has && setContentStep(stage.id)} disabled={!has}>
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{stage.label}</span>
                    <span style={{ fontSize: 12, color: "#6B7684" }}>{stage.desc}</span>
                  </button>
                );
              })}
            </div>
          </>
        );
      }

      // Default: filtered STEPS based on subject restrictions
      return (
        <>
          <div style={S.crumb}>{issueLabel(contentIssue, selectedSubject)}</div>
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>연령 선택</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(filteredSteps.length, 4)}, 1fr)`, gap: 8 }}>
            {filteredSteps.map(step => {
              const has = stepHasData(step.id);
              return (
                <button key={step.id} style={{ ...S.subjectBtn(false), opacity: has ? 1 : 0.35, cursor: has ? "pointer" : "default", padding: "16px 4px 14px", gap: 4 }} onClick={() => has && setContentStep(step.id)} disabled={!has}>
                  <span style={{ fontSize: 14, fontWeight: 800 }}>{step.label}</span>
                  <span style={{ fontSize: 11, color: "#6B7684", fontWeight: 600 }}>{step.age}</span>
                </button>
              );
            })}
          </div>
        </>
      );
    };

    // Render content list after issue + step selected
    const renderContentList = () => {
      const stepLabel = STEPS.find(s => s.id === contentStep);
      const stageLabel = isPlayMelody ? (getProgramCustomStages(selectedProgram) || []).find(s => s.id === contentStep) : null;
      const issueLabelStr = issueLabel(contentIssue, selectedSubject);
      const backLabel = skipsSteps ? issueLabelStr : stageLabel ? `${issueLabelStr} · ${stageLabel.label}` : `${issueLabelStr} · ${stepLabel?.label || ""}`;

      // 검색 매칭: 띄어쓰기/대소문자 무시, 부분 매칭
      const normalize = (s) => (s || "").toLowerCase().replace(/\s+/g, "");
      const matchSearch = (title, query) => {
        if (!query?.trim()) return true;
        const matched = normalize(title).includes(normalize(query));
        return matched;
      };

      // 제목 앞의 (N주) 패턴 추출 → 자동 정렬용
      // "(1주)노래" → 1, "(2주)노래" → 2, "02.정리송" → 999 (주차 없음은 맨 뒤)
      // 다양한 표기 허용: (1주), [1주], 1주차, 1주
      const extractWeek = (title) => {
        if (!title) return 999;
        const match = String(title).match(/[([\s]?(\d+)\s*주(?:차)?\s*[)\]]?/);
        return match ? parseInt(match[1], 10) : 999;
      };

      // 트랙/영상 자동 정렬: 주차 → 기존 order → 제목
      const sortByWeek = (items) => {
        return [...items].sort((a, b) => {
          const wa = extractWeek(a?.title);
          const wb = extractWeek(b?.title);
          if (wa !== wb) return wa - wb;
          // 같은 주차: 기존 order 필드로
          const oa = a?.order ?? 999;
          const ob = b?.order ?? 999;
          if (oa !== ob) return oa - ob;
          // 그래도 같으면 제목 가나다순
          return (a?.title || "").localeCompare(b?.title || "", "ko");
        });
      };

      // ── 곡 목록 주차별 그룹핑 (곡명 앞 "호-주차" 인식) ──
      // "5-1 비가내려요" → 1주차, "5-3한 여름밤..." → 3주차, "헬로우송/정리송" → 공통
      const extractWeekG = (title) => {
        if (!title) return null;
        const t = String(title).trim();
        const dash = t.match(/^\d+\s*-\s*(\d+)/);                  // 호-주차 (예: 5-1 → 1)
        if (dash) return parseInt(dash[1], 10);
        const wk = t.match(/[([\s]?(\d+)\s*주(?:차)?\s*[)\]]?/);   // (1주)/1주차 등 예비
        if (wk) return parseInt(wk[1], 10);
        return null; // 주차 표시 없음 → 공통곡
      };

      const groupTracksByWeek = (items) => {
        const common = [];
        const weekMap = new Map();
        for (const it of items) {
          const w = extractWeekG(it?.title);
          if (w == null) common.push(it);
          else { if (!weekMap.has(w)) weekMap.set(w, []); weekMap.get(w).push(it); }
        }
        const sortInner = (arr) => sortByUploadOrder(arr);
        const groups = [];
        // 공통곡(헬로우송·정리송 등)은 항상 맨 위 고정
        if (common.length) groups.push({ key: "common", label: "공통 (매주)", items: sortInner(common) });
        // 그다음 1주차 → 2주차 → ... 순서
        [...weekMap.keys()].sort((a, b) => a - b).forEach(w =>
          groups.push({ key: "week-" + w, label: w + "주차", items: sortInner(weekMap.get(w)) })
        );
        return groups;
      };
      // 피앙세: 주차 구분 없이 호수·연령만으로 구분 → 전체를 하나의 목록으로 표시
      const groupTracksFlat = (items) => {
        const sorted = sortByUploadOrder(items);
        return sorted.length ? [{ key: "all", label: null, items: sorted }] : [];
      };
      const groupTracksByTheme = (items) => {
        const common = [];
        const themeMap = new Map();
        for (const it of items) {
          const t = String(it?.title || "").trim();
          const idx = t.indexOf(")");
          const th = idx > 0 ? t.slice(0, idx).trim() : null;

          if (th == null) common.push(it);
          else {
            if (!themeMap.has(th)) themeMap.set(th, []);
            themeMap.get(th).push(it);
          }
        }

        const sortInner = (arr) => sortByUploadOrder(arr);

        const groups = [];
        if (common.length) groups.push({ key: "common", label: "공통", items: sortInner(common) });
        [...themeMap.keys()].sort((a, b) => a.localeCompare(b, "ko")).forEach((th) => {
          groups.push({ key: "theme-" + th, label: th, items: sortInner(themeMap.get(th)) });
        });

        return groups;
      };
      // 검색바 UI 헬퍼
      const renderSearchBar = (placeholder) => (
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input
            style={{ ...S.input, paddingRight: searchTerm ? 36 : 12 }}
            placeholder={placeholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                width: 24, height: 24, borderRadius: "50%", border: "none",
                background: "#e5e5e5", color: "#666", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, lineHeight: 1, padding: 0,
              }}
              aria-label="검색어 지우기"
            >
              ×
            </button>
          )}
        </div>
      );

      // 검색 결과 안내 (결과 0개일 때)
      const renderSearchEmpty = (hasItems, type) => (
        <div style={S.empty}>
          {searchTerm
            ? <>"<strong>{searchTerm}</strong>" 검색 결과가 없습니다</>
            : `등록된 ${type}이 없습니다`}
        </div>
      );

      // Helper: get tracks/videos/plans for current context
      const getItemsForContext = (data) => {
        if (skipsSteps) {
          // Piano: merge all steps for this issue
          const issueData = data?.[contentIssue];
          if (!issueData) return [];
          return Object.entries(issueData).reduce((acc, [k, v]) => Array.isArray(v) ? [...acc, ...v] : acc, []);
        }
        // Play Melody & all others: contentStep is "touch", "play", "step2", etc. — direct key access
        const items = data?.[contentIssue]?.[contentStep];
        console.log(`[렌더링] 데이터 접근: [${contentIssue}][${contentStep}] →`, items?.length ?? 0, "개");
        return Array.isArray(items) ? items : [];
      };

      const getPlanForContext = () => {
        if (skipsSteps) {
          // 피아노 등: 해당 호의 모든 step 계획안을 합쳐서 반환
          const issueData = plansData?.monthly?.[contentIssue];
          if (!issueData) return [];
          return Object.values(issueData).reduce((acc, v) => Array.isArray(v) ? [...acc, ...v] : (v ? [...acc, v] : acc), []);
        }
        const arr = plansData?.monthly?.[contentIssue]?.[contentStep];
        if (Array.isArray(arr)) return arr;
        return arr ? [arr] : []; // 구버전 단일 객체 호환
      };

      if (subjectTab === "tracks") {
        const allTracks = sortByWeek(getItemsForContext(currentData));
        const filtered = allTracks.filter(t => matchSearch(t?.title, searchTerm));
        return (
          <>
            <div style={S.crumb}>{backLabel}</div>
            {renderSearchBar("음원 검색... (Search audio)")}
            {searchTerm && filtered.length > 0 && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>검색 결과 {filtered.length}곡</div>
            )}
            {filtered.length === 0 ? (
              renderSearchEmpty(allTracks.length > 0, "음원")
            ) : (
              (isScandiProgram(selectedProgram) ? groupTracksByTheme(filtered)
                : selectedProgram === "fianse" ? groupTracksFlat(filtered)
                : groupTracksByWeek(filtered)).map((group, gi) => (
                <div key={group.key} style={{ marginBottom: 4 }}>
                  {group.label && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: gi > 0 ? 18 : 4, marginBottom: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: group.key === "common" ? ACCENT : "#191F28" }}>{group.label}</span>
                      <span style={{ fontSize: 12, color: "#8B95A1" }}>{group.items.length}곡</span>
                    </div>
                  )}
                  {group.items.map(t => renderTrackItem(t))}
                </div>
              ))
            )}
          </>
        );
      }

      if (subjectTab === "videos") {
        const allVideos = sortByWeek(getItemsForContext(currentData));
        const filtered = allVideos.filter(v => matchSearch(v?.title, searchTerm));
        return (
          <>
            <div style={S.crumb}>{backLabel}</div>
            {renderSearchBar("영상 검색... (Search videos)")}
            {searchTerm && filtered.length > 0 && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>검색 결과 {filtered.length}개</div>
            )}
            {filtered.length === 0 ? (
              renderSearchEmpty(allVideos.length > 0, "영상")
            ) : (
              filtered.map(v => renderVideoCard(v, "#111"))
            )}
          </>
        );
      }

      if (subjectTab === "pictures") {
        const isFianseFlat = selectedProgram === "fianse";
        // 피앙세: 모든 호수의 그림자료를 통합. 그 외: 현재 호수/스텝의 항목만.
        const gatherAllPics = (data) => {
          const out = [];
          if (!data) return out;
          Object.values(data).forEach(issueData => {
            if (!issueData || typeof issueData !== "object") return;
            Object.values(issueData).forEach(v => { if (Array.isArray(v)) out.push(...v); });
          });
          return out;
        };
        const allPics = (isFianseFlat ? gatherAllPics(currentData) : getItemsForContext(currentData))
          .slice()
          .sort((a, b) => {
            const oa = a?.order ?? 999, ob = b?.order ?? 999;
            if (oa !== ob) return oa - ob;
            return (a?.title || "").localeCompare(b?.title || "", "ko");
          });
        const filtered = allPics.filter(p => matchSearch(p?.title, searchTerm));
        const picLabel = selectedSubject === "senses" ? "물품리스트" : "그림자료";
        return (
          <>
            {!isFianseFlat && (
              <div style={S.crumb}>{backLabel}</div>
            )}
            {renderSearchBar(`${picLabel} 검색... (Search)`)}
            {searchTerm && filtered.length > 0 && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>검색 결과 {filtered.length}개</div>
            )}
            {filtered.length === 0 ? (
              renderSearchEmpty(allPics.length > 0, picLabel)
            ) : (
              filtered.map(p => renderPlanCard(p, backLabel))
            )}
          </>
        );
      }

      if (subjectTab === "plans") {
        const plansList = getPlanForContext();
        return (
          <>
            <div style={S.crumb}>{backLabel}</div>
            {plansList.length > 0 ? (
              plansList.map(plan => renderPlanCard(plan, backLabel))
            ) : (
              <div style={S.empty}>등록된 계획안이 없습니다</div>
            )}
          </>
        );
      }
    };

    // Render plans tab with monthly/yearly toggle
    const renderPlansTab = () => {
      if (!plansData) return <div style={S.empty}>등록된 계획안이 없습니다</div>;

      return (
        <>
          <div style={S.pillWrap}>
            <button style={S.pill(planType === "monthly")} onClick={() => { setPlanType("monthly"); setContentIssue(null); setContentStep(null); }}>{Bi("월간계획안")}</button>
            <button style={S.pill(planType === "yearly")} onClick={() => { setPlanType("yearly"); setContentIssue(null); setContentStep(null); }}>{Bi("연간계획안")}</button>
          </div>

          {planType === "yearly" && (
            <>
              {skipsSteps ? (
                // skipsSteps 과목 (피아노, 오감): STEP 없이 바로 계획안 표시
                (() => {
                  const yearlyData = plansData.yearly;
                  if (!yearlyData) return <div style={S.empty}>등록된 연간계획안이 없습니다</div>;
                  // 배열이면 그대로, 평탄 객체({id,title})면 [obj], 중첩({step2:[...]})이면 모든 step 합침
                  let list = [];
                  if (Array.isArray(yearlyData)) list = yearlyData;
                  else if (yearlyData.id || yearlyData.title || yearlyData.fileUrl) list = [yearlyData];
                  else list = Object.values(yearlyData).reduce((acc, v) => Array.isArray(v) ? [...acc, ...v] : (v ? [...acc, v] : acc), []);
                  if (list.length === 0) return <div style={S.empty}>등록된 연간계획안이 없습니다</div>;
                  return list.map(p => renderPlanCard(p, "연간계획안"));
                })()
              ) : (
                <>
                  <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>연령 선택</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
                    {STEPS.map(step => {
                      const raw = plansData.yearly?.[step.id];
                      const has = Array.isArray(raw) ? raw.length > 0 : !!raw;
                      return (
                        <button key={step.id} style={{ ...S.subjectBtn(false), opacity: has ? 1 : 0.35, cursor: has ? "pointer" : "default" }} onClick={() => has && setContentStep(step.id)} disabled={!has}>
                          <span style={{ fontSize: 12, fontWeight: 800 }}>{step.label}</span>
                          <span style={{ fontSize: 9, color: "#999" }}>{step.age} · {step.ageEn}</span>
                        </button>
                      );
                    })}
                  </div>
                  {contentStep && (() => {
                    const raw = plansData.yearly?.[contentStep];
                    const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                    const meta = `${STEPS.find(s => s.id === contentStep)?.label} · ${STEPS.find(s => s.id === contentStep)?.age}`;
                    return list.map(p => renderPlanCard(p, meta));
                  })()}
                </>
              )}
            </>
          )}

          {planType === "monthly" && !contentIssue && renderIssueGrid()}
          {planType === "monthly" && contentIssue && skipsSteps && renderContentList()}
          {planType === "monthly" && contentIssue && !skipsSteps && !contentStep && renderStepGrid()}
          {planType === "monthly" && contentIssue && !skipsSteps && contentStep && renderContentList()}
        </>
      );
    };

    // ─── 공유 게시판 (강사들이 직접 수업 노하우/팁 공유) ───────────────────────
    const renderShareTab = () => {
      const myId = authState?.firestoreId;
      const canManage = (post) => isAdmin() || (post.authorId && post.authorId === myId);

      // 공유 게시판 전용 검색 (renderContentList의 헬퍼는 스코프가 달라 자체 정의)
      const postMatch = (text, query) => {
        if (!query || !query.trim()) return true;
        return String(text || "").toLowerCase().includes(query.trim().toLowerCase());
      };

      // 현재 과목/프로그램에 해당하는 글만
      const list = posts.filter(p => p.subject === selectedSubject && p.program === selectedProgram);

      const fmtDate = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (isNaN(d)) return "";
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
      };

      // ── 글쓰기 / 수정 화면 ──
      if (postEditing) {
        const isEdit = !!postEditing.id;
        return (
          <>
            <button style={{ ...S.backBtn, marginBottom: 12 }} onClick={() => setPostEditing(null)}>
              {Icons.back} <span style={S.backText}>공유</span>
            </button>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 12 }}>{isEdit ? "글 수정" : "새 글 작성"}</div>
            <input
              style={{ ...S.input, marginBottom: 10 }}
              placeholder="제목을 입력하세요"
              value={postEditing.title}
              onChange={e => setPostEditing(p => ({ ...p, title: e.target.value }))}
            />
            <textarea
              style={{ ...S.input, minHeight: 200, resize: "vertical", lineHeight: 1.6, fontFamily: "inherit" }}
              placeholder={"수업 방법, 노하우, 팁을 자유롭게 나눠주세요.\n\n예) 5호 '비가내려요' 수업 때 우산 소품을 쓰면 아이들 집중도가 훨씬 좋아요!"}
              value={postEditing.body}
              onChange={e => setPostEditing(p => ({ ...p, body: e.target.value }))}
            />

            {/* 유튜브 링크 (선택) */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginTop: 6, marginBottom: 6 }}>
              유튜브 영상 링크 <span style={{ fontWeight: 500, color: "#aaa" }}>(선택)</span>
            </div>
            <input
              style={{ ...S.input, marginBottom: 4 }}
              placeholder="https://www.youtube.com/watch?v=..."
              value={postEditing.youtubeUrl || ""}
              onChange={e => setPostEditing(p => ({ ...p, youtubeUrl: e.target.value }))}
            />
            {postEditing.youtubeUrl?.trim() && (
              getYoutubeId(postEditing.youtubeUrl) ? (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: "#16a34a", marginBottom: 6 }}>✓ 영상을 인식했어요. 아래처럼 표시됩니다.</div>
                  <img
                    src={`https://img.youtube.com/vi/${getYoutubeId(postEditing.youtubeUrl)}/mqdefault.jpg`}
                    alt="미리보기"
                    style={{ width: "100%", maxWidth: 240, borderRadius: 10, display: "block" }}
                  />
                </div>
              ) : (
                <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 8 }}>유튜브 주소를 확인해주세요.</div>
              )
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                style={{ ...S.btn("primary"), flex: 1, opacity: (postEditing.title.trim() && postEditing.body.trim()) ? 1 : 0.4 }}
                disabled={!postEditing.title.trim() || !postEditing.body.trim()}
                onClick={async () => {
                  const yt = (postEditing.youtubeUrl || "").trim();
                  // 링크를 넣었는데 형식이 잘못된 경우 저장 막기
                  if (yt && !getYoutubeId(yt)) {
                    alert("유튜브 주소가 올바르지 않습니다.\n\n확인 후 다시 시도하거나, 링크를 비워두고 등록해주세요.");
                    return;
                  }
                  const ok = await savePost({
                    id: postEditing.id,
                    title: postEditing.title.trim(),
                    body: postEditing.body.trim(),
                    youtubeUrl: yt,
                    subject: selectedSubject,
                    program: selectedProgram,
                  });
                  if (ok) { setPostEditing(null); setPostViewing(null); }
                }}
              >{isEdit ? "수정 완료" : "등록하기"}</button>
              <button style={{ ...S.btn("sm"), background: "#f5f5f5", color: "#666", padding: "12px 20px" }} onClick={() => setPostEditing(null)}>취소</button>
            </div>
          </>
        );
      }

      // ── 상세보기 화면 ──
      if (postViewing) {
        const post = posts.find(p => p.firestoreId === postViewing) || null;
        if (!post) { setPostViewing(null); return null; }
        return (
          <>
            <button style={{ ...S.backBtn, marginBottom: 12 }} onClick={() => setPostViewing(null)}>
              {Icons.back} <span style={S.backText}>공유</span>
            </button>
            <div style={{ ...S.card, padding: 18 }}>
              <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8, lineHeight: 1.4 }}>{post.title}</div>
              <div style={{ fontSize: 11, color: "#999", marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid #eee" }}>
                {post.authorName || "익명"} · {fmtDate(post.createdAt)}
                {post.updatedAt && post.updatedAt !== post.createdAt && " (수정됨)"}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: "pre-wrap", color: "#333" }}>{post.body}</div>

              {/* 첨부된 유튜브 영상 */}
              {post.youtubeUrl && getYoutubeId(post.youtubeUrl) && (
                <div
                  style={{ marginTop: 18, cursor: "pointer", borderRadius: 12, overflow: "hidden", position: "relative", background: "#000" }}
                  onClick={() => setPlayingVideo({ id: post.firestoreId, title: post.title, youtubeUrl: post.youtubeUrl })}
                >
                  <div style={{ width: "100%", height: 0, paddingBottom: "56.25%", position: "relative" }}>
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(post.youtubeUrl)}/mqdefault.jpg`}
                      alt={post.title}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.2)" }}>
                      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20 }}>▶</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {canManage(post) && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={{ ...S.btn("sm"), background: "#f5f5f5", color: "#666", padding: "8px 16px" }}
                  onClick={() => setPostEditing({ id: post.firestoreId, title: post.title, body: post.body, youtubeUrl: post.youtubeUrl || "" })}>수정</button>
                <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", padding: "8px 16px" }}
                  onClick={async () => {
                    if (!window.confirm(`"${post.title}"\n\n이 글을 삭제하시겠습니까?`)) return;
                    const ok = await deletePost(post.firestoreId);
                    if (ok) setPostViewing(null);
                  }}>삭제</button>
              </div>
            )}
          </>
        );
      }

      // ── 목록 화면 ──
      const filtered = list.filter(p => postMatch(p.title, searchTerm) || postMatch(p.body, searchTerm));
      return (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>공유 <span style={{ fontSize: 12, fontWeight: 600, color: "#999" }}>Share</span></div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>선생님들의 수업 노하우와 팁을 나눠보세요</div>
            </div>
            <button style={{ ...S.btn("primary"), fontSize: 12, padding: "8px 14px", flexShrink: 0 }}
              onClick={() => setPostEditing({ title: "", body: "" })}>+ 글쓰기</button>
          </div>

          <div style={{ position: "relative", marginTop: 14, marginBottom: 12 }}>
            <input
              style={{ ...S.input, marginBottom: 0 }}
              placeholder="글 검색... (Search posts)"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {filtered.length === 0 ? (
            <div style={S.empty}>
              {list.length === 0 ? "아직 공유된 글이 없습니다. 첫 번째 글을 남겨보세요!" : "검색 결과가 없습니다"}
            </div>
          ) : (
            filtered.map(post => (
              <div key={post.firestoreId} style={{ ...S.card, cursor: "pointer", marginBottom: 8 }} onClick={() => setPostViewing(post.firestoreId)}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                  {post.youtubeUrl && getYoutubeId(post.youtubeUrl) && (
                    <span style={{ fontSize: 10, background: "#fee2e2", color: "#dc2626", padding: "2px 6px", borderRadius: 4, fontWeight: 700, flexShrink: 0 }}>▶ 영상</span>
                  )}
                  <span>{post.title}</span>
                </div>
                <div style={{ fontSize: 12, color: "#777", lineHeight: 1.5, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {post.body}
                </div>
                <div style={{ fontSize: 10, color: "#aaa" }}>
                  {post.authorName || "익명"} · {fmtDate(post.createdAt)}
                  {post.authorId === myId && <span style={{ marginLeft: 6, background: "#e0f2fe", color: "#0369a1", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>내 글</span>}
                </div>
              </div>
            ))
          )}
        </>
      );
    };

    // Determine what to show for tracks/videos/pictures tabs
    const renderContentTab = () => {
      if (!currentData) return (
        <>
          {contentIssue != null && <div style={S.crumb}>{issueLabel(contentIssue, selectedSubject)}{contentStep && !skipsSteps ? ` · ${(STEPS.find(x => x.id === contentStep) || (getProgramCustomStages(selectedProgram) || []).find(x => x.id === contentStep))?.label || ""}` : ""}</div>}
          <div style={S.empty}>{subjectTab === "tracks" ? "등록된 음원이 없습니다" : subjectTab === "pictures" ? `등록된 ${selectedSubject === "senses" ? "물품리스트가" : "그림자료가"} 없습니다` : "등록된 영상이 없습니다"}</div>
        </>
      );
      // 피앙세 그림자료: 호수 구분 없이 전체를 하나의 목록으로 표시 (호수 선택 단계 건너뜀)
      if (subjectTab === "pictures" && selectedProgram === "fianse") return renderContentList();
      if (!contentIssue) return renderIssueGrid();
      // Piano: skip step selection, go directly to content
      if (skipsSteps) return renderContentList();
      if (!contentStep) return renderStepGrid();
      return renderContentList();
    };

    return (
      <div style={S.section}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <SubjectTile id={subj.id} icon={subj.icon} size={52} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.2 }}>{programInfo ? programInfo.name : subj.name}</div>
            {programInfo && <div style={{ fontSize: 13, color: "#6B7684", fontWeight: 500 }}>{subj.name} · {subj.nameEn}</div>}
            {!programInfo && <div style={{ fontSize: 13, color: "#6B7684", fontWeight: 500 }}>{subj.nameEn}{subj.parent ? ` · ${subj.parent}` : ""}</div>}
          </div>
        </div>

        {/* Tabs */}
        <div style={S.pillWrap}>
          {["tracks", "videos", "plans", "pictures", ...(selectedProgram === "play-melody" ? ["share"] : [])].map(tab => (
            <button key={tab} style={S.pill(subjectTab === tab)} onClick={() => {
              // 탭을 바꿔도 보던 호수·STEP은 유지 (그 탭에 없는 특수 폴더면 호수 선택으로)
              const validIssue = contentIssue != null && getIssuesForContext(selectedProgram, tab).some(i => String(i.id) === String(contentIssue));
              const fromYearly = subjectTab === "plans" && planType === "yearly";
              if (!validIssue || fromYearly) { setContentIssue(null); setContentStep(null); }
              setSubjectTab(tab); setPlanType("monthly"); setSearchTerm(""); setPostEditing(null); setPostViewing(null);
            }}>
              {Bi(tab === "tracks" ? "음원" : tab === "videos" ? "영상" : tab === "plans" ? "계획안" : tab === "share" ? "공유" : (selectedSubject === "senses" ? "물품리스트" : "그림자료"))}
            </button>
          ))}
        </div>

        {subjectTab === "plans" ? renderPlansTab() : subjectTab === "share" ? renderShareTab() : renderContentTab()}
      </div>
    );
  };

  const renderFavorites = () => {
    // Get subjects that have playlists
    const playlistSubjects = Object.entries(playlists).filter(([_, ids]) => ids.length > 0);
    const allFavIds = Object.values(playlists).flat();

    // Get tracks for current filter
    const getFilteredTracks = () => {
      if (favSubjectFilter === "all") {
        // Show all, grouped by subject order in playlists
        const result = [];
        playlistSubjects.forEach(([subj, ids]) => {
          ids.forEach(id => {
            const trk = allTracksFlat.find(t => t.id === id);
            if (trk) result.push({ ...trk, playlistSubject: subj });
          });
        });
        return result;
      }
      const ids = playlists[favSubjectFilter] || [];
      return ids.map(id => allTracksFlat.find(t => t.id === id)).filter(Boolean).map(t => ({ ...t, playlistSubject: favSubjectFilter }));
    };

    const filteredTracks = getFilteredTracks();

    return (
      <div style={S.section}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={S.sectionTitle}>즐겨찾기 <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>Favorites</span></div>
          {filteredTracks.length > 0 && (
            <button
              style={{ ...S.btn(reorderMode ? "primary" : "outline"), fontSize: 11, padding: "5px 12px" }}
              onClick={() => setReorderMode(!reorderMode)}
            >
              {reorderMode ? "완료" : "순서 편집"}
            </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: "#999", marginBottom: 16, fontWeight: 500 }}>
          과목별 플레이리스트로 수업 음원을 관리하세요
        </div>

        {/* 오프라인 저장됨 목록 (캐시 정리) — 주황 포인트 */}
        {downloadedFiles.length > 0 && (
          <div style={{ marginBottom: 20, background: "#fff", borderRadius: 20, padding: "14px 12px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 4px 10px" }}>
              <span style={{ width: 40, height: 40, borderRadius: 13, background: OFFLINE_TINT, color: OFFLINE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{Icons.download}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#191F28" }}>
                  오프라인 저장 <span style={{ display: "inline-block", marginLeft: 4, padding: "1px 8px", borderRadius: 10, background: OFFLINE_TINT, color: OFFLINE_DARK, fontSize: 12, fontWeight: 800, verticalAlign: 1 }}>{downloadedFiles.length}곡</span>
                </div>
                <div style={{ fontSize: 12, color: "#8B95A1", marginTop: 1 }}>인터넷이 없어도 재생돼요</div>
              </div>
              <button
                style={{ background: "none", border: "none", color: "#8B95A1", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font, padding: "10px 4px", flexShrink: 0 }}
                onClick={async () => {
                  if (!window.confirm("오프라인 저장된 음원을 모두 삭제할까요?")) return;
                  for (const id of [...downloadedFiles]) { await removeDownload(id); }
                }}
              >전체 삭제</button>
            </div>
            {downloadedFiles.map(id => {
              const t = allTracksFlat.find(x => x.id === id);
              const title = t ? t.title : id;
              const active = playingTrack?.id === id;
              return (
                <div key={"off-" + id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 4px 8px 8px", borderRadius: 16, background: active ? OFFLINE_TINT : "transparent" }}>
                  <button aria-label="재생" style={{ width: 44, height: 44, borderRadius: 22, border: "none", background: active ? OFFLINE : OFFLINE_TINT, color: active ? "#fff" : OFFLINE, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: t ? "pointer" : "default", opacity: t ? 1 : 0.5 }} onClick={() => t && playTrack(t)} disabled={!t}>
                    {active && isPlaying ? Icons.pause : Icons.play}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: active ? OFFLINE_DARK : "#191F28", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                    <div style={{ fontSize: 12, color: "#8B95A1" }}>{t ? getTrackDuration(t) : "오프라인 저장됨"}</div>
                  </div>
                  <button
                    aria-label="오프라인 삭제"
                    style={{ ...touch44, background: "none", border: "none", color: "#B0B8C1", fontSize: 19, cursor: "pointer", flexShrink: 0 }}
                    onClick={() => removeDownload(id)}
                    title="오프라인 삭제"
                  >{svgIcon(<><path d="M4 7h16M10 11v6M14 11v6" /><path d="M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12" /><path d="M9 7V4h6v3" /></>)}</button>
                </div>
              );
            })}
          </div>
        )}

        {allFavIds.length === 0 ? (
          <div style={S.empty}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>★</div>
            즐겨찾기한 음원이 없습니다<br/>
            <span style={{ fontSize: 12 }}>음원 목록에서 ♡ 를 눌러 추가하세요</span>
          </div>
        ) : (
          <>
            {/* Subject filter pills */}
            <div style={{ ...S.pillWrap, marginBottom: 16 }}>
              <button style={S.pill(favSubjectFilter === "all")} onClick={() => setFavSubjectFilter("all")}>
                전체 ({allFavIds.length})
              </button>
              {playlistSubjects.map(([subj, ids]) => {
                const s = subjects.find(x => x.id === subj);
                return (
                  <button key={subj} style={S.pill(favSubjectFilter === subj)} onClick={() => setFavSubjectFilter(subj)}>
                    {s?.icon} {s?.name} ({ids.length})
                  </button>
                );
              })}
            </div>

            {/* Play all */}
            {filteredTracks.length > 0 && !reorderMode && (
              <button style={{ ...S.btn("primary"), width: "100%", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 0" }} onClick={() => playTrack(filteredTracks[0])}>
                {Icons.play} 전체 재생 ({filteredTracks.length}곡)
              </button>
            )}

            {/* Track list with reorder */}
            {favSubjectFilter !== "all" && reorderMode ? (
              // 화살표(▲▼) 순서 편집 모드 — 드래그보다 모바일에서 안정적
              (playlists[favSubjectFilter] || []).map((trackId, idx, arr) => {
                const track = allTracksFlat.find(t => t.id === trackId);
                if (!track) return null;
                return (
                  <div
                    key={track.id}
                    style={{
                      ...S.trackItem(false),
                      justifyContent: "space-between",
                      userSelect: "none",
                      WebkitUserSelect: "none",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                      {/* 순서 이동 ▲▼ */}
                      <div style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
                        <button
                          style={{ background: "none", border: "none", fontSize: 15, lineHeight: 1, cursor: idx === 0 ? "default" : "pointer", color: "#888", padding: "3px 6px", opacity: idx === 0 ? 0.25 : 1 }}
                          disabled={idx === 0}
                          aria-label="위로"
                          onClick={() => movePlaylistItem(favSubjectFilter, idx, -1)}
                        >▲</button>
                        <button
                          style={{ background: "none", border: "none", fontSize: 15, lineHeight: 1, cursor: idx === arr.length - 1 ? "default" : "pointer", color: "#888", padding: "3px 6px", opacity: idx === arr.length - 1 ? 0.25 : 1 }}
                          disabled={idx === arr.length - 1}
                          aria-label="아래로"
                          onClick={() => movePlaylistItem(favSubjectFilter, idx, 1)}
                        >▼</button>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#999", width: 20, textAlign: "center", flexShrink: 0 }}>{idx + 1}</span>
                      <div style={S.trackInfo}>
                        <div style={S.trackTitle}>{track.title}</div>
                        <div style={S.trackDuration(false)}>{getTrackDuration(track)}</div>
                      </div>
                    </div>
                    <button
                      style={{ ...S.btn("sm"), padding: "6px 8px", fontSize: 12, background: "#fee2e2", color: "#dc2626", lineHeight: 1, flexShrink: 0 }}
                      onClick={(e) => { e.stopPropagation(); toggleFav(trackId, favSubjectFilter); }}
                    >{Icons.close}</button>
                  </div>
                );
              })
            ) : reorderMode && favSubjectFilter === "all" ? (
              <div style={{ ...S.empty, padding: "20px" }}>
                순서를 편집하려면 과목을 선택해주세요
              </div>
            ) : (
              // Normal play mode
              filteredTracks.map((t, idx) => {
                const playing = playingTrack?.id === t.id && isPlaying;
                const active = playingTrack?.id === t.id;
                const showHeader = favSubjectFilter === "all" && (idx === 0 || t.playlistSubject !== filteredTracks[idx - 1]?.playlistSubject);
                const subjInfo = subjects.find(s => s.id === t.playlistSubject);
                return (
                  <div key={t.id + "-" + idx}>
                    {showHeader && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: idx > 0 ? 16 : 0, marginBottom: 8 }}>
                        <span style={{ fontSize: 16 }}>{subjInfo?.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{subjInfo?.name}</span>
                        <span style={{ fontSize: 11, color: "#999" }}>({(playlists[t.playlistSubject] || []).length}곡)</span>
                      </div>
                    )}
                    <div style={S.trackItem(active)}>
                      <button style={S.trackPlay(active)} onClick={() => playTrack(t)}>
                        {playing ? Icons.pause : Icons.play}
                      </button>
                      <div style={S.trackInfo} onClick={() => playTrack(t)}>
                        <div style={S.trackTitle}>{t.title}</div>
                        <div style={S.trackDuration(active)}>{getTrackDuration(t)}</div>
                      </div>
                      <button style={S.favBtn(true, active)} onClick={() => toggleFav(t.id)}>
                        {Icons.heart}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    );
  };

  // Shared content renderer for events
  // 우선순위: eventContent[ev.id] (Firestore) → source.tracks/videos/plans (시드 fallback)
  const renderEventContent = (source, color) => {
    const eid = source?.id;
    const ec = eid ? eventContent[eid] : null;
    const tracksList = (ec?.tracks && ec.tracks.length > 0) ? ec.tracks : (source.tracks || []);
    const videosList = (ec?.videos && ec.videos.length > 0) ? ec.videos : (source.videos || []);
    const plansList  = (ec?.plans && ec.plans.length > 0)   ? ec.plans  : (source.plans  || []);
    const isAdminMode = showAdmin && adminLoggedIn;

    return (
      <>
        <div style={{ ...S.pillWrap, marginTop: 4 }}>
          {["tracks", "videos", "plans"].map(tab => (
            <button key={tab} style={S.pill(eventTab === tab)} onClick={() => setEventTab(tab)}>
              {tab === "tracks" ? `음원 (${tracksList.length})` : tab === "videos" ? `영상 (${videosList.length})` : `계획안 (${plansList.length})`}
            </button>
          ))}
        </div>

        {/* 관리자 모드: 현재 탭에 새 항목 추가 버튼 */}
        {isAdminMode && !eventEditItem && (
          <div style={{ marginBottom: 10 }}>
            <button style={S.btn("primary")} onClick={() => {
              setEventBulkFiles([]);
              if (eventTab === "tracks") setEventEditItem({ kind: "track", isNew: true, title: "", duration: "", rawFile: null });
              else if (eventTab === "videos") setEventEditItem({ kind: "video", isNew: true, title: "", duration: "", youtubeUrl: "" });
              else setEventEditItem({ kind: "plan", isNew: true, title: "", rawFile: null });
            }}>
              {Icons.add} {eventTab === "tracks" ? "음원" : eventTab === "videos" ? "영상" : "계획안"} 추가
            </button>
          </div>
        )}

        {/* 관리자 편집 폼 */}
        {isAdminMode && eventEditItem && (
          <div style={{ ...S.card, border: "1.5px solid #111", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {eventEditItem.isNew ? "새 항목 추가" : "항목 수정"} — {eventEditItem.kind === "track" ? "음원" : eventEditItem.kind === "video" ? "영상" : "계획안"}
              </div>
              <button style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999" }}
                onClick={() => { setEventEditItem(null); setEventBulkFiles([]); }} disabled={eventUploading}>×</button>
            </div>

            {eventEditItem.kind === "video" ? (
              <>
                <input style={S.input} placeholder="https://www.youtube.com/watch?v=..." value={eventEditItem.youtubeUrl || ""} onChange={e => setEventEditItem(ei => ({ ...ei, youtubeUrl: e.target.value }))} />
                <input style={S.input} placeholder="제목" value={eventEditItem.title} onChange={e => setEventEditItem(ei => ({ ...ei, title: e.target.value }))} />
                <input style={S.input} placeholder="재생시간 (예: 5:30, 선택사항)" value={eventEditItem.duration || ""} onChange={e => setEventEditItem(ei => ({ ...ei, duration: e.target.value }))} />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                  <button style={S.btn("outline")} onClick={() => setEventEditItem(null)} disabled={eventUploading}>취소</button>
                  <button style={S.btn("primary")} disabled={eventUploading} onClick={() => uploadEventItem(source)}>
                    {eventUploading ? "업로드 중..." : "등록"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* 다중 파일 선택 영역 */}
                <div
                  style={{
                    border: "2px dashed #ddd", borderRadius: 12, padding: "20px 16px",
                    textAlign: "center", cursor: eventUploading ? "default" : "pointer",
                    background: "#fafafa", marginBottom: 12,
                  }}
                  onClick={() => {
                    if (eventUploading) return;
                    openFilePicker({
                      accept: eventEditItem.kind === "track" ? "*/*" : ".pdf",
                      multiple: true,
                      onFiles: (files) => {
                        const newBulk = files.map((f, i) => ({
                          id: `evbulk_${Date.now()}_${i}`,
                          rawFile: f,
                          title: f.name.replace(/\.[^/.]+$/, "").replace(/_/g, " "),
                          duration: "",
                        }));
                        setEventBulkFiles(prev => [...prev, ...newBulk]);
                        if (eventEditItem.kind === "track") {
                          files.forEach((f, i) => {
                            const bid = newBulk[i].id;
                            extractAudioDuration(f).then(dur => {
                              setEventBulkFiles(bfs => bfs.map(b => b.id === bid ? { ...b, duration: dur } : b));
                            }).catch(() => {});
                          });
                        }
                      },
                    });
                  }}
                >
                  <div style={{ fontSize: 28, color: "#ccc" }}>↑</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#666", marginTop: 6 }}>
                    {eventEditItem.kind === "track" ? "음원 파일" : "PDF 파일"} 선택
                  </div>
                  <div style={{ fontSize: 11, color: "#bbb", marginTop: 4 }}>여러 파일을 한번에 올릴 수 있습니다</div>
                </div>

                {/* 대기 중인 파일 목록 */}
                {eventBulkFiles.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>대기 중인 파일 ({eventBulkFiles.length}개)</div>
                      <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11 }}
                        disabled={eventUploading}
                        onClick={() => setEventBulkFiles([])}>전체 삭제</button>
                    </div>
                    {eventBulkFiles.map((bf, idx) => (
                      <div key={bf.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#f9f9f9", borderRadius: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: "#999", width: 22, textAlign: "center", flexShrink: 0 }}>{idx + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <input
                            style={{ ...S.input, fontSize: 12, padding: "4px 8px", marginBottom: 0, width: "100%" }}
                            placeholder="제목"
                            value={bf.title}
                            disabled={eventUploading}
                            onChange={e => {
                              const v = e.target.value;
                              setEventBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, title: v } : b));
                            }}
                          />
                          <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                            {bf.rawFile.name} · {(bf.rawFile.size / 1024 / 1024).toFixed(1)}MB
                            {eventEditItem.kind === "track" && bf.duration && ` · ${bf.duration}`}
                          </div>
                        </div>
                        <button
                          style={{ background: "none", border: "none", color: "#dc2626", fontSize: 16, cursor: "pointer", padding: 4, flexShrink: 0 }}
                          disabled={eventUploading}
                          onClick={() => setEventBulkFiles(bfs => bfs.filter(b => b.id !== bf.id))}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 진행 상황 */}
                {eventBulkProgress && (
                  <div style={{ padding: "10px 12px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, marginBottom: 12, fontSize: 12, fontWeight: 600 }}>
                    업로드 중... {eventBulkProgress.current} / {eventBulkProgress.total}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button style={S.btn("outline")} onClick={() => { setEventEditItem(null); setEventBulkFiles([]); }} disabled={eventUploading}>취소</button>
                  <button
                    style={S.btn("primary")}
                    disabled={eventUploading || eventBulkFiles.length === 0}
                    onClick={() => uploadEventBulk(source, eventEditItem.kind)}
                  >
                    {eventUploading ? `업로드 중...` : `${eventBulkFiles.length}개 업로드`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {eventTab === "tracks" && (
          tracksList.length === 0 ? <div style={S.empty}>등록된 음원이 없습니다</div> :
          tracksList.map(t => {
            const playing = playingTrack?.id === t.id && isPlaying;
            const active = playingTrack?.id === t.id;
            const dlState = isDownloaded(t.id) ? "done" : isDownloading(t.id) ? "loading" : "none";
            return (
              <div key={t.id} style={S.trackItem(active)}>
                <button style={S.trackPlay(active)} onClick={() => playTrack(t)}>{playing ? Icons.pause : Icons.play}</button>
                <div style={S.trackInfo} onClick={() => playTrack(t)}>
                  <div style={S.trackTitle}>{t.title}</div>
                  <div style={S.trackDuration(active)}>{getTrackDuration(t)}{dlState === "done" && " · 오프라인"}</div>
                </div>
                {!isAdminMode && (
                  <>
                    <button style={{ ...touch44, background: "none", border: "none", fontSize: 16, cursor: dlState === "done" ? "default" : "pointer", color: dlState === "done" ? "#16A34A" : dlState === "loading" ? "#f59e0b" : "#B0B8C1", flexShrink: 0 }} onClick={() => handleDownload(t)}>
                      {dlState === "done" ? "✓" : dlState === "loading" ? <span style={{ display: "inline-block", animation: "spin 1s linear infinite", fontSize: 14 }}>⟳</span> : "↓"}
                    </button>
                    <button style={{ ...S.favBtn(favorites.includes(t.id), active), flexShrink: 0 }} onClick={() => toggleFav(t.id)}>
                      {favorites.includes(t.id) ? Icons.heart : Icons.heartEmpty}
                    </button>
                  </>
                )}
                {isAdminMode && t.firestoreId && (
                  <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", flexShrink: 0 }}
                    onClick={() => deleteEventItem("track", t)}>삭제</button>
                )}
              </div>
            );
          })
        )}

        {eventTab === "videos" && (
          videosList.length === 0 ? <div style={S.empty}>등록된 영상이 없습니다</div> :
          videosList.map(v => (
            <div key={v.id} style={{ position: "relative", marginBottom: 8 }}>
              {renderVideoCard(v, color)}
              {isAdminMode && v.firestoreId && (
                <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", position: "absolute", top: 8, right: 8, zIndex: 2 }}
                  onClick={() => deleteEventItem("video", v)}>삭제</button>
              )}
            </div>
          ))
        )}

        {eventTab === "plans" && (
          plansList.length === 0 ? <div style={S.empty}>등록된 계획안이 없습니다</div> :
          plansList.map(p => (
            <div key={p.id} style={{ position: "relative", marginBottom: 8 }}>
              {renderPlanCard(p, "")}
              {isAdminMode && p.firestoreId && (
                <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", position: "absolute", top: 8, right: 8, zIndex: 2 }}
                  onClick={() => deleteEventItem("plan", p)}>삭제</button>
              )}
            </div>
          ))
        )}
      </>
    );
  };

  const renderRecent = () => (
    <div style={S.section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={S.sectionTitle}>최근 재생 <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>Recently Played</span></div>
        {recentHistory.length > 0 && (
          <button style={{ ...S.btn("sm"), fontSize: 11, background: "#fee2e2", color: "#dc2626" }} onClick={() => setRecentHistory([])}>기록 삭제</button>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#999", marginBottom: 16, fontWeight: 500 }}>
        최근 재생한 음원이 시간순으로 표시됩니다
      </div>
      {recentHistory.length === 0 ? (
        <div style={S.empty}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🕐</div>
          재생 기록이 없습니다<br/>
          <span style={{ fontSize: 12 }}>음원을 재생하면 여기에 기록됩니다</span>
        </div>
      ) : (
        recentHistory.map((item, idx) => {
          const t = item.track;
          const playing = playingTrack?.id === t.id && isPlaying;
          const active = playingTrack?.id === t.id;
          return (
            <div key={t.id + "-" + idx} style={{ ...S.trackItem(active), position: "relative" }}>
              <button style={S.trackPlay(active)} onClick={() => playTrack(t)}>
                {playing ? Icons.pause : Icons.play}
              </button>
              <div style={S.trackInfo} onClick={() => playTrack(t)}>
                <div style={S.trackTitle}>{t.title}</div>
                <div style={S.trackDuration(active)}>
                  {t.subject && <span>{subjects.find(s => s.id === t.subject)?.name} · </span>}
                  {getTrackDuration(t)} · {item.timestamp}
                </div>
              </div>
              <button style={{ ...S.favBtn(favorites.includes(t.id), active), flexShrink: 0 }} onClick={() => toggleFav(t.id, t.subject)}>
                {favorites.includes(t.id) ? Icons.heart : Icons.heartEmpty}
              </button>
            </div>
          );
        })
      )}
    </div>
  );

  const renderEvents = () => {
    // Level 2: 행사 상세 (음원/영상/계획안 탭)
    if (selectedEvent) {
      const ev = selectedEvent;
      return (
        <div style={{ ...S.section, padding: 0, paddingBottom: 100 }}>
          {/* Theme banner — 제목과 이모지만 */}
          <div style={{
            background: ev.themeBg || "#111", padding: "32px 24px 24px", position: "relative",
            display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
          }}>
            <div style={{ fontSize: 56, marginBottom: 10, filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.2))" }}>{ev.themeEmoji}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>{ev.title}</div>
          </div>

          <div style={{ padding: "16px 20px 0" }}>
            {renderEventContent(ev, ev.themeColor)}
          </div>
        </div>
      );
    }

    // Level 1: 선택한 과목의 행사 목록
    if (eventSubject) {
      const subj = subjects.find(s => s.id === eventSubject);
      const evts = events[eventSubject] || [];
      return (
        <div style={S.section}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 28 }}>{subj?.icon}</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{subj?.name} 행사</div>
              <div style={{ fontSize: 12, color: "#999" }}>{subj?.nameEn} Events · {evts.length}개</div>
            </div>
          </div>
          {evts.length === 0 ? (
            <div style={S.empty}>등록된 행사가 없습니다</div>
          ) : (
            evts.map(ev => {
              const contentCount = [
                ((eventContent[ev.id]?.tracks?.length ?? ev.tracks?.length) || 0) + "곡",
                ((eventContent[ev.id]?.videos?.length ?? ev.videos?.length) || 0) + "영상",
                ((eventContent[ev.id]?.plans?.length ?? ev.plans?.length) || 0) + "계획안",
              ].filter(x => !x.startsWith("0")).join(" · ") || "자료 없음";
              return (
                <div key={ev.id} style={{ ...S.card, overflow: "hidden", padding: 0, cursor: "pointer" }} onClick={() => { setSelectedEvent(ev); setEventTab("tracks"); }}>
                  <div style={{ background: ev.themeBg || "#111", padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                    <span style={{ fontSize: 32, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.2))" }}>{ev.themeEmoji}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.2)" }}>{ev.title}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>{contentCount}</div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      );
    }

    // Level 0: 과목 목록 (모든 과목 표시, 각 과목의 행사 개수 표시)
    return (
      <div style={S.section}>
        <div style={S.sectionTitle}>과목별 행사 <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>Events</span></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12 }}>
          {subjects.map(subj => {
            const evts = events[subj.id] || [];
            return (
              <button
                key={subj.id}
                style={{
                  ...S.card,
                  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8,
                  cursor: "pointer", textAlign: "left", border: "1px solid #eee",
                  padding: "18px 16px",
                }}
                onClick={() => setEventSubject(subj.id)}
              >
                <span style={{ fontSize: 30 }}>{subj.icon}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>{subj.name}</div>
                  <div style={{ fontSize: 11, color: evts.length > 0 ? "#f97316" : "#bbb", fontWeight: 600, marginTop: 2 }}>
                    행사 {evts.length}개
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const ADMIN_PASSWORD = "wollim2026"; // fallback

  const isAdmin = () => {
    if (!authState) return false;
    if (authState.role === "admin" || authState.role === "owner") return true;
    const user = registeredUsers.find(u => u.firestoreId === authState.firestoreId || u.email === authState.email);
    return user?.role === "admin" || user?.role === "owner";
  };

  const handleAdminLogin = () => {
    // Admin access: role-based (primary) or password fallback
    if (isAdmin()) {
      setAdminLoggedIn(true);
      setAdminPwError(false);
      setAdminTab("dashboard");
    } else if (adminPw === ADMIN_PASSWORD) {
      setAdminLoggedIn(true);
      setAdminPwError(false);
      setAdminTab("dashboard");
    } else {
      setAdminPwError(true);
    }
  };

  // Admin: get content list for current filters
  const getAdminContentListRaw = () => {
    const src = adminContentType === "track" ? tracks : adminContentType === "video" ? videos : adminContentType === "picture" ? pictures : null;
    if (!src || !src[adminContentSubject]) return [];
    if (!adminContentIssue) return [];
    const skipsSteps = subjectSkipsStepsDyn(adminContentSubject);
    if (!skipsSteps && !adminContentStep) return [];
    const subjData = src[adminContentSubject];
    const hasPrograms = getProgramsBySubject(adminContentSubject);
    if (hasPrograms && adminContentProgram) {
      if (skipsSteps) {
        // skipsSteps 과목: 모든 step 데이터 합치기
        const issueData = subjData?.[adminContentProgram]?.[adminContentIssue];
        if (!issueData) return [];
        return Object.values(issueData).reduce((acc, v) => Array.isArray(v) ? [...acc, ...v] : acc, []);
      }
      const items = subjData?.[adminContentProgram]?.[adminContentIssue]?.[adminContentStep];
      return Array.isArray(items) ? items : [];
    } else if (!hasPrograms) {
      if (skipsSteps) {
        const issueData = subjData?.[adminContentIssue];
        if (!issueData) return [];
        return Object.values(issueData).reduce((acc, v) => Array.isArray(v) ? [...acc, ...v] : acc, []);
      }
      const items = subjData?.[adminContentIssue]?.[adminContentStep];
      return Array.isArray(items) ? items : [];
    }
    return [];
  };

  // 관리자 콘텐츠 목록을 order 순으로 정렬해서 반환 (순서 변경 ↑↓ 반영)
  const getAdminContentList = () => {
    return getAdminContentListRaw().slice().sort((a, b) => {
      const oa = (typeof a?.order === "number") ? a.order : 999;
      const ob = (typeof b?.order === "number") ? b.order : 999;
      if (oa !== ob) return oa - ob;
      return (a?.title || "").localeCompare(b?.title || "", "ko");
    });
  };

  // Admin: get events for subject
  const getAdminEvents = () => events[adminEventSubject] || [];

  // Admin: count all content for a subject (handles program-nested structure)
  const countSubjectContent = (subjId) => {
    let count = 0;
    const hasPrograms = getProgramsBySubject(subjId);

    [tracks, videos].forEach(src => {
      const subjData = src[subjId];
      if (!subjData) return;
      if (hasPrograms) {
        Object.values(subjData).forEach(progData => {
          if (!progData || typeof progData !== "object") return;
          Object.values(progData).forEach(issue => {
            if (!issue || typeof issue !== "object") return;
            Object.values(issue).forEach(stepArr => {
              count += Array.isArray(stepArr) ? stepArr.length : 0;
            });
          });
        });
      } else {
        Object.values(subjData).forEach(issue => {
          if (!issue || typeof issue !== "object") return;
          Object.values(issue).forEach(stepArr => {
            count += Array.isArray(stepArr) ? stepArr.length : 0;
          });
        });
      }
    });

    const plansSubj = plans[subjId];
    if (plansSubj) {
      // step 값이 배열이면 length, 단일객체면 1로 계산
      const countStep = (v) => Array.isArray(v) ? v.length : (v ? 1 : 0);
      if (hasPrograms) {
        Object.values(plansSubj).forEach(progPlans => {
          if (progPlans?.monthly) Object.values(progPlans.monthly).forEach(issue => { Object.values(issue).forEach(v => { count += countStep(v); }); });
          if (progPlans?.yearly) Object.values(progPlans.yearly).forEach(v => { count += countStep(v); });
        });
      } else {
        if (plansSubj?.monthly) Object.values(plansSubj.monthly).forEach(issue => { Object.values(issue).forEach(v => { count += countStep(v); }); });
        if (plansSubj?.yearly) Object.values(plansSubj.yearly).forEach(v => { count += countStep(v); });
      }
    }

    return count;
  };

  const renderAdminLogin = () => {
    // Admin role users: auto-login without password
    if (isAdmin() && !adminLoggedIn) {
      setTimeout(() => { setAdminLoggedIn(true); setAdminTab("dashboard"); }, 0);
      return <div style={{ ...S.section, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}><div style={{ fontSize: 14, color: "#999" }}>관리자 모드 진입 중...</div></div>;
    }

    return (
    <div style={{ ...S.section, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <div style={{ width: "100%", maxWidth: 320, textAlign: "center" }}>
        <div style={{ width: 64, height: 64, background: "#111", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: "#fff", fontSize: 28 }}>
          {Icons.admin}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 6 }}>관리자 로그인</div>
        <div style={{ fontSize: 12, color: "#999", marginBottom: 28 }}>관리자 권한이 없습니다. 비밀번호를 입력해주세요.</div>
        <input
          style={{ ...S.input, textAlign: "center", fontSize: 16, padding: "14px", letterSpacing: 4, border: adminPwError ? "1.5px solid #dc2626" : "1.5px solid #ddd" }}
          type="password"
          placeholder="••••••••"
          value={adminPw}
          onChange={e => { setAdminPw(e.target.value); setAdminPwError(false); }}
          onKeyDown={e => e.key === "Enter" && handleAdminLogin()}
        />
        {adminPwError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4, marginBottom: 8 }}>비밀번호가 올바르지 않습니다</div>}
        <button style={{ ...S.btn("primary"), width: "100%", padding: "14px 0", marginTop: 8, fontSize: 14 }} onClick={handleAdminLogin}>로그인</button>
      </div>
    </div>
    );
  };

  const renderAdmin = () => {
    if (!adminLoggedIn) return renderAdminLogin();

    return (
      <div style={S.section}>
        {/* Admin nav tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
          {[
            { id: "dashboard", label: "대시보드" },
            { id: "content", label: "콘텐츠" },
            { id: "event", label: "행사" },
            { id: "users", label: "사용자" },
            { id: "stats", label: "통계" },
          ].map(tab => (
            <button key={tab.id} style={S.pill(adminTab === tab.id)} onClick={() => { setAdminTab(tab.id); setAdminContentIssue(null); setAdminContentStep(null); setAdminContentProgram(null); }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Dashboard ── */}
        {adminTab === "dashboard" && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>관리자 대시보드</div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 20 }}>울림티쳐 앱 콘텐츠를 관리합니다</div>

            {/* 프로그램 동기화 (새 프로그램 추가 후 1회 실행) */}
            <div style={{ ...S.card, marginBottom: 20, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>프로그램 동기화</div>
                  <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>새로 추가된 프로그램을 앱에 반영합니다 (기존 데이터는 유지)</div>
                </div>
                <button
                  style={{ ...S.btn("primary"), fontSize: 12, padding: "8px 14px", flexShrink: 0, opacity: programSyncing ? 0.5 : 1 }}
                  disabled={programSyncing}
                  onClick={syncProgramsToFirestore}
                >{programSyncing ? "동기화 중..." : "동기화"}</button>
              </div>
              {programSyncMsg && (
                <div style={{ fontSize: 11, color: "#16a34a", marginTop: 10, lineHeight: 1.5 }}>{programSyncMsg}</div>
              )}
            </div>

            {/* Stats cards */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[
                { label: "과목 수", value: subjects.length, icon: "📚" },
                { label: "승인 사용자", value: registeredUsers.filter(u => u.status === "approved").length, icon: "👤" },
                { label: "승인 대기", value: registeredUsers.filter(u => u.status === "pending").length, icon: "⏳" },
              ].map((stat, i) => (
                <div key={i} style={{ ...S.card, display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 24 }}>{stat.icon}</span>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 800 }}>{stat.value}</div>
                    <div style={{ fontSize: 11, color: "#999" }}>{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Content overview per subject */}
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>과목별 콘텐츠 현황</div>
            {subjects.map(subj => (
              <div key={subj.id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{subj.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{subj.name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#999" }}>{countSubjectContent(subj.id)}개</span>
                  <button style={{ ...S.btn("sm"), fontSize: 11, padding: "4px 10px" }} onClick={() => { setAdminTab("content"); setAdminContentSubject(subj.id); }}>관리</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Content Management ── */}
        {adminTab === "content" && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>콘텐츠 관리</div>

            {/* Subject selector */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>과목</div>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
                {subjects.map(s => (
                  <button key={s.id} style={S.pill(adminContentSubject === s.id)} onClick={() => { setAdminContentSubject(s.id); setAdminContentProgram(null); setAdminContentIssue(null); setAdminContentStep(null); }}>
                    {s.icon} {s.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Program selector (if subject has programs) */}
            {getProgramsBySubject(adminContentSubject) && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>프로그램</div>
                <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
                  {getProgramsBySubject(adminContentSubject).map(prog => (
                    <button key={prog.id} style={S.pill(adminContentProgram === prog.id)} onClick={() => { setAdminContentProgram(prog.id); setAdminContentIssue(null); setAdminContentStep(null); }}>
                      {prog.logo ? <img src={prog.logo} alt="" style={{ width: 18, height: 16, objectFit: "contain", verticalAlign: -3, marginRight: 4 }} /> : <>{prog.icon} </>}{prog.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Type selector */}
            {(!getProgramsBySubject(adminContentSubject) || adminContentProgram) && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>유형</div>
                <div style={S.pillWrap}>
                  {[{ id: "track", label: "음원" }, { id: "video", label: "영상" }, { id: "plan", label: "계획안" }, { id: "picture", label: adminContentSubject === "senses" ? "물품리스트" : "그림자료" }].map(t => (
                    <button key={t.id} style={S.pill(adminContentType === t.id)} onClick={() => { setAdminContentType(t.id); setAdminContentIssue(null); setAdminContentStep(null); }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Show "select program" message if needed */}
            {getProgramsBySubject(adminContentSubject) && !adminContentProgram && (
              <div style={S.empty}>프로그램을 선택해주세요</div>
            )}

            {(!getProgramsBySubject(adminContentSubject) || adminContentProgram) && (
              <>
                <div style={S.divider} />

                {/* Issue grid */}
                {!adminContentIssue && (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
                      {MONTH_LABELED_SUBJECTS.includes(adminContentSubject)
                        ? <>월 선택 <span style={{ fontSize: 9, color: "#999", fontWeight: 400 }}>Select Month</span></>
                        : <>호수 선택 <span style={{ fontSize: 9, color: "#999", fontWeight: 400 }}>Select Issue</span></>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                      {getIssuesForContext(adminContentProgram, adminContentType === "track" ? "tracks" : adminContentType).map(issue => {
                        const hasProgs = getProgramsBySubject(adminContentSubject);
                        const progKey = adminContentProgram;
                        let has = false;
                        const d = adminContentType === "track" ? tracks : adminContentType === "video" ? videos : adminContentType === "picture" ? pictures : plans;
                        const subjData = d?.[adminContentSubject];
                        if (adminContentType === "plan") {
                          if (hasProgs && progKey) { has = !!subjData?.[progKey]?.monthly?.[issue.id]; }
                          else { has = !!subjData?.monthly?.[issue.id]; }
                        } else {
                          if (hasProgs && progKey) { has = !!subjData?.[progKey]?.[issue.id]; }
                          else { has = !!subjData?.[issue.id]; }
                        }
                        const isMonthLabel = MONTH_LABELED_SUBJECTS.includes(adminContentSubject);
                        const isSpecial = typeof issue.id !== "number"; // 발표회/그외 등 특수 폴더
                        const month = isMonthLabel ? issueToMonth(issue.id) : null;
                        return (
                          <button key={issue.id} style={{ ...S.subjectBtn(adminContentIssue === issue.id), position: "relative" }} onClick={() => setAdminContentIssue(issue.id)}>
                            <span style={{ fontSize: 14, fontWeight: 800 }}>{isSpecial ? issue.label : isMonthLabel ? month : issue.id}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: adminContentIssue === issue.id ? "#fff" : "#888" }}>{isSpecial ? "\u00a0" : isMonthLabel ? "월" : issue.label}</span>
                            {has && <span style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Step grid */}
                {adminContentIssue && !adminContentStep && !subjectSkipsStepsDyn(adminContentSubject) && (
                  <>
                    <button style={{ ...S.backBtn, marginBottom: 12 }} onClick={() => setAdminContentIssue(null)}>
                      {Icons.back} <span style={S.backText}>{issueLabel(adminContentIssue, adminContentSubject)}</span>
                    </button>
                    {adminContentProgram === "play-melody" ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>단계 선택</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          {(getProgramCustomStages(adminContentProgram) || []).map(stage => (
                            <button key={stage.id} style={{ ...S.subjectBtn(false), padding: "16px 12px" }} onClick={() => setAdminContentStep(stage.id)}>
                              <span style={{ fontSize: 14, fontWeight: 800 }}>{stage.label}</span>
                              <span style={{ fontSize: 10, color: "#999" }}>{stage.desc}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                    <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>연령 선택</div>
                    {(() => { const adminSteps = getStepsForSubjectDyn(adminContentSubject); return (
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(adminSteps.length, 4)}, 1fr)`, gap: 8 }}>
                      {adminSteps.map(step => {
                        const hasProgs = getProgramsBySubject(adminContentSubject);
                        const progKey = adminContentProgram;
                        let has = false;
                        const d = adminContentType === "track" ? tracks : adminContentType === "video" ? videos : adminContentType === "picture" ? pictures : plans;
                        const subjData = d?.[adminContentSubject];
                        if (adminContentType === "plan") {
                          if (hasProgs && progKey) { has = !!subjData?.[progKey]?.monthly?.[adminContentIssue]?.[step.id]; }
                          else { has = !!subjData?.monthly?.[adminContentIssue]?.[step.id]; }
                        } else {
                          if (hasProgs && progKey) { has = !!(subjData?.[progKey]?.[adminContentIssue]?.[step.id]?.length); }
                          else { has = !!(subjData?.[adminContentIssue]?.[step.id]?.length); }
                        }
                        return (
                          <button key={step.id} style={{ ...S.subjectBtn(false), position: "relative" }} onClick={() => setAdminContentStep(step.id)}>
                            <span style={{ fontSize: 12, fontWeight: 800 }}>{step.label}</span>
                            <span style={{ fontSize: 9, color: "#999" }}>{step.age} · {step.ageEn}</span>
                            {has && <span style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />}
                          </button>
                        );
                  })}
                </div>
                    ); })()}
                      </>
                    )}
              </>
            )}

            {/* Content list with CRUD */}
            {/* skipsSteps 과목(오감 등): adminContentIssue 만으로 콘텐츠 화면 진입. step은 "all" 가상 키 사용 */}
            {((adminContentIssue && adminContentStep) || (adminContentIssue && subjectSkipsStepsDyn(adminContentSubject))) && (
              <>
                <button style={{ ...S.backBtn, marginBottom: 12 }} onClick={() => { if (subjectSkipsStepsDyn(adminContentSubject)) { setAdminContentIssue(null); setAdminContentStep(null); } else setAdminContentStep(null); }}>
                  {Icons.back} <span style={S.backText}>{issueLabel(adminContentIssue, adminContentSubject)}{!subjectSkipsStepsDyn(adminContentSubject) ? ` · ${STEPS.find(s => s.id === adminContentStep)?.label || adminContentStep}` : ""}</span>
                </button>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {adminContentType === "track" ? "음원" : adminContentType === "video" ? "영상" : adminContentType === "picture" ? (adminContentSubject === "senses" ? "물품리스트" : "그림자료") : "계획안"} 목록
                  </div>
                  <button style={S.btn("primary")} onClick={() => setAdminEditItem({ id: `new_${Date.now()}`, title: "", duration: "", isNew: true, file: null })}>
                    {Icons.add} 개별 추가
                  </button>
                </div>

                {/* Bulk upload zone - different for video vs track/plan */}
                {adminContentType === "video" ? (
                  /* ── Video: YouTube link bulk input ── */
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>유튜브 링크 일괄 추가</div>
                    <textarea
                      style={{ ...S.input, minHeight: 80, resize: "vertical", fontSize: 12, lineHeight: 1.6 }}
                      placeholder={"유튜브 링크를 한 줄에 하나씩 붙여넣으세요\nhttps://www.youtube.com/watch?v=...\nhttps://youtu.be/..."}
                      value={bulkVideoLinks || ""}
                      onChange={e => setBulkVideoLinks(e.target.value)}
                    />
                    <button style={{ ...S.btn("primary"), width: "100%", marginTop: 8 }} onClick={async () => {
                      const links = (bulkVideoLinks || "").split("\n").map(l => l.trim()).filter(l => l.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/));
                      if (links.length === 0) return;

                      const newBulk = [];
                      for (let i = 0; i < links.length; i++) {
                        const url = links[i];
                        const ytId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/)?.[1];
                        let title = `영상 ${i + 1}`;
                        // Try to auto-fetch title
                        try {
                          const res = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${ytId}&format=json`);
                          const data = await res.json();
                          if (data.title) title = data.title;
                        } catch {}
                        newBulk.push({
                          id: `bulk_${Date.now()}_${i}`,
                          file: { name: `${ytId}.youtube`, size: 0, type: "video/youtube" },
                          title,
                          duration: "",
                          youtubeUrl: url,
                          ytId,
                        });
                      }
                      setBulkFiles(prev => [...prev, ...newBulk]);
                      setBulkVideoLinks("");
                    }}>
                      {Icons.add} 링크 추가 ({((bulkVideoLinks || "").split("\n").map(l => l.trim()).filter(l => l.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/))).length}개 감지)
                    </button>
                  </div>
                ) : (
                  /* ── Track / Plan: file drag-and-drop zone ── */
                <div
                  style={{
                    padding: dragOver ? "28px 16px" : "24px 16px", background: dragOver ? "#f0fdf4" : "#fafafa",
                    border: dragOver ? "2px dashed #22c55e" : "2px dashed #ddd", borderRadius: 14,
                    textAlign: "center", marginBottom: 16, transition: "all 0.2s", cursor: "pointer",
                  }}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOver(false);
                    const files = Array.from(e.dataTransfer.files);
                    const newBulk = files.map((f, i) => ({
                      id: `bulk_${Date.now()}_${i}`,
                      file: { name: f.name, size: f.size, type: f.type },
                      rawFile: f,
                      fileUrl: URL.createObjectURL(f),
                      title: f.name.replace(/\.[^/.]+$/, "").replace(/_/g, " "),
                      duration: "",
                    }));
                    setBulkFiles(prev => [...prev, ...newBulk]);
                    if (adminContentType === "track") {
                      files.forEach((f, i) => {
                        const bulkId = newBulk[i].id;
                        extractAudioDuration(f).then(dur => {
                          setBulkFiles(bfs => bfs.map(b => b.id === bulkId ? { ...b, duration: dur } : b));
                        });
                      });
                    }
                  }}
                  onClick={() => {
                    openFilePicker({
                      accept: adminContentType === "track" ? "*/*" : ".pdf,.ppt,.pptx,.hwp,.doc,.docx",
                      multiple: true,
                      onFiles: (files) => {
                        const newBulk = files.map((f, i) => ({
                          id: `bulk_${Date.now()}_${i}`,
                          file: { name: f.name, size: f.size, type: f.type },
                          rawFile: f,
                          fileUrl: URL.createObjectURL(f),
                          title: f.name.replace(/\.[^/.]+$/, "").replace(/_/g, " "),
                          duration: "",
                        }));
                        setBulkFiles(prev => [...prev, ...newBulk]);
                        if (adminContentType === "track") {
                          files.forEach((f, i) => {
                            const bulkId = newBulk[i].id;
                            extractAudioDuration(f).then(dur => {
                              setBulkFiles(bfs => bfs.map(b => b.id === bulkId ? { ...b, duration: dur } : b));
                            });
                          });
                        }
                      },
                    });
                  }}
                >
                  <span style={{ fontSize: 32, color: dragOver ? "#22c55e" : "#ccc" }}>↑</span>
                  <div style={{ fontSize: 13, fontWeight: 700, color: dragOver ? "#22c55e" : "#888", marginTop: 6 }}>
                    파일을 여기에 드래그하거나 터치하세요
                  </div>
                  <div style={{ fontSize: 11, color: "#bbb", marginTop: 4 }}>여러 파일을 한번에 올릴 수 있습니다</div>
                </div>
                )}

                {/* Bulk file staging list */}
                {bulkFiles.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>대기 중인 파일 ({bulkFiles.length}개)</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11 }} onClick={() => { setBulkFiles([]); setUploadStatus(null); }}>전체 삭제</button>
                        <button style={{ ...S.btn("primary"), opacity: uploadStatus?.type === "uploading" ? 0.6 : 1, pointerEvents: uploadStatus?.type === "uploading" ? "none" : "auto" }} onClick={async () => {
                          const prog = adminContentProgram;
                          const hasProgs = getProgramsBySubject(adminContentSubject);
                          const subj = adminContentSubject;
                          const issue = adminContentIssue;
                          const step = adminContentStep || (subjectSkipsStepsDyn(subj) ? "all" : null);
                          const total = bulkFiles.length;
                          // 기존 곡 뒤에 올린 순서대로 붙도록 시작 번호 계산
                          const existingTracks = (hasProgs && prog ? tracks?.[subj]?.[prog]?.[issue]?.[step] : tracks?.[subj]?.[issue]?.[step]) || [];
                          const baseTrackOrder = nextOrderOf(existingTracks);

                          setUploadStatus({ type: "uploading", current: 0, total, message: "업로드 준비 중..." });

                          let uploadCount = 0;
                          let errorCount = 0;
                          for (let idx = 0; idx < bulkFiles.length; idx++) {
                            const bf = bulkFiles[idx];
                            setUploadStatus({ type: "uploading", current: idx + 1, total, message: `"${bf.title}" 업로드 중... (${idx + 1}/${total})` });
                            // Mark individual file as uploading
                            setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, uploadState: "uploading" } : b));

                            try {
                            if (adminContentType === "track" && bf.rawFile) {
                                const storage = getStorage();
                                const fileName = bf.rawFile.name.replace(/[\s\/\\'"]/g, '_');
                                const storagePath = hasProgs && prog
                                  ? `${subj}/${prog}/tracks/${issue}/${step}/${fileName}`
                                  : `${subj}/tracks/${issue}/${step}/${fileName}`;
                                const fileRef = ref(storage, storagePath);
                                await uploadBytes(fileRef, bf.rawFile);

                                // Get permanent download URL
                                const downloadUrl = await getDownloadURL(fileRef);

                                // Use auto-extracted duration or manual input
                                const duration = bf.duration || "0:00";

                                await addTrackToFirestore({
                                  title: bf.title,
                                  duration,
                                  storagePath,
                                  downloadUrl,
                                  subject: subj,
                                  program: prog || null,
                                  issue,
                                  step,
                                  order: baseTrackOrder + idx,
                                });
                                uploadCount++;
                                setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, uploadState: "success" } : b));
                            } else if (adminContentType === "video") {
                              await addVideoToFirestore({
                                subject: subj, program: prog || null, issue, step,
                                title: bf.title, duration: bf.duration || "",
                                youtubeUrl: bf.youtubeUrl || "",
                              });
                              uploadCount++;
                              setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, uploadState: "success" } : b));
                            } else if (adminContentType === "plan" && bf.rawFile) {
                                const storage = getStorage();
                                const fileName = bf.rawFile.name.replace(/[\s\/\\'"]/g, '_');
                                const storagePath = hasProgs && prog
                                  ? `${subj}/${prog}/plans/${issue}/${step}/${fileName}`
                                  : `${subj}/plans/${issue}/${step}/${fileName}`;
                                const fileRef = ref(storage, storagePath);
                                await uploadBytes(fileRef, bf.rawFile);

                                await addPlanToFirestore({
                                  title: bf.title,
                                  storagePath,
                                  subject: subj,
                                  program: prog || null,
                                  issue,
                                  step,
                                });

                                uploadCount++;
                                setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, uploadState: "success" } : b));
                            } else if (adminContentType === "picture" && bf.rawFile) {
                                const storage = getStorage();
                                const fileName = bf.rawFile.name.replace(/[\s\/\\'"]/g, '_');
                                const storagePath = hasProgs && prog
                                  ? `${subj}/${prog}/pictures/${issue}/${step}/${fileName}`
                                  : `${subj}/pictures/${issue}/${step}/${fileName}`;
                                const fileRef = ref(storage, storagePath);
                                await uploadBytes(fileRef, bf.rawFile);

                                await addPictureToFirestore({
                                  title: bf.title,
                                  storagePath,
                                  subject: subj,
                                  program: prog || null,
                                  issue,
                                  step,
                                  order: idx,
                                });

                                uploadCount++;
                                setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, uploadState: "success" } : b));
                            }
                            } catch (err) {
                              console.error(`업로드 오류 (${bf.title}):`, err);
                              errorCount++;
                              setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, uploadState: "error" } : b));
                            }
                          }

                          console.log(`일괄 업로드 완료: ${uploadCount}/${total}개`);
                          if (adminContentType === "track") await loadTracksFromFirebase();
                          if (adminContentType === "plan") await loadPlansFromFirebase();
                          if (adminContentType === "picture") await loadPicturesFromFirebase();

                          if (errorCount === 0) {
                            setUploadStatus({ type: "success", current: uploadCount, total, message: `${uploadCount}개 파일 업로드 완료!` });
                            setTimeout(() => { setBulkFiles([]); setUploadStatus(null); }, 2000);
                          } else {
                            setUploadStatus({ type: "error", current: uploadCount, total, message: `${uploadCount}개 성공, ${errorCount}개 실패` });
                          }
                        }}>{uploadStatus?.type === "uploading" ? `업로드 중 (${uploadStatus.current}/${uploadStatus.total})` : "전체 업로드"}</button>
                      </div>
                    </div>
                    {/* Upload progress bar */}
                    {uploadStatus && (
                      <div style={{ marginBottom: 12, padding: "12px 14px", background: uploadStatus.type === "uploading" ? "#eff6ff" : uploadStatus.type === "success" ? "#f0fdf4" : "#fef2f2", borderRadius: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: uploadStatus.type === "uploading" ? 8 : 0 }}>
                          {uploadStatus.type === "uploading" && <span style={{ width: 16, height: 16, border: "2px solid #3b82f6", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />}
                          {uploadStatus.type === "success" && <span style={{ fontSize: 16, color: "#16a34a" }}>✓</span>}
                          {uploadStatus.type === "error" && <span style={{ fontSize: 16, color: "#dc2626" }}>⚠</span>}
                          <span style={{ fontSize: 12, fontWeight: 600, color: uploadStatus.type === "uploading" ? "#3b82f6" : uploadStatus.type === "success" ? "#16a34a" : "#dc2626" }}>{uploadStatus.message}</span>
                        </div>
                        {uploadStatus.type === "uploading" && (
                          <div style={{ height: 4, background: "#dbeafe", borderRadius: 2, overflow: "hidden" }}>
                            <div style={{ height: "100%", background: "#3b82f6", borderRadius: 2, transition: "width 0.3s", width: `${(uploadStatus.current / uploadStatus.total) * 100}%` }} />
                          </div>
                        )}
                      </div>
                    )}
                    {bulkFiles.map((bf, idx) => (
                      <div key={bf.id} style={{ ...S.card, display: "flex", alignItems: "center", gap: 10, marginBottom: 6, padding: "10px 14px", borderLeft: bf.uploadState === "success" ? "3px solid #22c55e" : bf.uploadState === "error" ? "3px solid #dc2626" : bf.uploadState === "uploading" ? "3px solid #3b82f6" : undefined, opacity: bf.uploadState === "success" ? 0.7 : 1 }}>
                        {adminContentType === "video" && bf.ytId ? (
                          <img src={`https://img.youtube.com/vi/${bf.ytId}/default.jpg`} alt="" style={{ width: 48, height: 36, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
                        ) : (
                        <span style={{ fontSize: 12, fontWeight: 800, width: 20, textAlign: "center", color: bf.uploadState === "success" ? "#22c55e" : bf.uploadState === "error" ? "#dc2626" : "#999" }}>
                          {bf.uploadState === "success" ? "✓" : bf.uploadState === "error" ? "✕" : bf.uploadState === "uploading" ? "…" : idx + 1}
                        </span>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <input style={{ ...S.input, marginBottom: 0, padding: "6px 10px", fontSize: 12 }} value={bf.title} onChange={e => { const v = e.target.value; setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, title: v } : b)); }} disabled={!!bf.uploadState} />
                          <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                            {bf.youtubeUrl ? (
                              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ background: "#dc2626", color: "#fff", fontSize: 9, padding: "1px 4px", borderRadius: 3, fontWeight: 700 }}>YT</span>
                                {bf.youtubeUrl.length > 45 ? bf.youtubeUrl.slice(0, 45) + "..." : bf.youtubeUrl}
                              </span>
                            ) : (
                              <>{bf.file.name} · {(bf.file.size / 1024 / 1024).toFixed(1)}MB</>
                            )}
                          </div>
                        </div>
                        {adminContentType === "track" && (
                          <input style={{ ...S.input, width: 70, marginBottom: 0, padding: "6px 8px", fontSize: 11, textAlign: "center", background: bf.duration ? "#f0fdf4" : undefined, borderColor: bf.duration ? "#22c55e" : undefined }} placeholder="0:00" value={bf.duration} onChange={e => { const v = e.target.value; setBulkFiles(bfs => bfs.map(b => b.id === bf.id ? { ...b, duration: v } : b)); }} disabled={!!bf.uploadState} />
                        )}
                        {!bf.uploadState && (
                          <>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <button style={{ background: "none", border: "none", fontSize: 12, cursor: "pointer", padding: 2, opacity: idx === 0 ? 0.3 : 1 }} disabled={idx === 0} onClick={() => setBulkFiles(bfs => { const n = [...bfs]; [n[idx], n[idx - 1]] = [n[idx - 1], n[idx]]; return n; })}>↑</button>
                          <button style={{ background: "none", border: "none", fontSize: 12, cursor: "pointer", padding: 2, opacity: idx === bulkFiles.length - 1 ? 0.3 : 1 }} disabled={idx === bulkFiles.length - 1} onClick={() => setBulkFiles(bfs => { const n = [...bfs]; [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]; return n; })}>↓</button>
                        </div>
                        <button style={{ background: "none", border: "none", fontSize: 14, cursor: "pointer", color: "#dc2626", padding: 4 }} onClick={() => setBulkFiles(bfs => bfs.filter(b => b.id !== bf.id))}>{Icons.close}</button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add/Edit form with file upload */}
                {adminEditItem && (
                  <div style={{ ...S.card, border: "1.5px solid #111", marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{adminEditItem.isNew ? "새 항목 추가" : "항목 수정"}</div>

                    {adminContentType === "video" ? (
                      /* ── Video: YouTube link-first UI ── */
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>YouTube 링크</div>
                          <input style={S.input} placeholder="https://www.youtube.com/watch?v=..." value={adminEditItem.youtubeUrl || ""} onChange={e => {
                            const url = e.target.value;
                            setAdminEditItem(ei => ({ ...ei, youtubeUrl: url }));
                            // Auto-fetch title from YouTube oEmbed
                            const ytId = (() => { const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/); return m ? m[1] : null; })();
                            if (ytId) {
                              fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${ytId}&format=json`)
                                .then(r => r.json())
                                .then(data => {
                                  setAdminEditItem(ei => {
                                    if (!ei) return ei;
                                    const updates = {};
                                    if (!ei.title || ei.title === "" || ei.titleAutoFilled) updates.title = data.title || ei.title;
                                    if (updates.title) updates.titleAutoFilled = true;
                                    return { ...ei, ...updates };
                                  });
                                })
                                .catch(() => {});
                            }
                          }} />
                          {adminEditItem.youtubeUrl && (() => { const m = adminEditItem.youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/); return m ? m[1] : null; })() && (
                            <div style={{ borderRadius: 10, overflow: "hidden", marginBottom: 8, position: "relative" }}>
                              <img src={`https://img.youtube.com/vi/${(() => { const m = adminEditItem.youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/); return m ? m[1] : ""; })()}/mqdefault.jpg`} alt="미리보기" style={{ width: "100%", height: "auto", display: "block" }} />
                              <div style={{ position: "absolute", top: 8, right: 8, background: "#dc2626", color: "#fff", fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>YouTube</div>
                            </div>
                          )}
                        </div>
                        <input style={S.input} placeholder="제목 (링크 입력 시 자동 감지)" value={adminEditItem.title} onChange={e => setAdminEditItem(ei => ({ ...ei, title: e.target.value, titleAutoFilled: false }))} />
                        {adminEditItem.titleAutoFilled && (
                          <div style={{ fontSize: 10, color: "#22c55e", marginTop: -8, marginBottom: 8 }}>✓ 제목 자동 감지됨 (수정 가능)</div>
                        )}
                        <input style={S.input} placeholder="재생시간 (예: 2:30, 선택사항)" value={adminEditItem.duration || ""} onChange={e => setAdminEditItem(ei => ({ ...ei, duration: e.target.value }))} />
                      </>
                    ) : (
                      /* ── Track / Plan: file upload UI ── */
                      <>
                    <input style={S.input} placeholder="제목" value={adminEditItem.title} onChange={e => setAdminEditItem(ei => ({ ...ei, title: e.target.value }))} />
                    {adminContentType === "track" && (
                      <div style={{ position: "relative" }}>
                        <input style={S.input} placeholder="재생시간 (예: 2:30)" value={adminEditItem.duration || ""} onChange={e => setAdminEditItem(ei => ({ ...ei, duration: e.target.value }))} />
                        {adminEditItem.duration && (
                          <div style={{ fontSize: 10, color: "#22c55e", marginTop: -8, marginBottom: 8 }}>✓ 재생시간 자동 감지됨 (수정 가능)</div>
                        )}
                      </div>
                    )}

                    {/* File upload area */}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>
                        {adminContentType === "track" ? "음원 파일 (MP3, WAV)" : adminContentType === "picture" ? `${adminContentSubject === "senses" ? "물품리스트" : "그림자료"} 파일 (PDF, PPT)` : "계획안 파일 (PDF, HWP, DOCX)"}
                      </div>
                      {adminEditItem.file ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#f0fdf4", border: "1.5px solid #22c55e", borderRadius: 10 }}>
                          <span style={{ fontSize: 20 }}>✓</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{adminEditItem.file.name}</div>
                            <div style={{ fontSize: 10, color: "#999" }}>{(adminEditItem.file.size / 1024 / 1024).toFixed(1)}MB</div>
                          </div>
                          <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "4px 10px" }} onClick={() => setAdminEditItem(ei => ({ ...ei, file: null, fileUrl: null }))}>삭제</button>
                        </div>
                      ) : (
                        <label style={{
                          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                          padding: "24px 16px", background: "#fafafa", border: "2px dashed #ddd",
                          borderRadius: 12, cursor: "pointer", transition: "all 0.2s",
                        }}>
                          <span style={{ fontSize: 28, color: "#ccc" }}>↑</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#999" }}>파일을 선택하세요</span>
                          <span style={{ fontSize: 10, color: "#bbb" }}>
                            {adminContentType === "track" ? "MP3, WAV (최대 50MB)" : adminContentType === "picture" ? "PDF, PPT, PPTX (최대 20MB)" : "PDF, HWP, DOCX (최대 20MB)"}
                          </span>
                          <input
                            type="file"
                            style={{ display: "none" }}
                            accept={adminContentType === "track" ? "*/*" : ".pdf,.ppt,.pptx,.hwp,.doc,.docx"}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const url = URL.createObjectURL(file);
                                setAdminEditItem(ei => ({ ...ei, file: { name: file.name, size: file.size, type: file.type }, fileUrl: url, rawFile: file }));
                                // Auto-extract duration for audio files
                                if (adminContentType === "track" && file.type.startsWith("audio/")) {
                                  extractAudioDuration(file).then(dur => {
                                    setAdminEditItem(ei => ei ? ({ ...ei, duration: dur }) : ei);
                                  });
                                }
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                      </>
                    )}

                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button style={S.btn("outline")} onClick={() => { setAdminEditItem(null); setSingleUploadStatus(null); }}>취소</button>
                      <button style={{ ...S.btn("primary"), opacity: singleUploadStatus === "uploading" ? 0.6 : 1, pointerEvents: singleUploadStatus === "uploading" ? "none" : "auto" }} onClick={async () => {
                        if (!adminEditItem.title) return;
                        setSingleUploadStatus("uploading");
                        const subj = adminContentSubject;
                        const issue = adminContentIssue;
                        const step = adminContentStep || (subjectSkipsStepsDyn(subj) ? "all" : null);
                        const prog = adminContentProgram;
                        const hasProgs = getProgramsBySubject(subj);

                        try {
                        if (adminContentType === "track") {
                          // Upload to Firebase Storage + save metadata to Firestore
                          if (adminEditItem.rawFile) {
                              const storage = getStorage();
                              const fileName = adminEditItem.rawFile.name.replace(/[\s\/\\'"]/g, '_');
                              const storagePath = hasProgs && prog
                                ? `${subj}/${prog}/tracks/${issue}/${step}/${fileName}`
                                : `${subj}/tracks/${issue}/${step}/${fileName}`;
                              const fileRef = ref(storage, storagePath);
                              await uploadBytes(fileRef, adminEditItem.rawFile);

                              // Get permanent download URL
                              const downloadUrl = await getDownloadURL(fileRef);
                              console.log("[관리자 업로드] storagePath:", storagePath, "| downloadUrl:", downloadUrl.slice(0, 60) + "...", "| step:", step);

                              // Auto-extract duration if not provided
                              let duration = adminEditItem.duration || "";
                              if (!duration || duration === "0:00") {
                                duration = await extractAudioDuration(adminEditItem.rawFile);
                              }

                              // Save metadata to Firestore
                              await addTrackToFirestore({
                                title: adminEditItem.title,
                                duration,
                                storagePath,
                                downloadUrl,
                                subject: subj,
                                program: prog || null,
                                issue,
                                step,
                                order: nextOrderOf((hasProgs && prog ? tracks?.[subj]?.[prog]?.[issue]?.[step] : tracks?.[subj]?.[issue]?.[step]) || []),
                              });

                              // Reload tracks
                              await loadTracksFromFirebase();
                          }
                        } else if (adminContentType === "video") {
                          // Save to Firestore
                          await addVideoToFirestore({
                            subject: subj,
                            program: prog || null,
                            issue: issue,
                            step: step,
                            title: adminEditItem.title,
                            duration: adminEditItem.duration || "",
                            youtubeUrl: adminEditItem.youtubeUrl || "",
                          });
                        } else if (adminContentType === "plan") {
                          // Upload plan to Firebase Storage + save metadata to Firestore
                          if (adminEditItem.rawFile) {
                              const storage = getStorage();
                              const fileName = adminEditItem.rawFile.name.replace(/[\s\/\\'"]/g, '_');
                              const storagePath = hasProgs && prog
                                ? `${subj}/${prog}/plans/${issue}/${step}/${fileName}`
                                : `${subj}/plans/${issue}/${step}/${fileName}`;
                              const fileRef = ref(storage, storagePath);
                              await uploadBytes(fileRef, adminEditItem.rawFile);
                              console.log("계획안 업로드 완료:", storagePath);

                              // Save metadata to Firestore
                              await addPlanToFirestore({
                                title: adminEditItem.title,
                                storagePath,
                                subject: subj,
                                program: prog || null,
                                issue,
                                step,
                              });

                              await loadPlansFromFirebase();
                          }
                        } else if (adminContentType === "picture") {
                          // Upload picture material to Firebase Storage + save metadata to Firestore
                          if (adminEditItem.rawFile) {
                              const storage = getStorage();
                              const fileName = adminEditItem.rawFile.name.replace(/[\s\/\\'"]/g, '_');
                              const storagePath = hasProgs && prog
                                ? `${subj}/${prog}/pictures/${issue}/${step}/${fileName}`
                                : `${subj}/pictures/${issue}/${step}/${fileName}`;
                              const fileRef = ref(storage, storagePath);
                              await uploadBytes(fileRef, adminEditItem.rawFile);
                              console.log("그림자료 업로드 완료:", storagePath);

                              await addPictureToFirestore({
                                title: adminEditItem.title,
                                storagePath,
                                subject: subj,
                                program: prog || null,
                                issue,
                                step,
                                order: 0,
                              });

                              await loadPicturesFromFirebase();
                          }
                        }
                        setSingleUploadStatus("success");
                        setTimeout(() => { setAdminEditItem(null); setSingleUploadStatus(null); }, 1500);
                        } catch (err) {
                          console.error("업로드 오류:", err);
                          setSingleUploadStatus("error");
                          setTimeout(() => setSingleUploadStatus(null), 3000);
                        }
                      }}>
                        {singleUploadStatus === "uploading" ? "업로드 중..." : adminEditItem.isNew ? "업로드" : "저장"}
                      </button>
                    </div>
                    {singleUploadStatus === "uploading" && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#eff6ff", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, border: "2px solid #3b82f6", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: "#3b82f6", fontWeight: 600 }}>Firebase에 업로드 중...</span>
                      </div>
                    )}
                    {singleUploadStatus === "success" && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>✓</span>
                        <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>업로드 완료!</span>
                      </div>
                    )}
                    {singleUploadStatus === "error" && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#fef2f2", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>✕</span>
                        <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>업로드 실패. 다시 시도해주세요.</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Uploaded items for this location */}
                {(() => {
                  const uploadedHere = Object.entries(adminUploadedFiles).filter(([_, f]) =>
                    f.subject === adminContentSubject && f.issue === adminContentIssue && f.step === adminContentStep && f.contentType === adminContentType
                  );
                  if (uploadedHere.length > 0) return (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#22c55e", marginBottom: 8 }}>업로드된 파일</div>
                      {uploadedHere.map(([id, f]) => (
                        <div key={id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", borderLeft: "3px solid #22c55e" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={S.cardTitle}>{f.title}</div>
                            <div style={S.cardMeta}>
                              {f.name} · {(f.size / 1024 / 1024).toFixed(1)}MB
                              {f.duration && ` · ${f.duration}`}
                              {` · ${f.uploadDate}`}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            {f.url && adminContentType === "track" && (
                              <button style={{ ...S.btn("sm"), fontSize: 11, padding: "4px 10px" }} onClick={() => {
                                const audio = new Audio(f.url);
                                audio.play();
                              }}>미리듣기</button>
                            )}
                            <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "4px 10px" }} onClick={() => {
                              setAdminUploadedFiles(uf => { const n = { ...uf }; delete n[id]; return n; });
                            }}>삭제</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                  return null;
                })()}

                {/* Existing sample items */}
                {adminContentType !== "plan" ? (
                  getAdminContentList().length === 0 && Object.entries(adminUploadedFiles).filter(([_, f]) => f.subject === adminContentSubject && f.issue === adminContentIssue && f.step === adminContentStep && f.contentType === adminContentType).length === 0 ? (
                    <div style={S.empty}>등록된 항목이 없습니다</div>
                  ) : (
                    getAdminContentList().map((item, idx, list) => {
                      const isEditing = adminEditItem?.id === item.id;
                      return (
                      <div key={item.id} style={{ ...S.card, display: "flex", flexDirection: "column", gap: 8 }}>
                        {isEditing ? (
                          // 편집 모드
                          <>
                            <input
                              style={{ ...S.input, fontSize: 13 }}
                              value={adminEditItem.title}
                              onChange={e => setAdminEditItem({ ...adminEditItem, title: e.target.value })}
                              placeholder="제목"
                              autoFocus
                            />
                            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                              <button style={{ ...S.btn("sm"), fontSize: 11, padding: "4px 12px" }} onClick={() => setAdminEditItem(null)}>
                                취소
                              </button>
                              <button style={{ ...S.btn("sm"), background: "#22c55e", color: "#fff", fontSize: 11, padding: "4px 12px" }} onClick={async () => {
                                const newTitle = adminEditItem.title?.trim();
                                if (!newTitle) {
                                  alert("제목을 입력해주세요");
                                  return;
                                }
                                const subj = adminContentSubject;
                                const prog = adminContentProgram;
                                const issue = adminContentIssue;
                                const step = adminContentStep;
                                const hasProgs = getProgramsBySubject(subj);
                                const updates = { title: newTitle };

                                // Firestore 업데이트
                                if (adminContentType === "track" && item.firestoreId) {
                                  const ok = await updateTrackInFirestore(item.firestoreId, updates);
                                  if (!ok) {
                                    alert("저장 실패. 다시 시도해주세요.");
                                    return;
                                  }
                                } else if (adminContentType === "video") {
                                  const fid = item.firestoreId || (typeof item.id === "string" && item.id.length > 15 ? item.id : null);
                                  if (fid) {
                                    const ok = await updateVideoInFirestore(fid, updates);
                                    if (!ok) {
                                      alert("저장 실패. 다시 시도해주세요.");
                                      return;
                                    }
                                  }
                                }

                                // 로컬 state 업데이트
                                const updateLocal = (prev) => {
                                  const u = JSON.parse(JSON.stringify(prev));
                                  const updateInArr = (arr) => {
                                    if (!Array.isArray(arr)) return arr;
                                    return arr.map(x => x.id === item.id ? { ...x, title: newTitle } : x);
                                  };
                                  if (hasProgs && prog) {
                                    if (u?.[subj]?.[prog]?.[issue]?.[step]) {
                                      u[subj][prog][issue][step] = updateInArr(u[subj][prog][issue][step]);
                                    }
                                  } else {
                                    if (u?.[subj]?.[issue]?.[step]) {
                                      u[subj][issue][step] = updateInArr(u[subj][issue][step]);
                                    }
                                  }
                                  return u;
                                };
                                if (adminContentType === "track") setTracks(updateLocal);
                                else if (adminContentType === "video") setVideos(updateLocal);
                                else if (adminContentType === "picture") setPictures(updateLocal);

                                setAdminEditItem(null);
                              }}>
                                저장
                              </button>
                            </div>
                          </>
                        ) : (
                          // 일반 모드
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                            {/* 순서 변경 ↑↓ */}
                            <div style={{ display: "flex", flexDirection: "column", flexShrink: 0, marginRight: 2 }}>
                              <button
                                style={{ background: "none", border: "none", fontSize: 13, lineHeight: 1, cursor: idx === 0 || reordering ? "default" : "pointer", color: "#888", padding: "2px 4px", opacity: idx === 0 || reordering ? 0.25 : 1 }}
                                disabled={idx === 0 || reordering}
                                title="위로"
                                onClick={() => reorderAdminItem(list, idx, -1)}
                              >▲</button>
                              <button
                                style={{ background: "none", border: "none", fontSize: 13, lineHeight: 1, cursor: idx === list.length - 1 || reordering ? "default" : "pointer", color: "#888", padding: "2px 4px", opacity: idx === list.length - 1 || reordering ? 0.25 : 1 }}
                                disabled={idx === list.length - 1 || reordering}
                                title="아래로"
                                onClick={() => reorderAdminItem(list, idx, 1)}
                              >▼</button>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={S.cardTitle}>{item.title}</div>
                              <div style={S.cardMeta}>
                                {item.duration && <span>{item.duration} </span>}
                                {item.youtubeUrl && <span style={{ background: "#fee2e2", color: "#dc2626", fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>YouTube</span>}
                                {item.storagePath && <span style={{ background: "#e0f2fe", color: "#0369a1", fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>Firebase</span>}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button style={{ ...S.btn("sm"), background: "#dbeafe", color: "#1e40af", fontSize: 11, padding: "4px 10px" }} onClick={() => {
                                setAdminEditItem({ id: item.id, title: item.title || "" });
                              }}>수정</button>
                              <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "4px 10px" }} onClick={async () => {
                                if (!window.confirm(`"${item.title}"\n\n정말 삭제하시겠습니까?\n삭제된 항목은 복구할 수 없습니다.`)) return;

                                const subj = adminContentSubject;
                                const prog = adminContentProgram;
                                const issue = adminContentIssue;
                                const step = adminContentStep;
                                const hasProgs = getProgramsBySubject(subj);

                                if (adminContentType === "track") {
                                  // Delete from Firestore if it has firestoreId
                                  if (item.firestoreId) {
                                    await deleteTrackFromFirestore(item.firestoreId);
                                  }
                                  // Always remove from local state
                                  setTracks(prev => {
                                    const u = JSON.parse(JSON.stringify(prev));
                                    if (hasProgs && prog) {
                                      const arr = u?.[subj]?.[prog]?.[issue]?.[step];
                                      if (Array.isArray(arr)) u[subj][prog][issue][step] = arr.filter(t => t.id !== item.id);
                                    } else {
                                      const arr = u?.[subj]?.[issue]?.[step];
                                      if (Array.isArray(arr)) u[subj][issue][step] = arr.filter(t => t.id !== item.id);
                                    }
                                    return u;
                                  });
                                } else if (adminContentType === "video") {
                                  // Delete from Firestore if it has firestoreId
                                  if (item.firestoreId || (item.id && typeof item.id === "string" && item.id.length > 15)) {
                                    await deleteVideoFromFirestore(item.firestoreId || item.id);
                                  }
                                  // Always remove from local state
                                  setVideos(prev => {
                                    const u = JSON.parse(JSON.stringify(prev));
                                    if (hasProgs && prog) {
                                      const arr = u?.[subj]?.[prog]?.[issue]?.[step];
                                      if (Array.isArray(arr)) u[subj][prog][issue][step] = arr.filter(v => v.id !== item.id);
                                    } else {
                                      const arr = u?.[subj]?.[issue]?.[step];
                                      if (Array.isArray(arr)) u[subj][issue][step] = arr.filter(v => v.id !== item.id);
                                    }
                                    return u;
                                  });
                                } else if (adminContentType === "picture") {
                                  // Delete file from Storage + doc from Firestore
                                  if (item.storagePath) {
                                    try { await deleteObject(ref(getStorage(), item.storagePath)); } catch (err) { console.error("그림자료 파일 삭제 오류:", err); }
                                  }
                                  if (item.firestoreId) {
                                    try {
                                      const db = getFirestore();
                                      await deleteDoc(doc(db, "pictures", item.firestoreId));
                                      console.log("그림자료 삭제 완료:", item.firestoreId);
                                    } catch (err) { console.error("그림자료 삭제 오류:", err); }
                                  }
                                  // Always remove from local state
                                  setPictures(prev => {
                                    const u = JSON.parse(JSON.stringify(prev));
                                    if (hasProgs && prog) {
                                      const arr = u?.[subj]?.[prog]?.[issue]?.[step];
                                      if (Array.isArray(arr)) u[subj][prog][issue][step] = arr.filter(p => p.id !== item.id);
                                    } else {
                                      const arr = u?.[subj]?.[issue]?.[step];
                                      if (Array.isArray(arr)) u[subj][issue][step] = arr.filter(p => p.id !== item.id);
                                    }
                                    return u;
                                  });
                                }
                              }}>삭제</button>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })
                  )
                ) : (
                  (() => {
                    const plansSubj = plans[adminContentSubject];
                    const hasProgs = getProgramsBySubject(adminContentSubject);
                    const skipsSteps = subjectSkipsStepsDyn(adminContentSubject);
                    // 계획안이 저장된 monthly 컨테이너(호 단위)를 가져옴
                    let issueContainer = null;
                    if (hasProgs && adminContentProgram) {
                      issueContainer = plansSubj?.[adminContentProgram]?.monthly?.[adminContentIssue];
                    } else if (!hasProgs) {
                      issueContainer = plansSubj?.monthly?.[adminContentIssue];
                    }
                    let planList = [];
                    if (issueContainer) {
                      if (skipsSteps) {
                        // 오감 등 STEP 없는 과목: 해당 호의 모든 step 계획안을 합침 (step="all" 등)
                        planList = Object.values(issueContainer).reduce((acc, v) => Array.isArray(v) ? [...acc, ...v] : (v ? [...acc, v] : acc), []);
                      } else {
                        const raw = issueContainer[adminContentStep];
                        planList = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                      }
                    }
                    const hasUploaded = Object.entries(adminUploadedFiles).filter(([_, f]) => f.subject === adminContentSubject && f.issue === adminContentIssue && (skipsSteps || f.step === adminContentStep) && f.contentType === "plan").length > 0;
                    return planList.length > 0 ? (
                      planList.map(plan => (
                      <div key={plan.id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.cardTitle}>{plan.title}</div>
                          <div style={S.cardMeta}>
                            {issueLabel(adminContentIssue, adminContentSubject)}{!subjectSkipsStepsDyn(adminContentSubject) && ` · ${STEPS.find(s => s.id === adminContentStep)?.label || ""}`}
                            {plan.storagePath && <span style={{ background: "#e0f2fe", color: "#0369a1", fontSize: 10, padding: "1px 5px", borderRadius: 4, fontWeight: 700, marginLeft: 4 }}>Firebase</span>}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "4px 10px" }} onClick={async () => {
                            if (!window.confirm(`"${plan.title}"\n\n정말 삭제하시겠습니까?`)) return;
                            const subj = adminContentSubject;
                            const prog = adminContentProgram;
                            const issue = adminContentIssue;
                            const hasProgs = getProgramsBySubject(subj);

                            // Storage 파일 삭제
                            if (plan.storagePath) {
                              try { await deleteObject(ref(getStorage(), plan.storagePath)); } catch (err) { console.error("계획안 파일 삭제 오류:", err); }
                            }
                            // Firestore 문서 삭제
                            if (plan.firestoreId) {
                              try {
                                const db = getFirestore();
                                await deleteDoc(doc(db, "plans", plan.firestoreId));
                                console.log("계획안 삭제 완료:", plan.firestoreId);
                              } catch (err) { console.error("계획안 삭제 오류:", err); }
                            }
                            // 로컬 state에서 해당 항목 제거: step을 몰라도 되도록 호 컨테이너의 모든 step에서 id로 찾아 제거
                            setPlans(prev => {
                              const u = JSON.parse(JSON.stringify(prev));
                              const container = (hasProgs && prog) ? u?.[subj]?.[prog]?.monthly?.[issue] : u?.[subj]?.monthly?.[issue];
                              if (container) {
                                for (const stepKey of Object.keys(container)) {
                                  if (Array.isArray(container[stepKey])) {
                                    container[stepKey] = container[stepKey].filter(x => x.id !== plan.id);
                                    if (container[stepKey].length === 0) delete container[stepKey];
                                  } else if (container[stepKey] && (container[stepKey].id === plan.id)) {
                                    delete container[stepKey]; // 구버전 단일 객체 호환
                                  }
                                }
                              }
                              return u;
                            });
                          }}>삭제</button>
                        </div>
                      </div>
                      ))
                    ) : !hasUploaded ? (
                      <div style={S.empty}>등록된 계획안이 없습니다</div>
                    ) : null;
                  })()
                )}
              </>
            )}
            </>
            )}
          </div>
        )}

        {/* ── Event Management ── */}
        {adminTab === "event" && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>행사 관리</div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 6 }}>과목</div>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
                {subjects.map(s => (
                  <button key={s.id} style={S.pill(adminEventSubject === s.id)} onClick={() => setAdminEventSubject(s.id)}>
                    {s.icon} {s.name}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>등록된 행사</div>
              <button style={S.btn("primary")} onClick={() => setAdminEditItem({ id: null, title: "", themeEmoji: "🎉", themeColor: "#3b82f6", themeBg: "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #1e3a5f 60%, #3b82f6 100%)", isNew: true })}>
                {Icons.add} 행사 추가
              </button>
            </div>

            {adminEditItem && (
              <div style={{ ...S.card, border: "1.5px solid #111", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{adminEditItem.isNew ? "새 행사 등록" : "행사 수정"}</div>

                <div style={{ display: "flex", gap: 8, marginBottom: 8, position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => setEmojiPickerOpen(o => !o)}
                    style={{
                      width: 70, height: 44, fontSize: 26, padding: 0,
                      background: "#fff", border: "1px solid #ddd", borderRadius: 8,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}
                    title="이모지 선택"
                  >
                    {adminEditItem.themeEmoji || "🎉"}
                  </button>
                  <input
                    style={{ ...S.input, flex: 1, marginBottom: 0 }}
                    placeholder="행사명"
                    value={adminEditItem.title}
                    onChange={e => setAdminEditItem(ei => ({ ...ei, title: e.target.value }))}
                  />

                  {/* 이모지 피커 팝오버 */}
                  {emojiPickerOpen && (
                    <div style={{
                      position: "absolute", top: 50, left: 0, right: 0, zIndex: 50,
                      background: "#fff", border: "1px solid #ddd", borderRadius: 12,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 10,
                    }}>
                      {/* 카테고리 탭 */}
                      <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 8, marginBottom: 8, borderBottom: "1px solid #f0f0f0" }}>
                        {EVENT_EMOJI_CATEGORIES.map((cat, idx) => (
                          <button
                            key={cat.name}
                            type="button"
                            onClick={() => setEmojiCategoryIdx(idx)}
                            style={{
                              padding: "4px 10px", fontSize: 11, fontWeight: 600,
                              background: emojiCategoryIdx === idx ? "#111" : "#f5f5f5",
                              color: emojiCategoryIdx === idx ? "#fff" : "#666",
                              border: "none", borderRadius: 14, cursor: "pointer",
                              flexShrink: 0, whiteSpace: "nowrap",
                            }}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>

                      {/* 이모지 그리드 */}
                      <div style={{
                        display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4,
                        maxHeight: 180, overflowY: "auto",
                      }}>
                        {EVENT_EMOJI_CATEGORIES[emojiCategoryIdx].emojis.map(em => (
                          <button
                            key={em}
                            type="button"
                            onClick={() => {
                              setAdminEditItem(ei => ({ ...ei, themeEmoji: em }));
                              setEmojiPickerOpen(false);
                            }}
                            style={{
                              width: "100%", aspectRatio: "1 / 1", fontSize: 22,
                              background: adminEditItem.themeEmoji === em ? "#fef3c7" : "transparent",
                              border: adminEditItem.themeEmoji === em ? "2px solid #f59e0b" : "1px solid transparent",
                              borderRadius: 8, cursor: "pointer", padding: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "background 0.15s",
                            }}
                            onMouseOver={e => { if (adminEditItem.themeEmoji !== em) e.currentTarget.style.background = "#f5f5f5"; }}
                            onMouseOut={e => { if (adminEditItem.themeEmoji !== em) e.currentTarget.style.background = "transparent"; }}
                          >
                            {em}
                          </button>
                        ))}
                      </div>

                      {/* 닫기 버튼 */}
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0", textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={() => setEmojiPickerOpen(false)}
                          style={{ fontSize: 11, color: "#666", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
                        >
                          닫기
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <label style={{ fontSize: 11, color: "#666", flexShrink: 0 }}>테마 색상</label>
                  <input
                    type="color"
                    value={adminEditItem.themeColor || "#3b82f6"}
                    onChange={e => {
                      const c = e.target.value;
                      // 색상 변경 시 그라디언트도 자동 생성
                      const dark = c + "44"; // 살짝 어둡게
                      setAdminEditItem(ei => ({
                        ...ei,
                        themeColor: c,
                        themeBg: `linear-gradient(135deg, #1a1a2e 0%, ${c}88 50%, ${c} 100%)`,
                      }));
                    }}
                    style={{ width: 40, height: 32, padding: 0, border: "1px solid #ddd", borderRadius: 6, cursor: "pointer" }}
                  />
                  <div style={{ flex: 1, height: 32, borderRadius: 6, background: adminEditItem.themeBg || "#666" }} />
                </div>

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                  <button style={S.btn("outline")} onClick={() => { setAdminEditItem(null); setEmojiPickerOpen(false); }}>취소</button>
                  <button style={S.btn("primary")} onClick={async () => {
                    const ok = await saveEventToFirestore(adminEditItem, adminEditItem.isNew);
                    if (ok) { setAdminEditItem(null); setEmojiPickerOpen(false); }
                  }}>{adminEditItem.isNew ? "등록" : "저장"}</button>
                </div>
              </div>
            )}

            {getAdminEvents().length === 0 ? (
              <div style={S.empty}>등록된 행사가 없습니다</div>
            ) : (
              getAdminEvents().map(ev => {
                const ec = eventContent[ev.id] || {};
                const tCount = (ec.tracks?.length ?? ev.tracks?.length) || 0;
                const vCount = (ec.videos?.length ?? ev.videos?.length) || 0;
                const pCount = (ec.plans?.length ?? ev.plans?.length) || 0;
                const counts = [
                  tCount + "곡",
                  vCount + "영상",
                  pCount + "계획안",
                ].filter(x => !x.startsWith("0")).join(" · ") || "자료 없음";
                return (
                  <div key={ev.id} style={{ ...S.card, marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.cardTitle}>{ev.themeEmoji} {ev.title}</div>
                        <div style={S.cardMeta}>{counts}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 10px" }}
                          onClick={() => { setSelectedEvent(ev); setEventTab("tracks"); setEventEditItem(null); }}>
                          콘텐츠 관리
                        </button>
                        <button style={{ ...S.btn("sm"), fontSize: 11, padding: "4px 10px" }} onClick={() => setAdminEditItem({ ...ev, isNew: false })}>수정</button>
                        <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "4px 10px" }}
                          onClick={() => deleteEventFromFirestore(ev)}>삭제</button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── User Management ── */}
        {adminTab === "users" && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>사용자 관리</div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 20 }}>강사 가입 승인 및 관리</div>

            {/* Pending approvals */}
            {(() => {
              const pending = registeredUsers.filter(u => u.status === "pending");
              if (pending.length === 0) return null;
              return (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    승인 대기 <span style={{ ...S.badge("#fee2e2"), color: "#dc2626" }}>{pending.length}</span>
                  </div>
                  {pending.map(user => (
                    <div key={user.id} style={{ ...S.card, borderLeft: "3px solid #f59e0b" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={S.cardTitle}>{user.name}</div>
                          <div style={S.cardMeta}>{user.phone} · {user.subject} · {user.date}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button style={{ ...S.btn("primary"), fontSize: 11, padding: "6px 14px" }} onClick={async () => { await updateUserInFirestore(user.firestoreId, { status: "approved" }); setRegisteredUsers(us => us.map(u => u.firestoreId === user.firestoreId ? { ...u, status: "approved" } : u)); }}>승인</button>
                          <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "6px 10px" }} onClick={async () => { await updateUserInFirestore(user.firestoreId, { status: "rejected" }); setRegisteredUsers(us => us.map(u => u.firestoreId === user.firestoreId ? { ...u, status: "rejected" } : u)); }}>거절</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Approved users */}
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>승인된 사용자 ({registeredUsers.filter(u => u.status === "approved").length})</div>
            {registeredUsers.filter(u => u.status === "approved").map(user => (
              <div key={user.id} style={{ ...S.card, borderLeft: user.role === "admin" ? "3px solid #3b82f6" : "3px solid #22c55e" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ ...S.cardTitle, display: "flex", alignItems: "center", gap: 6 }}>
                      {user.name}
                      {user.role === "admin" && <span style={{ fontSize: 9, background: "#3b82f6", color: "#fff", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>관리자</span>}
                    </div>
                    <div style={S.cardMeta}>{user.email} · {user.phone} · {user.subject}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button style={{ ...S.btn("sm"), fontSize: 10, padding: "4px 8px", background: user.role === "admin" ? "#dbeafe" : "#f5f5f5", color: user.role === "admin" ? "#3b82f6" : "#666" }}
                      onClick={async () => { const newRole = user.role === "admin" ? "teacher" : "admin"; await updateUserInFirestore(user.firestoreId, { role: newRole }); setRegisteredUsers(us => us.map(u => u.firestoreId === user.firestoreId ? { ...u, role: newRole } : u)); }}>
                      {user.role === "admin" ? "권한 해제" : "관리자 부여"}
                    </button>
                    <button style={{ ...S.btn("sm"), background: "#fee2e2", color: "#dc2626", fontSize: 10, padding: "4px 8px" }} onClick={async () => { await updateUserInFirestore(user.firestoreId, { status: "rejected" }); setRegisteredUsers(us => us.map(u => u.firestoreId === user.firestoreId ? { ...u, status: "rejected" } : u)); }}>차단</button>
                  </div>
                </div>
              </div>
            ))}

            {/* Rejected/blocked users */}
            {registeredUsers.filter(u => u.status === "rejected").length > 0 && (
              <>
                <div style={{ ...S.divider }} />
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10, color: "#999" }}>차단/거절된 사용자</div>
                {registeredUsers.filter(u => u.status === "rejected").map(user => (
                  <div key={user.id} style={{ ...S.card, opacity: 0.6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={S.cardTitle}>{user.name}</div>
                        <div style={S.cardMeta}>{user.phone} · {user.subject}</div>
                      </div>
                      <button style={{ ...S.btn("sm"), fontSize: 11, padding: "4px 10px" }} onClick={async () => { await updateUserInFirestore(user.firestoreId, { status: "approved" }); setRegisteredUsers(us => us.map(u => u.firestoreId === user.firestoreId ? { ...u, status: "approved" } : u)); }}>복구</button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Statistics ── */}
        {adminTab === "stats" && (
          <div>
            {/* ── 방문 통계 (강사별 앱 사용 현황) ── */}
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>강사 앱 사용 통계</div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>강사들이 앱을 실행한 횟수와 사용한 날짜를 집계합니다</div>

            {(() => {
              const todayStr = (() => {
                const n = new Date();
                return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`;
              })();
              // 강사별 집계: 사용 횟수, 사용일 수, 마지막 사용일
              const byUser = {};
              visitStats.forEach(v => {
                const key = v.userId || v.userName || v.id;
                if (!byUser[key]) byUser[key] = { name: v.userName || "이름없음", days: 0, totalCount: 0, lastDate: "" };
                byUser[key].days += 1;
                byUser[key].totalCount += (v.count || 1);
                if ((v.date || "") > byUser[key].lastDate) byUser[key].lastDate = v.date || "";
              });
              // 사용 횟수 많은 순 (같으면 사용일 수 순)
              const userList = Object.values(byUser).sort((a, b) => (b.totalCount - a.totalCount) || (b.days - a.days));
              const todayVisitors = visitStats.filter(v => v.date === todayStr).length;
              const totalUsage = visitStats.reduce((sum, v) => sum + (v.count || 1), 0);
              const uniqueUsers = userList.length;

              return (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
                    {[
                      { label: "총 사용 횟수", value: totalUsage, icon: "📊" },
                      { label: "오늘 사용 강사", value: todayVisitors, icon: "📅" },
                      { label: "이용 강사", value: uniqueUsers, icon: "👩‍🏫" },
                    ].map((stat, i) => (
                      <div key={i} style={{ ...S.card, textAlign: "center", padding: "16px 8px" }}>
                        <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
                        <div style={{ fontSize: 22, fontWeight: 900 }}>{stat.value}</div>
                        <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>강사별 사용 현황</div>
                  {visitStatsLoading ? (
                    <div style={S.empty}>불러오는 중...</div>
                  ) : userList.length === 0 ? (
                    <div style={S.empty}>아직 사용 기록이 없습니다<br/><span style={{ fontSize: 11 }}>강사가 앱에 접속하면 여기에 기록됩니다</span></div>
                  ) : (
                    <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
                      {/* 헤더 */}
                      <div style={{ display: "flex", padding: "10px 14px", background: "#f9f9f9", fontSize: 11, fontWeight: 700, color: "#888", borderBottom: "1px solid #eee" }}>
                        <span style={{ flex: 1 }}>강사</span>
                        <span style={{ width: 64, textAlign: "center" }}>사용 횟수</span>
                        <span style={{ width: 52, textAlign: "center" }}>사용일</span>
                        <span style={{ width: 88, textAlign: "right" }}>마지막 사용</span>
                      </div>
                      {userList.map((u, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "center", padding: "12px 14px", borderBottom: idx < userList.length - 1 ? "1px solid #f2f2f2" : "none" }}>
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{u.name}</span>
                          <span style={{ width: 64, textAlign: "center", fontSize: 15, fontWeight: 900 }}>{u.totalCount}회</span>
                          <span style={{ width: 52, textAlign: "center", fontSize: 12, color: "#666" }}>{u.days}일</span>
                          <span style={{ width: 88, textAlign: "right", fontSize: 11, color: "#999" }}>{u.lastDate || "-"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            <div style={S.divider} />

            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>콘텐츠 활용 통계</div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 20 }}>강사들의 콘텐츠 사용 현황을 확인합니다</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 24 }}>
              {[
                { label: "총 재생 수", value: Object.values(playCounts).reduce((a, b) => a + b, 0), icon: "▶" },
                { label: "재생된 곡", value: Object.keys(playCounts).length, icon: "♫" },
                { label: "다운로드", value: downloadedFiles.length, icon: "↓" },
              ].map((stat, i) => (
                <div key={i} style={{ ...S.card, textAlign: "center", padding: "16px 8px" }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{stat.value}</div>
                  <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>인기 음원 TOP 10</div>
            {(() => {
              const sorted = Object.entries(playCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);
              if (sorted.length === 0) return <div style={S.empty}>아직 재생 기록이 없습니다<br/><span style={{ fontSize: 11 }}>강사가 음원을 재생하면 여기에 기록됩니다</span></div>;
              const maxCount = sorted[0][1];
              return sorted.map(([trackId, count], idx) => {
                const track = allTracksFlat.find(t => t.id === trackId);
                const title = track?.title || adminUploadedFiles[trackId]?.title || trackId;
                const subj = track?.subject || adminUploadedFiles[trackId]?.subject;
                const subjName = subjects.find(s => s.id === subj)?.name || "";
                const pct = (count / maxCount) * 100;
                return (
                  <div key={trackId} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 900, color: idx < 3 ? "#111" : "#999", width: 24, textAlign: "center" }}>{idx + 1}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                        <div style={{ fontSize: 10, color: "#999" }}>{subjName}</div>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{count}회</span>
                    </div>
                    <div style={{ marginLeft: 34, height: 6, background: "#f5f5f5", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: idx === 0 ? "#111" : idx === 1 ? "#555" : idx === 2 ? "#999" : "#ccc", borderRadius: 3, transition: "width 0.3s" }} />
                    </div>
                  </div>
                );
              });
            })()}

            <div style={S.divider} />
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>과목별 재생 현황</div>
            {(() => {
              const subjCounts = {};
              Object.entries(playCounts).forEach(([trackId, count]) => {
                const track = allTracksFlat.find(t => t.id === trackId);
                const subj = track?.subject || adminUploadedFiles[trackId]?.subject || "unknown";
                subjCounts[subj] = (subjCounts[subj] || 0) + count;
              });
              const totalPlays = Object.values(subjCounts).reduce((a, b) => a + b, 0) || 1;
              const sorted = Object.entries(subjCounts).sort((a, b) => b[1] - a[1]);
              if (sorted.length === 0) return <div style={S.empty}>아직 재생 기록이 없습니다</div>;
              return sorted.map(([subjId, count]) => {
                const subj = subjects.find(s => s.id === subjId);
                const pct = (count / totalPlays) * 100;
                return (
                  <div key={subjId} style={{ ...S.card, padding: "12px 14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{subj?.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>{subj?.name || subjId}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>{count}회 ({Math.round(pct)}%)</span>
                    </div>
                    <div style={{ height: 8, background: "#f5f5f5", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "#111", borderRadius: 4, transition: "width 0.3s" }} />
                    </div>
                  </div>
                );
              });
            })()}

            <div style={S.divider} />
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>최근 다운로드된 파일</div>
            {downloadedFiles.length === 0 ? (
              <div style={S.empty}>다운로드된 파일이 없습니다</div>
            ) : (
              downloadedFiles.slice(-10).reverse().map(fileId => {
                const track = allTracksFlat.find(t => t.id === fileId);
                const uploaded = adminUploadedFiles[fileId];
                const title = track?.title || uploaded?.title || fileId;
                const subj = track?.subject || uploaded?.subject;
                return (
                  <div key={fileId} style={{ ...S.card, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
                    <span style={{ fontSize: 14, color: "#22c55e" }}>✓</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{title}</div>
                      <div style={{ fontSize: 10, color: "#999" }}>{subjects.find(s => s.id === subj)?.name || ""}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Modals ─────────────────────────────────────────────────────────────────

  // ─── YouTube Helper ────────────────────────────────────────────────────────
  const getYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  // Reusable video card component
  const renderVideoCard = (v, color) => {
    const ytId = getYoutubeId(v.youtubeUrl);
    return (
      <div key={v.id} style={{ ...S.card, cursor: v.youtubeUrl ? "pointer" : "default", overflow: "hidden", padding: 0 }} onClick={() => v.youtubeUrl && setPlayingVideo(v)}>
        {ytId && (
          <div style={{ width: "100%", height: 0, paddingBottom: "56.25%", position: "relative", background: "#000" }}>
            <img
              src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
              alt={v.title}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.2)" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20 }}>▶</div>
            </div>
          </div>
        )}
        <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          {!ytId && (
            <div style={{ width: 48, height: 48, background: color || "#111", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18, flexShrink: 0 }}>{Icons.video}</div>
          )}
          <div style={{ flex: 1 }}>
            <div style={S.cardTitle}>{v.title}</div>
            <div style={S.cardMeta}>
              {v.duration}
              {v.youtubeUrl && <span> · YouTube</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Video player modal
  const renderVideoPlayer = () => {
    if (!playingVideo) return null;
    const ytId = getYoutubeId(playingVideo.youtubeUrl);
    if (!ytId) return null;
    return (
      <div style={S.overlay} onClick={() => setPlayingVideo(null)}>
        <div style={{ background: "#000", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 480, overflow: "hidden" }} onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#111" }}>
            <div style={{ color: "#fff", fontSize: 14, fontWeight: 700, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{playingVideo.title}</div>
            <button style={{ ...touch44, background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: 20, cursor: "pointer", flexShrink: 0 }} onClick={() => setPlayingVideo(null)}>{Icons.close}</button>
          </div>
          <div style={{ width: "100%", height: 0, paddingBottom: "56.25%", position: "relative" }}>
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
              title={playingVideo.title}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    );
  };

  // ─── Plan/Document helpers ──────────────────────────────────────────────────
  const getFileType = (plan) => {
    // fileUrl이 아직 없으면 storagePath로 확장자 판별 (그림자료/악보는 로드시 fileUrl=null)
    const src = (plan?.fileUrl || plan?.storagePath || "").toLowerCase();
    if (!src) return "pdf"; // default
    if (src.endsWith(".pptx") || src.endsWith(".ppt")) return "ppt";
    if (src.endsWith(".hwp")) return "hwp";
    if (src.endsWith(".docx") || src.endsWith(".doc")) return "docx";
    if (src.endsWith(".png") || src.endsWith(".jpg") || src.endsWith(".jpeg") || src.endsWith(".gif") || src.endsWith(".webp")) return "image";
    return "pdf";
  };

  const fileTypeLabel = { pdf: "PDF", ppt: "PPT", hwp: "HWP", docx: "DOCX", image: "IMG" };
  const fileTypeColor = { pdf: "#dc2626", ppt: "#f59e0b", hwp: "#3b82f6", docx: "#6366f1", image: "#16a34a" };

  const renderPlanCard = (plan, meta) => {
    const ftype = getFileType(plan);
    const hasFile = !!(plan.fileUrl || plan.storagePath);
    const uploaded = adminUploadedFiles[plan.id];
    const localUrl = plan.fileUrl || uploaded?.url;

    return (
      <div key={plan.id} style={{ ...S.card, cursor: hasFile ? "pointer" : "default" }} onClick={async () => {
        let url = localUrl;
        // If no local URL but has Firebase Storage path, fetch it
        if (!url && plan.storagePath) {
          try {
            const storage = getStorage();
            const fileRef = ref(storage, plan.storagePath);
            url = await getDownloadURL(fileRef);
          } catch (err) {
            console.error("계획안 URL 가져오기 오류:", err);
          }
        }
        if (url) setViewingDoc({ title: plan.title, fileUrl: url, fileType: ftype });
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 44, height: 44, background: fileTypeColor[ftype] || "#111", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>{fileTypeLabel[ftype]}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={S.cardTitle}>{plan.title}</div>
            <div style={S.cardMeta}>
              {meta || ""}
              {hasFile && <span> · 터치하여 열기</span>}
              {plan.storagePath && <span style={{ background: "#e0f2fe", color: "#0369a1", fontSize: 10, padding: "1px 5px", borderRadius: 4, fontWeight: 700, marginLeft: 4 }}>Firebase</span>}
            </div>
          </div>
          {hasFile && <span style={{ fontSize: 16, color: "#999" }}>→</span>}
        </div>
      </div>
    );
  };

  // Document viewer modal
  const renderDocViewer = () => {
    if (!viewingDoc) return null;
    const { title, fileUrl, fileType } = viewingDoc;

    let viewerContent;
    if (fileType === "pdf") {
      // PDF: use native browser viewer with toolbar
      viewerContent = (
        <iframe src={`${fileUrl}#toolbar=1&navpanes=0`} title={title} style={{ width: "100%", height: "100%", border: "none" }} />
      );
    } else if (fileType === "image") {
      // 이미지 악보: 이미지를 보여주고 다운로드 버튼 제공
      viewerContent = (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ flex: 1, overflow: "auto", background: "#f5f5f5", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 16 }}>
            <img src={fileUrl} alt={title} style={{ maxWidth: "100%", height: "auto", borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }} />
          </div>
          <div style={{ padding: 12, borderTop: "1px solid #eee", flexShrink: 0 }}>
            <a href={fileUrl} download target="_blank" rel="noopener noreferrer" style={{ ...S.btn("primary"), textDecoration: "none", display: "block", textAlign: "center", padding: "12px 0", fontSize: 14 }}>이미지 다운로드</a>
          </div>
        </div>
      );
    } else if (fileType === "docx") {
      // DOCX: use Microsoft Office Online viewer
      viewerContent = (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
          title={title}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      );
    } else if (fileType === "ppt") {
      // PPT/PPTX: Office Online 뷰어가 Firebase Storage URL과 호환되지 않는 경우가 많아
      // 다운로드하여 기기의 오피스 앱으로 열도록 안내
      viewerContent = (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 40 }}>
          <div style={{ fontSize: 48 }}>📊</div>
          <div style={{ fontSize: 16, fontWeight: 700, textAlign: "center" }}>{title}</div>
          <div style={{ fontSize: 13, color: "#999", textAlign: "center", lineHeight: 1.6 }}>
            PPT 파일은 앱 내 미리보기가 불안정하여<br/>
            다운로드 후 확인하시는 것을 권장합니다.
          </div>
          <a href={fileUrl} download target="_blank" rel="noopener noreferrer" style={{ ...S.btn("primary"), textDecoration: "none", padding: "12px 28px", fontSize: 14 }}>파일 다운로드 / 열기</a>
        </div>
      );
    } else {
      // HWP or unsupported: show download prompt
      viewerContent = (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 40 }}>
          <div style={{ fontSize: 48 }}>📄</div>
          <div style={{ fontSize: 16, fontWeight: 700, textAlign: "center" }}>{title}</div>
          <div style={{ fontSize: 13, color: "#999", textAlign: "center", lineHeight: 1.6 }}>
            이 파일 형식은 앱 내 미리보기를 지원하지 않습니다.<br/>
            다운로드하여 확인해주세요.
          </div>
          <a href={fileUrl} download style={{ ...S.btn("primary"), textDecoration: "none", padding: "12px 28px", fontSize: 14 }}>파일 다운로드</a>
        </div>
      );
    }

    return (
      <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 300, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #eee", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <div style={{ width: 28, height: 28, background: fileTypeColor[fileType] || "#111", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>{fileTypeLabel[fileType]}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
          </div>
          <button style={{ ...touch44, background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#111", flexShrink: 0 }} onClick={() => setViewingDoc(null)}>
            {Icons.close}
          </button>
        </div>
        {/* Viewer */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {viewerContent}
        </div>
      </div>
    );
  };

  // ─── Global Search Overlay ──────────────────────────────────────────────────
  const renderGlobalSearch = () => {
    if (!showGlobalSearch) return null;
    const results = getGlobalSearchResults();
    const totalResults = results.tracks.length + results.videos.length + results.plans.length;
    const hasQuery = globalSearchTerm.trim().length > 0;

    return (
      <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 300, display: "flex", flexDirection: "column" }}>
        {/* Search header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #eee" }}>
          <button style={{ ...touch44, background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#111", flexShrink: 0 }} onClick={() => setShowGlobalSearch(false)}>
            {Icons.back}
          </button>
          <input
            ref={searchInputRef}
            style={{ flex: 1, padding: "12px 16px", border: "1.5px solid #111", borderRadius: 12, fontSize: 14, fontFamily: font, outline: "none" }}
            placeholder="음원, 영상, 계획안 검색... (Search)"
            value={globalSearchTerm}
            onChange={e => setGlobalSearchTerm(e.target.value)}
          />
          {globalSearchTerm && (
            <button style={{ ...touch44, background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#999", flexShrink: 0 }} onClick={() => setGlobalSearchTerm("")}>
              {Icons.close}
            </button>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 40px" }}>
          {!hasQuery ? (
            <div style={S.empty}>검색어를 입력해주세요</div>
          ) : totalResults === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>{Icons.searchLg}</div>
              '{globalSearchTerm}' 에 대한 결과가 없습니다
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: "#999", marginBottom: 16, fontWeight: 500 }}>총 {totalResults}개 결과</div>

              {results.tracks.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>♫</span> 음원 ({results.tracks.length})
                  </div>
                  {results.tracks.map(t => renderTrackItem(t, true))}
                </div>
              )}

              {results.videos.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>▶</span> 영상 ({results.videos.length})
                  </div>
                  {results.videos.map(v => renderVideoCard(v, "#111"))}
                </div>
              )}

              {results.plans.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>📋</span> 계획안 ({results.plans.length})
                  </div>
                  {results.plans.map(p => renderPlanCard(p, `${subjects.find(s => s.id === p.subject)?.name}${p.yearly ? " · 연간" : ` · ${issueLabel(p.issue, p.subject)}`} · ${STEPS.find(s => s.id === p.step)?.label}`))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── Auth Screens ──────────────────────────────────────────────────────────
  const renderAuthScreen = () => (
    <div key="auth-screen" style={S.app}>

      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
        <div style={{ width: 72, height: 72, background: "#111", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          <Logo color="#fff" size={44} />
        </div>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 2, letterSpacing: -1 }}>울림교육</div>
        <div style={{ fontSize: 11, color: "#999", letterSpacing: 3, marginBottom: 8 }}>EDUCATION LAB</div>
        <div style={{ fontSize: 12, color: "#bbb", marginBottom: 36 }}>울림티쳐 - 강사 전용 앱</div>

        {authScreen === "login" ? (
          <div style={{ width: "100%", maxWidth: 320 }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20, textAlign: "center" }}>로그인 <span style={{ fontSize: 12, fontWeight: 400, color: "#999" }}>Sign In</span></div>

            <input style={S.input} type="email" placeholder="이메일 (Email)" value={loginEmail} onChange={e => { setLoginEmail(e.target.value); setLoginError(""); }} />
            <input style={S.input} type="password" placeholder="비밀번호 (Password)" value={loginPassword} onChange={e => { setLoginPassword(e.target.value); setLoginError(""); }}
              onKeyDown={e => { if (e.key === "Enter") handleSignIn(); }}
            />
            {loginError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: -8, marginBottom: 8 }}>{loginError}</div>}

            <button style={{
              width: "100%", padding: "14px 0", background: "#111", color: "#fff", border: "none",
              borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: font, marginTop: 4,
              opacity: (loginEmail && loginPassword) ? 1 : 0.4,
            }} onClick={handleSignIn}>로그인 (Sign In)</button>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              <span style={{ fontSize: 12, color: "#999" }}>아직 계정이 없으신가요? </span>
              <button style={{ background: "none", border: "none", fontSize: 12, fontWeight: 700, color: "#111", cursor: "pointer", fontFamily: font, textDecoration: "underline" }} onClick={() => { setAuthScreen("signup"); setLoginError(""); }}>회원가입 (Sign Up)</button>
            </div>
          </div>
        ) : (
          <div style={{ width: "100%", maxWidth: 320 }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4, textAlign: "center" }}>회원가입 <span style={{ fontSize: 12, fontWeight: 400, color: "#999" }}>Sign Up</span></div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 24, textAlign: "center" }}>가입 후 관리자 승인이 필요합니다<br /><span style={{ fontSize: 11, color: "#bbb" }}>Admin approval is required after sign up</span></div>

            <input style={S.input} placeholder="이름 (Name)" value={signupName} onChange={e => setSignupName(e.target.value)} />
            <input style={S.input} type="email" placeholder="이메일 (Email)" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} />
            <input style={S.input} type="password" placeholder="비밀번호 (Password)" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} />
            <input style={S.input} placeholder="연락처 (Phone: 010-0000-0000)" value={signupPhone} onChange={e => setSignupPhone(e.target.value)} />
            <select style={{ ...S.input, appearance: "auto" }} value={signupSubject} onChange={e => setSignupSubject(e.target.value)}>
              <option value="">담당 과목 선택 (Select Subject)</option>
              {subjects.map(s => <option key={s.id} value={s.name}>{s.name} ({s.nameEn})</option>)}
            </select>

            <button style={{
              width: "100%", padding: "14px 0", background: "#111", color: "#fff", border: "none",
              borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: font, marginTop: 8,
              opacity: (signupName && signupEmail && signupPassword && signupPhone && signupSubject) ? 1 : 0.4,
            }} onClick={async () => {
              if (!signupName || !signupEmail || !signupPassword || !signupPhone || !signupSubject) return;
              if (signupPassword.length < 6) {
                alert("비밀번호는 6자 이상이어야 합니다.");
                return;
              }
              try {
                const auth = getAuth();
                // Firebase Auth 계정 생성
                const cred = await createUserWithEmailAndPassword(auth, signupEmail.trim(), signupPassword);
                const uid = cred.user.uid;
                const isOwner = isOwnerEmail(signupEmail);
                // Firestore users 문서 생성 (비밀번호 미저장)
                const userData = {
                  uid,
                  name: signupName,
                  email: signupEmail.trim(),
                  phone: signupPhone,
                  subject: signupSubject,
                  status: isOwner ? "approved" : "pending",
                  role: isOwner ? "owner" : "teacher",
                  date: new Date().toISOString().split("T")[0],
                };
                const db = getFirestore();
                await setDoc(doc(db, "users", uid), userData);
                console.log("회원가입 완료:", uid, userData.role, userData.status);
                setRegisteredUsers(us => [...us, { firestoreId: uid, ...userData }]);
                setAuthState({ name: signupName, status: userData.status, firestoreId: uid, role: userData.role, email: signupEmail.trim() });
                setSignupName(""); setSignupEmail(""); setSignupPassword(""); setSignupPhone(""); setSignupSubject("");
              } catch (err) {
                console.error("가입 실패:", err);
                if (err.code === "auth/email-already-in-use") {
                  alert("이미 등록된 이메일입니다.");
                } else if (err.code === "auth/invalid-email") {
                  alert("올바른 이메일 형식이 아닙니다.");
                } else if (err.code === "auth/weak-password") {
                  alert("비밀번호가 너무 약합니다. 6자 이상 입력해주세요.");
                } else {
                  alert("가입 중 오류가 발생했습니다: " + (err.message || ""));
                }
              }
            }}>가입 신청 (Sign Up)</button>

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button style={{ background: "none", border: "none", fontSize: 12, fontWeight: 600, color: "#999", cursor: "pointer", fontFamily: font }} onClick={() => setAuthScreen("login")}>← 로그인으로 돌아가기 (Back to Sign In)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPendingScreen = () => (
    <div key="pending-screen" style={S.app}>

      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", textAlign: "center" }}>
        <div style={{ width: 80, height: 80, background: "#f5f5f5", borderRadius: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, marginBottom: 20 }}>⏳</div>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>승인 대기 중</div>
        <div style={{ fontSize: 13, color: "#999", lineHeight: 1.7, maxWidth: 280, marginBottom: 32 }}>
          {authState?.name}님의 가입 신청이 접수되었습니다.<br/>
          관리자 승인 후 앱을 이용하실 수 있습니다.
        </div>
        <button style={{ ...S.btn("outline"), fontSize: 13 }} onClick={async () => {
          // Reload users from Firestore and check status
          await loadUsersFromFirestore();
        }}>승인 상태 확인</button>
        <button style={{ background: "none", border: "none", fontSize: 12, color: "#999", cursor: "pointer", fontFamily: font, marginTop: 16 }} onClick={handleSignOut}>로그아웃</button>
      </div>
    </div>
  );

  const renderRejectedScreen = () => (
    <div key="rejected-screen" style={S.app}>

      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", textAlign: "center" }}>
        <div style={{ width: 80, height: 80, background: "#fee2e2", borderRadius: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, marginBottom: 20 }}>⚠</div>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>접근이 제한되었습니다</div>
        <div style={{ fontSize: 13, color: "#999", lineHeight: 1.7, maxWidth: 280, marginBottom: 32 }}>
          승인이 거절되었거나 계정이 차단되었습니다.<br/>
          문의사항은 관리자에게 연락해주세요.
        </div>
        <button style={{ ...S.btn("outline"), fontSize: 13 }} onClick={handleSignOut}>로그인 화면으로</button>
      </div>
    </div>
  );

  // ─── Auth Gate ─────────────────────────────────────────────────────────────
  if (!authState) return renderAuthScreen();
  if (authState.status === "pending") return renderPendingScreen();
  if (authState.status === "rejected") return renderRejectedScreen();

  // ─── Main Render (authenticated) ──────────────────────────────────────────
  return (
    <div style={S.app} translate="no">
      {renderHeader()}

      <div style={{ paddingBottom: playingTrack ? (playerCollapsed ? 30 : 50) : 0 }}>
        {showAdmin && selectedEvent ? renderEvents() :
         showAdmin ? renderAdmin() :
          page === "home" ? renderHome() :
          page === "subjects" ? renderSubjects() :
          page === "subjectDetail" ? renderSubjectDetail() :
          page === "favorites" ? renderFavorites() :
          page === "recent" ? renderRecent() :
          page === "events" ? renderEvents() :
          null
        }
      </div>

      {updateReady && (
        <button onClick={() => window.location.reload()} style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 250, display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#111", color: "#fff", border: "none", borderRadius: 22, fontSize: 13, fontWeight: 700, fontFamily: font, boxShadow: "0 6px 20px rgba(0,0,0,0.25)", cursor: "pointer", whiteSpace: "nowrap" }}>
          새 버전이 있어요 <span style={{ background: ACCENT, borderRadius: 12, padding: "3px 10px", fontSize: 12 }}>업데이트</span>
        </button>
      )}
      {renderMiniPlayer()}
      {renderNav()}
      {renderVideoPlayer()}
      {renderDocViewer()}
      {renderGlobalSearch()}
    </div>
  );
}
